import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, ArrowUp, ArrowDown, Users, CreditCard, BarChart, Plus, Minus, Search, UserCheck, Calendar, Upload, Pencil, BookOpen, Trash2, Key, UserPlus, Video, Mic, MicOff, Sparkles, Loader2, X, Move, Link2, Image as ImageIcon, Star, Save, TrendingUp, Instagram, Youtube, Facebook, RefreshCw, MapPin, CheckCircle, ShoppingBag, Download, MessageSquare, FileText, Eye, EyeOff, Ticket } from 'lucide-react';
import ThumbnailCropper from '../components/ThumbnailCropper';
import EventPhotosTab from '../components/admin/EventPhotosTab';
import EventVideosTab from '../components/admin/EventVideosTab';
import UserStatsTab from '../components/admin/UserStatsTab';
import DartfishTab from '../components/admin/DartfishTab';
import StoreManagementTab from '../components/admin/StoreManagementTab';
import LibraryVideosSortable from '../components/admin/LibraryVideosSortable';
import BuildMarker from '../components/admin/BuildMarker';
import AdminCritiquesTab from '../components/admin/AdminCritiquesTab';
import AdminAgreementsTab from '../components/admin/AdminAgreementsTab';
import CreditSettingsTab from '../components/admin/CreditSettingsTab';
import MediaPricingTab from '../components/admin/MediaPricingTab';
import VideoOrdersTab from '../components/admin/VideoOrdersTab';
import RawUploadsTab from '../components/admin/RawUploadsTab';
import GuestTicketsTab from '../components/admin/GuestTicketsTab';
import SponsorsTab from '../components/admin/SponsorsTab';
import TeaserVideosTab from '../components/admin/TeaserVideosTab';
import HomePageRailsTab from '../components/HomePageRailsTab';
import CreatableSelect from '../components/CreatableSelect';
import YouthApplicationsTab from '../components/admin/YouthApplicationsTab';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Capture a single JPEG frame ~2 seconds into a video File. Returns a Blob or
// null if the browser can't process it (e.g., unsupported codec, CORS, etc.).
const captureVideoFrameAsBlob = (file) => new Promise((resolve) => {
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';
    const url = URL.createObjectURL(file);
    video.src = url;

    const cleanup = () => {
      try { URL.revokeObjectURL(url); } catch { /* ignore */ }
    };

    const timeout = setTimeout(() => { cleanup(); resolve(null); }, 15000);

    video.onloadedmetadata = () => {
      // Seek ~2s in, or 10% of the video, whichever comes first
      const seekTo = Math.min(2, (video.duration || 0) * 0.1 || 0);
      try { video.currentTime = Math.max(0.1, seekTo); } catch { /* ignore */ }
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        const w = Math.min(video.videoWidth || 1280, 1280);
        const h = Math.round((video.videoHeight || 720) * (w / (video.videoWidth || 1280)));
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob((blob) => {
          clearTimeout(timeout);
          cleanup();
          resolve(blob);
        }, 'image/jpeg', 0.85);
      } catch (e) {
        clearTimeout(timeout);
        cleanup();
        resolve(null);
      }
    };

    video.onerror = () => { clearTimeout(timeout); cleanup(); resolve(null); };
  } catch { resolve(null); }
});

// Convert a plain Vimeo/YouTube URL to an embeddable URL. Returns null if input is raw HTML embed code.
const getEmbedUrlFromInput = (input) => {
  if (!input) return null;
  const trimmed = input.trim();
  // If it starts with '<', it's raw HTML embed code — let dangerouslySetInnerHTML handle it
  if (trimmed.startsWith('<')) return null;
  // YouTube
  const ytMatch = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo event (e.g. vimeo.com/event/5821047)
  const vimeoEventMatch = trimmed.match(/vimeo\.com\/event\/(\d+)/);
  if (vimeoEventMatch) return `https://vimeo.com/event/${vimeoEventMatch[1]}/embed`;
  // Vimeo regular (e.g. vimeo.com/123456789)
  const vimeoMatch = trimmed.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  // If it's a plain URL but not recognized, use as-is
  if (trimmed.startsWith('http')) return trimmed;
  return null;
};

const AdminPage = () => {
  const { user } = useAuth();
  const isFullAdmin = user?.role === 'admin';
  const isStreamOnly = !isFullAdmin && user?.is_stream_manager === true;
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [coaches, setCoaches] = useState([]);
  const [coachStats, setCoachStats] = useState([]);
  const [coachApplications, setCoachApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [creditAdjust, setCreditAdjust] = useState({});
  const [savingCredits, setSavingCredits] = useState({});
  const [showCreateCoach, setShowCreateCoach] = useState(false);
  const [newCoach, setNewCoach] = useState({ name: '', email: '', password: '', bio: '', region: 'Alberta', disciplines: [], photo_url: '', achievements: '', phone: '' });
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [editingCoach, setEditingCoach] = useState(null);
  const [editForm, setEditForm] = useState({ 
    name: '', 
    bio: '', 
    full_bio: '',
    region: '', 
    disciplines: [], 
    achievements: '',
    tagline: '',
    coaching_philosophy: '',
    specialties: '',
    years_experience: '',
    students_helped: '',
    website: '',
    instagram: '',
    facebook: '',
    media_gallery: []
  });
  // Coach photo position adjustment state
  const [showCoachPhotoCropper, setShowCoachPhotoCropper] = useState(false);
  const [coachPhotoPosition, setCoachPhotoPosition] = useState(null);
  const [savingCoachPhotoPosition, setSavingCoachPhotoPosition] = useState(false);
  const [uploadingGalleryMedia, setUploadingGalleryMedia] = useState(false);
  const [editingGalleryItemIndex, setEditingGalleryItemIndex] = useState(null); // For coach gallery position adjustment
  const [editingYAGalleryItemIndex, setEditingYAGalleryItemIndex] = useState(null); // For youth athlete gallery position adjustment
  
  const [knowledgeEntries, setKnowledgeEntries] = useState([]);
  const [showAddKnowledge, setShowAddKnowledge] = useState(false);
  const [editingKnowledge, setEditingKnowledge] = useState(null);
  const [knowledgeForm, setKnowledgeForm] = useState({ title: '', content: '', category: 'technique', tags: '' });
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ name: '', email: '', region: '', phone: '' });
  const [expandedCoach, setExpandedCoach] = useState(null);
  const [pendingCritiques, setPendingCritiques] = useState([]);
  const [loadingCritiques, setLoadingCritiques] = useState(false);
  const [adminVideoPlayer, setAdminVideoPlayer] = useState(null);
  const [mergingCoach, setMergingCoach] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({});
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user' });
  const [libraryVideos, setLibraryVideos] = useState([]);
  const [showAddLibraryVideo, setShowAddLibraryVideo] = useState(false);
  const [editingLibraryVideo, setEditingLibraryVideo] = useState(null);
  const [libraryForm, setLibraryForm] = useState({ 
    title: '', 
    description: '',
    video_url: '', 
    thumbnail_url: '',
    thumbnail_position: '',
    category: 'heading', 
    difficulty: 'beginner',
    duration: '',
    tags: '',
    coach_id: ''
  });
  const [uploadingLibraryVideo, setUploadingLibraryVideo] = useState(false);
  const [libraryUploadProgress, setLibraryUploadProgress] = useState(0);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [enhancingDescription, setEnhancingDescription] = useState(false);
  // Move-library-video-to-event dialog state
  const [movingLibraryVideo, setMovingLibraryVideo] = useState(null);
  const [moveTargetEventId, setMoveTargetEventId] = useState('');
  const [movingInProgress, setMovingInProgress] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);
  const shouldRestartRef = useRef(false);
  
  // Thumbnail cropping state
  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [thumbnailPosition, setThumbnailPosition] = useState(null);
  const [isRepositioning, setIsRepositioning] = useState(false); // true when adjusting existing thumbnail
  
  // Live Stream state
  const [liveStreamSettings, setLiveStreamSettings] = useState({
    embed_code: '',
    is_live: false,
    title: 'Live Event'
  });

  // Free Event override (independent from the main live stream)
  const [freeEvent, setFreeEvent] = useState({
    is_active: false,
    embed_code: '',
    title: '',
  });
  const [savingFreeEvent, setSavingFreeEvent] = useState(false);
  const [freeEventStats, setFreeEventStats] = useState({
    total_unique_viewers: 0,
    watching_now: 0,
    tv_screens: 0,
  });
  const [savingLiveStream, setSavingLiveStream] = useState(false);

  // Paid Live Stream viewer stats (mirrors freeEventStats)
  const [liveStreamStats, setLiveStreamStats] = useState({
    total_unique_viewers: 0,
    watching_now: 0,
    tv_screens: 0,
    peak_concurrent: 0,
  });
  // Past sessions history (free + paid)
  const [sessionHistory, setSessionHistory] = useState([]);
  
  // Events Management state
  const [events, setEvents] = useState([]);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    location: '',
    map_url: '',
    event_type: 'clinic',
    registration_url: '',
    registration_form_id: '',
    image_url: '',
    sponsor_logo_url: '',
    is_published: true,
    // Homepage / playback fields — surfaced in a dedicated section at the
    // bottom of the Edit Event modal. All optional with sensible fallbacks.
    opening_video_id: '',
    video_display_order: [],
    homepage_title: '',
    homepage_description: '',
    homepage_thumbnail_url: '',
    association: '',
    instructor_coach_id: '',
    youtube_channel_handle: '',
  });
  // YouTube sync state — set during the Sync Now click so the admin sees
  // progress and the resulting counts without a page reload.
  const [youtubeSyncing, setYoutubeSyncing] = useState(false);
  const [youtubeSyncResult, setYoutubeSyncResult] = useState(null);
  const [eventVideosForModal, setEventVideosForModal] = useState([]); // videos attached to editingEvent
  const [loadingEventVideos, setLoadingEventVideos] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  
  // Registration Forms state
  const [registrationForms, setRegistrationForms] = useState([]);
  const [showAddRegistration, setShowAddRegistration] = useState(false);
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [registrationForm, setRegistrationForm] = useState({
    event_name: '',
    slug: '',
    description: '',
    stripe_link: '',
    price: 0,
    is_open: true,
    max_total: 0,
    categories: []
  });
  const [savingRegistration, setSavingRegistration] = useState(false);
  const [viewingRegistrations, setViewingRegistrations] = useState(null);
  const [registrationEntries, setRegistrationEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [showManualReg, setShowManualReg] = useState(false);
  const [manualRegForm, setManualRegForm] = useState({ name: '', email: '', phone: '', address: '', discipline: '', experience_level: 'intermediate', special_requests: '', payment_status: 'paid', form_id: '' });
  const [manualRegSelectedForm, setManualRegSelectedForm] = useState(null);
  
  // Past Events state
  const [pastEvents, setPastEvents] = useState([]);
  const [showAddPastEvent, setShowAddPastEvent] = useState(false);
  const [editingPastEvent, setEditingPastEvent] = useState(null);
  const [pastEventForm, setPastEventForm] = useState({
    title: '',
    description: '',
    embed_code: '',
    thumbnail_url: '',
    event_date: ''
  });
  const [savingPastEvent, setSavingPastEvent] = useState(false);
  
  // Youth Athletes state
  const [youthAthletes, setYouthAthletes] = useState([]);
  const [showCreateYouthAthlete, setShowCreateYouthAthlete] = useState(false);
  const [editingYouthAthlete, setEditingYouthAthlete] = useState(null);
  const [newYouthAthlete, setNewYouthAthlete] = useState({ 
    name: '', 
    age: '', 
    bio: '', 
    region: 'Alberta', 
    disciplines: [], 
    photo_url: '', 
    achievements: '',
    tagline: '',
    story: '',
    goals: '',
    years_competing: '',
    events_competed: '',
    instagram: ''
  });
  const [newYouthAthletePhotoFile, setNewYouthAthletePhotoFile] = useState(null);
  const [newYouthAthletePhotoPreview, setNewYouthAthletePhotoPreview] = useState(null);
  const [newYouthAthletePhotoPosition, setNewYouthAthletePhotoPosition] = useState(null);
  const [showNewYouthAthleteCropper, setShowNewYouthAthleteCropper] = useState(false);
  const [creatingYouthAthlete, setCreatingYouthAthlete] = useState(false);
  const [youthAthleteForm, setYouthAthleteForm] = useState({
    name: '',
    age: '',
    bio: '',
    full_bio: '',
    region: '',
    disciplines: [],
    achievements: '',
    tagline: '',
    story: '',
    goals: '',
    years_competing: '',
    events_competed: '',
    website: '',
    instagram: '',
    facebook: '',
    media_gallery: []
  });
  const [uploadingYouthAthletePhoto, setUploadingYouthAthletePhoto] = useState(null);
  const [showYouthAthletePhotoCropper, setShowYouthAthletePhotoCropper] = useState(false);
  const [youthAthletePhotoPosition, setYouthAthletePhotoPosition] = useState(null);
  const [savingYouthAthletePhotoPosition, setSavingYouthAthletePhotoPosition] = useState(false);
  const [uploadingYouthAthleteGalleryMedia, setUploadingYouthAthleteGalleryMedia] = useState(false);

  // Social Media Dashboard state
  const [socialMediaStats, setSocialMediaStats] = useState(null);
  const [socialMediaHistory, setSocialMediaHistory] = useState([]);
  const [socialMediaProfiles, setSocialMediaProfiles] = useState({});
  const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
  const [refreshingSocialMedia, setRefreshingSocialMedia] = useState(false);
  const [editingSocialProfiles, setEditingSocialProfiles] = useState(false);
  const [chartTimePeriod, setChartTimePeriod] = useState('all');
  const [socialProfilesForm, setSocialProfilesForm] = useState({
    instagram: '',
    facebook: '',
    youtube: '',
    tiktok: ''
  });

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          }
        }
        if (finalTranscript) {
          setLibraryForm(prev => ({
            ...prev,
            description: prev.description ? prev.description + ' ' + finalTranscript : finalTranscript
          }));
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        // Don't stop on 'no-speech' or 'aborted' - these are normal
        if (event.error === 'no-speech' || event.error === 'aborted') {
          return;
        }
        shouldRestartRef.current = false;
        setIsListening(false);
      };

      recognition.onend = () => {
        // Auto-restart if we should still be listening
        if (shouldRestartRef.current) {
          try {
            recognition.start();
          } catch (err) {
            console.error('Failed to restart speech recognition:', err);
            shouldRestartRef.current = false;
            setIsListening(false);
          }
        } else {
          setIsListening(false);
        }
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        shouldRestartRef.current = false;
        recognitionRef.current.stop();
      }
    };
  }, []);

  const toggleListening = () => {
    if (!recognitionRef.current) return;
    if (isListening) {
      shouldRestartRef.current = false;
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      shouldRestartRef.current = true;
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (err) {
        recognitionRef.current.stop();
        setTimeout(() => {
          try {
            recognitionRef.current.start();
            setIsListening(true);
          } catch (e) {
            console.error('Failed to restart:', e);
            shouldRestartRef.current = false;
            setIsListening(false);
          }
        }, 100);
      }
    }
  };

  const enhanceDescriptionWithAI = async () => {
    if (!libraryForm.description.trim() || libraryForm.description.length < 10) {
      alert('Please write at least a few words before enhancing.');
      return;
    }
    setEnhancingDescription(true);
    try {
      const response = await axios.post(`${API_URL}/api/ai/enhance-note`, {
        note: libraryForm.description,
        discipline: libraryForm.category || 'roping'
      });
      setLibraryForm({ ...libraryForm, description: response.data.enhanced_note });
    } catch (error) {
      console.error('Failed to enhance description:', error);
      alert('Failed to enhance description. Please try again.');
    } finally {
      setEnhancingDescription(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Poll free-event stats every 30s while the event is running
  useEffect(() => {
    if (!freeEvent.is_active) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/free-event/stats`);
        if (!cancelled) setFreeEventStats(res.data || {});
      } catch (_) { /* silent */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [freeEvent.is_active]);

  // Poll paid live-stream stats every 30s while stream is live
  useEffect(() => {
    if (!liveStreamSettings.is_live) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/live-stream/stats`);
        if (!cancelled) setLiveStreamStats(res.data || {});
      } catch (_) { /* silent */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [liveStreamSettings.is_live]);

  // Load session history once on mount (and after any stream toggle)
  const fetchSessionHistory = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/live-sessions/history?limit=20`);
      setSessionHistory(res.data?.sessions || []);
    } catch (_) { /* silent */ }
  }, []);
  useEffect(() => { fetchSessionHistory(); }, [fetchSessionHistory]);

  const fetchData = async () => {
    try {
      // Core admin data. Uses allSettled so a 403 on any single call (which
      // happens for stream-manager-only users) doesn't nuke the rest of the
      // page — the Free Event card lives under Live and must always load.
      const [usersRes, statsRes, coachesRes, coachStatsRes, applicationsRes, knowledgeRes, libraryRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/admin/users`),
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/coaches`),
        axios.get(`${API_URL}/api/admin/coaches/stats`),
        axios.get(`${API_URL}/api/admin/coach-applications`),
        axios.get(`${API_URL}/api/knowledge`),
        axios.get(`${API_URL}/api/admin/library`)
      ]);
      if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data.users || []);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data || {});
      if (coachesRes.status === 'fulfilled') setCoaches(coachesRes.value.data.coaches || []);
      if (coachStatsRes.status === 'fulfilled') setCoachStats(coachStatsRes.value.data.coach_stats || []);
      if (applicationsRes.status === 'fulfilled') setCoachApplications(applicationsRes.value.data.applications || []);
      if (knowledgeRes.status === 'fulfilled') setKnowledgeEntries(knowledgeRes.value.data.entries || []);
      if (libraryRes.status === 'fulfilled') setLibraryVideos(libraryRes.value.data.videos || []);
      
      // Fetch youth athletes separately - don't let it break other data
      try {
        const youthAthletesRes = await axios.get(`${API_URL}/api/admin/youth-athletes`);
        setYouthAthletes(youthAthletesRes.data.athletes || []);
      } catch (yaError) {
        console.warn('Failed to fetch youth athletes:', yaError);
        setYouthAthletes([]);
      }
      
      // Fetch live stream settings
      try {
        const liveStreamRes = await axios.get(`${API_URL}/api/live-stream`);
        setLiveStreamSettings(liveStreamRes.data);
      } catch (lsError) {
        console.warn('Failed to fetch live stream settings:', lsError);
      }

      // Fetch free-event override (independent of the paid live stream)
      try {
        const freeRes = await axios.get(`${API_URL}/api/admin/free-event`);
        setFreeEvent({
          is_active: !!freeRes.data?.is_active,
          embed_code: freeRes.data?.embed_code || '',
          title: freeRes.data?.title || '',
        });
      } catch (feError) {
        console.warn('Failed to fetch free event:', feError);
      }
      
      // Fetch events
      try {
        const adminToken = localStorage.getItem('token');
        const eventsRes = await axios.get(`${API_URL}/api/admin/events`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        setEvents(eventsRes.data.events || []);
      } catch (evError) {
        console.warn('Failed to fetch events:', evError);
      }
      
      // Fetch registration forms
      try {
        const adminToken = localStorage.getItem('token');
        const regRes = await axios.get(`${API_URL}/api/admin/registrations`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        setRegistrationForms(regRes.data.forms || []);
      } catch (regError) {
        console.warn('Failed to fetch registration forms:', regError);
      }
      
      // Fetch past events
      try {
        const adminToken = localStorage.getItem('token');
        const pastEventsRes = await axios.get(`${API_URL}/api/admin/past-events`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        setPastEvents(pastEventsRes.data.events || []);
      } catch (peError) {
        console.warn('Failed to fetch past events:', peError);
      }
      
      // Fetch social media stats
      try {
        const adminToken = localStorage.getItem('token');
        const socialRes = await axios.get(`${API_URL}/api/admin/social-media/stats`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        setSocialMediaStats(socialRes.data.current || null);
        setSocialMediaHistory(socialRes.data.history || []);
        setSocialMediaProfiles(socialRes.data.profiles || {});
        setSocialProfilesForm(socialRes.data.profiles || {});
        setNextScheduledRefresh(socialRes.data.next_scheduled_refresh || null);
      } catch (smError) {
        console.warn('Failed to fetch social media stats:', smError);
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreditAdjust = async (userId, amount) => {
    if (!amount || isNaN(amount)) {
      alert('Please enter a valid number');
      return;
    }
    
    setSavingCredits({ ...savingCredits, [userId]: true });
    try {
      const response = await axios.put(`${API_URL}/api/admin/users/${userId}/credits?credits=${amount}`);
      console.log('Credits updated:', response.data);
      await fetchData();
      setCreditAdjust({ ...creditAdjust, [userId]: '' });
      // Show brief success indicator
      alert(`Successfully ${amount > 0 ? 'added' : 'removed'} ${Math.abs(amount)} credits`);
    } catch (error) {
      console.error('Failed to adjust credits:', error);
      alert('Failed to adjust credits: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingCredits({ ...savingCredits, [userId]: false });
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role?role=${role}`);
      fetchData();
    } catch (error) {
      console.error('Failed to change role:', error);
    }
  };

  const handleSuspend = async (userId) => {
    if (!window.confirm('Suspend this user?')) return;
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/suspend`);
      fetchData();
    } catch (error) {
      console.error('Failed to suspend user:', error);
    }
  };

  const handleResyncStripe = async (userId, userName) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/${userId}/resync-stripe`);
      const d = res.data || {};
      if (d.changed) {
        const changes = Object.entries(d.changes || {})
          .map(([k, v]) => `${k}: ${v.was || 'none'} → ${v.now || 'none'}`)
          .join(', ');
        toast.success(`${userName}: ${changes}`);
      } else {
        toast.info(`${userName}: already in sync with Stripe (${d.reason || d.current_subscription_status || 'no change'})`);
      }
      fetchData();
    } catch (error) {
      toast.error(`Resync failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleResyncAllStripe = async () => {
    if (!window.confirm('Resync every active/trial/cancelled user against Stripe? This runs in the background and can take several minutes.')) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/subscriptions/resync-all`);
      const { job_id, total } = res.data || {};
      toast.info(`Resyncing ${total || 0} subscribers in the background... this can take a few minutes.`);
      // Poll every 5s until done
      const poll = async () => {
        try {
          const job = await axios.get(`${API_URL}/api/admin/jobs/${job_id}`);
          const j = job.data || {};
          if (j.status === 'done') {
            toast.success(`Resync complete — scanned ${j.scanned}, updated ${j.changed_count}, errors ${j.error_count}`);
            fetchData();
          } else {
            toast.info(`Resync progress: ${j.scanned || 0}/${j.total || total} — ${j.changed_count || 0} updated so far`);
            setTimeout(poll, 5000);
          }
        } catch (e) {
          // Silently stop polling if job disappears
        }
      };
      setTimeout(poll, 5000);
    } catch (error) {
      toast.error(`Bulk resync failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleCreateCoach = async (e) => {
    e.preventDefault();
    try {
      // Convert achievements string to array (one per line)
      const achievementsArray = newCoach.achievements
        .split('\n')
        .map(a => a.trim())
        .filter(a => a.length > 0);
      
      await axios.post(`${API_URL}/api/admin/coaches`, {
        ...newCoach,
        achievements: achievementsArray
      });
      setShowCreateCoach(false);
      setNewCoach({ name: '', email: '', password: '', bio: '', region: 'Alberta', disciplines: [], photo_url: '', achievements: '', phone: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to create coach:', error);
      alert(error.response?.data?.detail || 'Failed to create coach');
    }
  };

  const fetchCoachStatsFiltered = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFilter.start) params.append('start_date', dateFilter.start);
      if (dateFilter.end) params.append('end_date', dateFilter.end);
      
      const response = await axios.get(`${API_URL}/api/admin/coaches/stats?${params.toString()}`);
      setCoachStats(response.data.coach_stats || []);
    } catch (error) {
      console.error('Failed to fetch filtered coach stats:', error);
    }
  };

  const handlePhotoUpload = async (coachId, file) => {
    if (!file) return;
    
    setUploadingPhoto(coachId);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API_URL}/api/admin/coaches/${coachId}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to upload photo:', error);
      alert(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(null);
    }
  };

  // Handle saving coach photo position
  const handleSaveCoachPhotoPosition = async (position) => {
    if (!editingCoach) return;
    
    setSavingCoachPhotoPosition(true);
    try {
      await axios.put(`${API_URL}/api/admin/coaches/${editingCoach.id}`, {
        photo_position: position
      });
      // Refresh coach data
      const response = await axios.get(`${API_URL}/api/coaches/${editingCoach.id}`);
      setEditingCoach(response.data);
      setShowCoachPhotoCropper(false);
      setCoachPhotoPosition(null);
    } catch (error) {
      console.error('Failed to save photo position:', error);
      alert(error.response?.data?.detail || 'Failed to save photo position');
    } finally {
      setSavingCoachPhotoPosition(false);
    }
  };

  const handleDeleteCoach = async (coach) => {
    if (!window.confirm(`Are you sure you want to delete ${coach.name}? This cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/coaches/${coach.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove from local state
      setCoaches(prev => prev.filter(c => c.id !== coach.id));
      toast.success(`${coach.name} has been deleted`);
    } catch (error) {
      console.error('Failed to delete coach:', error);
      toast.error('Failed to delete coach');
    }
  };

  const openEditCoach = async (coach) => {
    // Fetch complete coach data to ensure we have all fields including media_gallery
    try {
      const response = await axios.get(`${API_URL}/api/coaches/${coach.id}`);
      const fullCoach = response.data;
      
      setEditForm({
        name: fullCoach.name || '',
        bio: fullCoach.bio || '',
        full_bio: fullCoach.full_bio || '',
        region: fullCoach.region || '',
        phone: fullCoach.phone || '',
        disciplines: fullCoach.disciplines || [],
        achievements: (fullCoach.achievements || []).join('\n'),
        tagline: fullCoach.tagline || '',
        coaching_philosophy: fullCoach.coaching_philosophy || '',
        specialties: (fullCoach.specialties || []).join('\n'),
        years_experience: fullCoach.years_experience || '',
        students_helped: fullCoach.students_helped || '',
        website: fullCoach.website || '',
        instagram: fullCoach.instagram || '',
        facebook: fullCoach.facebook || '',
        media_gallery: fullCoach.media_gallery || []
      });
      setCoachPhotoPosition(fullCoach.photo_position || null);
      setEditingCoach(fullCoach);
    } catch (error) {
      console.error('Failed to fetch coach details:', error);
      // Fallback to using the coach data we already have
      setEditForm({
        name: coach.name || '',
        bio: coach.bio || '',
        full_bio: coach.full_bio || '',
        region: coach.region || '',
        phone: coach.phone || '',
        disciplines: coach.disciplines || [],
        achievements: (coach.achievements || []).join('\n'),
        tagline: coach.tagline || '',
        coaching_philosophy: coach.coaching_philosophy || '',
        specialties: (coach.specialties || []).join('\n'),
        years_experience: coach.years_experience || '',
        students_helped: coach.students_helped || '',
        website: coach.website || '',
        instagram: coach.instagram || '',
        facebook: coach.facebook || '',
        media_gallery: coach.media_gallery || []
      });
      setCoachPhotoPosition(coach.photo_position || null);
      setEditingCoach(coach);
    }
  };

  const handleUpdateCoach = async (e) => {
    e.preventDefault();
    try {
      const achievementsArray = editForm.achievements
        .split('\n')
        .map(a => a.trim())
        .filter(a => a.length > 0);
      
      const specialtiesArray = editForm.specialties
        .split('\n')
        .map(s => s.trim())
        .filter(s => s.length > 0);
      
      await axios.put(`${API_URL}/api/admin/coaches/${editingCoach.id}`, {
        name: editForm.name,
        bio: editForm.bio,
        full_bio: editForm.full_bio,
        region: editForm.region,
        phone: editForm.phone,
        disciplines: editForm.disciplines,
        achievements: achievementsArray,
        tagline: editForm.tagline,
        coaching_philosophy: editForm.coaching_philosophy,
        specialties: specialtiesArray,
        years_experience: editForm.years_experience ? parseInt(editForm.years_experience) : null,
        students_helped: editForm.students_helped ? parseInt(editForm.students_helped) : null,
        website: editForm.website,
        instagram: editForm.instagram,
        facebook: editForm.facebook,
        media_gallery: editForm.media_gallery
      });
      setEditingCoach(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update coach:', error);
      alert(error.response?.data?.detail || 'Failed to update coach');
    }
  };

  // Handle adding media to coach gallery
  const handleAddGalleryMedia = async (file, type) => {
    if (!editingCoach) return;
    
    setUploadingGalleryMedia(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const endpoint = type === 'video' 
        ? `${API_URL}/api/admin/upload/video?folder=coach_media`
        : `${API_URL}/api/admin/upload/image?folder=coach_media`;
      
      const uploadResponse = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const newMedia = {
        type: type,
        url: uploadResponse.data.url,
        caption: ''
      };
      
      // Get current gallery from editingCoach state
      const currentGallery = editingCoach.media_gallery || [];
      const updatedGallery = [...currentGallery, newMedia];
      
      // Save to database
      await axios.put(`${API_URL}/api/admin/coaches/${editingCoach.id}`, {
        media_gallery: updatedGallery
      });
      
      // Update local state directly with the new gallery (don't rely on GET response)
      setEditingCoach(prev => ({ ...prev, media_gallery: updatedGallery }));
      setEditForm(prev => ({ ...prev, media_gallery: updatedGallery }));
    } catch (error) {
      console.error('Failed to upload media:', error);
      alert('Failed to upload media: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingGalleryMedia(false);
    }
  };

  // Handle removing media from gallery
  const handleRemoveGalleryMedia = async (index) => {
    if (!editingCoach) return;
    
    const currentGallery = editingCoach.media_gallery || [];
    const updatedGallery = currentGallery.filter((_, i) => i !== index);
    
    try {
      await axios.put(`${API_URL}/api/admin/coaches/${editingCoach.id}`, {
        media_gallery: updatedGallery
      });
      
      // Update local state directly
      setEditingCoach(prev => ({ ...prev, media_gallery: updatedGallery }));
      setEditForm(prev => ({ ...prev, media_gallery: updatedGallery }));
    } catch (error) {
      console.error('Failed to remove media:', error);
      alert('Failed to remove media');
    }
  };

  // Handle adding video URL (YouTube, etc)
  const handleAddVideoUrl = async (url) => {
    if (!editingCoach || !url) return;
    
    const newMedia = {
      type: 'video',
      url: url,
      caption: ''
    };
    
    const currentGallery = editingCoach.media_gallery || [];
    const updatedGallery = [...currentGallery, newMedia];
    
    try {
      await axios.put(`${API_URL}/api/admin/coaches/${editingCoach.id}`, {
        media_gallery: updatedGallery
      });
      
      // Update local state directly
      setEditingCoach(prev => ({ ...prev, media_gallery: updatedGallery }));
      setEditForm(prev => ({ ...prev, media_gallery: updatedGallery }));
    } catch (error) {
      console.error('Failed to add video URL:', error);
      alert('Failed to add video: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddKnowledge = async (e) => {
    e.preventDefault();
    try {
      const tagsArray = knowledgeForm.tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);
      
      await axios.post(`${API_URL}/api/admin/knowledge`, {
        ...knowledgeForm,
        tags: tagsArray
      });
      setShowAddKnowledge(false);
      setKnowledgeForm({ title: '', content: '', category: 'technique', tags: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to add knowledge:', error);
      alert(error.response?.data?.detail || 'Failed to add knowledge entry');
    }
  };

  const handleUpdateKnowledge = async (e) => {
    e.preventDefault();
    try {
      const tagsArray = knowledgeForm.tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);
      
      await axios.put(`${API_URL}/api/admin/knowledge/${editingKnowledge.id}`, {
        ...knowledgeForm,
        tags: tagsArray
      });
      setEditingKnowledge(null);
      setKnowledgeForm({ title: '', content: '', category: 'technique', tags: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to update knowledge:', error);
      alert(error.response?.data?.detail || 'Failed to update knowledge entry');
    }
  };

  const handleDeleteKnowledge = async (entryId) => {
    if (!window.confirm('Delete this knowledge entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/knowledge/${entryId}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete knowledge:', error);
    }
  };

  const openEditKnowledge = (entry) => {
    setKnowledgeForm({
      title: entry.title || '',
      content: entry.content || '',
      category: entry.category || 'technique',
      tags: (entry.tags || []).join(', ')
    });
    setEditingKnowledge(entry);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/admin/users/${resetPasswordUser.id}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
      alert(`Password reset for ${resetPasswordUser.email}`);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleEditUser = async () => {
    try {
      const response = await axios.put(
        `${API_URL}/api/admin/users/${editingUser.id}`,
        editUserForm,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setUsers(prev => prev.map(u => u.id === editingUser.id ? response.data : u));
      toast.success('User updated');
      setEditingUser(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const toggleCoachCritiques = async (coachId) => {
    if (expandedCoach === coachId) {
      setExpandedCoach(null);
      setPendingCritiques([]);
      return;
    }
    setExpandedCoach(coachId);
    setLoadingCritiques(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/critiques/pending?coach_id=${coachId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPendingCritiques(res.data.critiques || []);
    } catch (err) {
      toast.error('Failed to load critiques');
    } finally {
      setLoadingCritiques(false);
    }
  };

  const handleMergeCoach = async (fromId, toId) => {
    const toCoach = coachStats.find(c => c.coach_id === toId);
    if (!toCoach) return;
    if (!window.confirm(`Merge all critiques into ${toCoach.coach_name} and remove the old entry?`)) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/coaches/merge`, 
        { from_coach_id: fromId, to_coach_id: toId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(res.data.message);
      setMergingCoach(null);
      // Refresh coach stats
      const statsRes = await axios.get(`${API_URL}/api/admin/coaches/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCoachStats(statsRes.data.stats || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Merge failed');
    }
  };


  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/users`, newUser);
      setShowCreateUser(false);
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      fetchData();
    } catch (error) {
      console.error('Failed to create user:', error);
      alert(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleLibraryVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/mov'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid video file (MP4, MOV, AVI, or WebM)');
      return;
    }

    if (file.size > 1000 * 1024 * 1024) {
      alert('Video must be less than 1GB');
      return;
    }

    setUploadingLibraryVideo(true);
    setLibraryUploadProgress(0);

    // Kick off local frame capture in parallel with upload so we don't slow down
    // the main upload. Result is applied only if admin hasn't set a thumbnail
    // themselves by the time it lands.
    const framePromise = captureVideoFrameAsBlob(file).catch(() => null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/admin/upload/video?folder=library`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setLibraryUploadProgress(progress);
        }
      });

      setLibraryForm(prev => ({ ...prev, video_url: response.data.url }));

      // Upload the captured frame as the auto-thumbnail (best-effort, non-fatal)
      const frameBlob = await framePromise;
      if (frameBlob) {
        try {
          const thumbForm = new FormData();
          thumbForm.append('file', frameBlob, 'auto-thumb.jpg');
          const thumbRes = await axios.post(
            `${API_URL}/api/admin/upload/image?folder=thumbnails`,
            thumbForm,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          setLibraryForm(prev => (
            // Only set if admin hasn't chosen their own thumbnail in the meantime
            prev.thumbnail_url ? prev : { ...prev, thumbnail_url: thumbRes.data.url }
          ));
        } catch (e) {
          console.info('Auto-thumbnail capture skipped:', e?.message);
        }
      }
    } catch (error) {
      console.error('Failed to upload video:', error);
      alert('Failed to upload video. Please try again.');
    } finally {
      setUploadingLibraryVideo(false);
      setLibraryUploadProgress(0);
    }
  };

  const handleAddLibraryVideo = async (e) => {
    e.preventDefault();
    if (!libraryForm.video_url) {
      alert('Please upload a video first');
      return;
    }
    try {
      const tagsArray = libraryForm.tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);
      
      await axios.post(`${API_URL}/api/admin/library`, {
        ...libraryForm,
        tags: tagsArray
      });
      setShowAddLibraryVideo(false);
      setLibraryForm({ title: '', description: '', video_url: '', thumbnail_url: '', thumbnail_position: '', category: 'heading', difficulty: 'beginner', duration: '', tags: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to add library video:', error);
      alert(error.response?.data?.detail || 'Failed to add library video');
    }
  };

  const handleUpdateLibraryVideo = async (e) => {
    e.preventDefault();
    try {
      const tagsArray = libraryForm.tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);
      
      await axios.put(`${API_URL}/api/admin/library/${editingLibraryVideo.id}`, {
        ...libraryForm,
        tags: tagsArray
      });
      setEditingLibraryVideo(null);
      setLibraryForm({ title: '', description: '', video_url: '', thumbnail_url: '', thumbnail_position: '', category: 'heading', difficulty: 'beginner', duration: '', tags: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to update library video:', error);
      alert(error.response?.data?.detail || 'Failed to update library video');
    }
  };

  const handleDeleteLibraryVideo = async (videoId) => {
    if (!window.confirm('Delete this video from the library?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/library/${videoId}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete library video:', error);
    }
  };

  const openEditLibraryVideo = (video) => {
    // For legacy values ('general' category, 'advanced' difficulty), we leave
    // the field EMPTY so the dropdown shows a placeholder — admin must pick
    // a new valid value before saving.
    const isLegacyCategory = !video.category || video.category === 'general';
    const isLegacyDifficulty = !video.difficulty || video.difficulty === 'advanced';
    setLibraryForm({
      title: video.title || '',
      description: video.description || '',
      video_url: video.video_url || '',
      thumbnail_url: video.thumbnail_url || '',
      thumbnail_position: video.thumbnail_position || '',
      category: isLegacyCategory ? '' : video.category,
      difficulty: isLegacyDifficulty ? '' : video.difficulty,
      duration: video.duration || '',
      tags: (video.tags || []).join(', ')
    });
    setThumbnailPosition(video.thumbnail_position || null);
    setEditingLibraryVideo(video);
  };

  const openMoveLibraryVideo = (video) => {
    setMovingLibraryVideo(video);
    setMoveTargetEventId('');
  };

  const handleSendLibraryVideoToTop = async (video) => {
    if (!window.confirm(`Send "${video.title}" to the top of the subscriber library?`)) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/library/${video.id}/send-to-top`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(`"${video.title}" sent to top`);
      // Refresh list so admin view reflects the same order subscribers now see
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send to top');
    }
  };

  const [legacyCount, setLegacyCount] = useState({ legacy_general: 0, legacy_advanced: 0 });
  const [clearingLegacy, setClearingLegacy] = useState(false);
  const [scanningThumbs, setScanningThumbs] = useState(false);

  const fetchLegacyCount = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/library/legacy-count`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setLegacyCount(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchLegacyCount(); }, [libraryVideos.length]);

  const handleAutoScanThumbnails = async () => {
    if (!window.confirm(
      'Scan every library video and past event for missing thumbnails and try to auto-fill from Vimeo/YouTube?\n\n' +
      'This is safe — records with a thumbnail already set are skipped. Anything we cannot resolve will be listed so you know what to upload manually.'
    )) return;
    setScanningThumbs(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/thumbnails/auto-scan`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const { scanned, updated, still_missing_count, still_missing } = res.data;
      toast.success(`Scanned ${scanned} — filled ${updated}, ${still_missing_count} still need manual upload`);
      if (still_missing_count > 0) {
        const preview = still_missing.slice(0, 8).map((r) => `• [${r.collection}] ${r.title}`).join('\n');
        window.alert(
          `${still_missing_count} record(s) still need thumbnails:\n\n${preview}` +
          (still_missing_count > 8 ? `\n\n…and ${still_missing_count - 8} more.` : '')
        );
      }
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-scan failed');
    } finally {
      setScanningThumbs(false);
    }
  };


  const handleClearLegacyValues = async () => {
    const total = legacyCount.legacy_general + legacyCount.legacy_advanced;
    if (!window.confirm(
      `Clear legacy values on ${total} row(s)?\n\n` +
      `• ${legacyCount.legacy_general} video(s) with category="general" → will be blank\n` +
      `• ${legacyCount.legacy_advanced} video(s) with difficulty="advanced" → will be blank\n\n` +
      `After this, you'll edit each video to pick a real category/difficulty.`
    )) return;
    setClearingLegacy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/library/clear-legacy-values`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(
        `Cleared ${res.data.categories_cleared} categories and ${res.data.difficulties_cleared} difficulties`
      );
      await fetchData();
      await fetchLegacyCount();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear legacy values');
    } finally {
      setClearingLegacy(false);
    }
  };

  const handleMoveLibraryVideoToEvent = async () => {
    if (!movingLibraryVideo || !moveTargetEventId) {
      toast.error('Pick an event first');
      return;
    }
    setMovingInProgress(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/library/${movingLibraryVideo.id}/move-to-event`,
        { event_id: moveTargetEventId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      // Remove from library list locally
      setLibraryVideos(prev => prev.filter(v => v.id !== movingLibraryVideo.id));
      toast.success(`Moved to ${res.data?.event_title || 'event'}`);
      setMovingLibraryVideo(null);
      setMoveTargetEventId('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move video');
    } finally {
      setMovingInProgress(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (subscriptionFilter === 'all') return true;
    if (subscriptionFilter === 'none') {
      return !u.subscription_status || u.subscription_status === 'none';
    }
    return u.subscription_status === subscriptionFilter;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight">
            Admin Panel
          </h1>
        </div>

        {/* Stats Cards — hidden for stream-only managers */}
        {!isStreamOnly && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="font-display font-bold text-2xl">{stats.total_users || 0}</p>
              <p className="text-xs text-muted-foreground uppercase">Total Users</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <BarChart className="w-6 h-6 text-secondary mx-auto mb-2" />
              <p className="font-display font-bold text-2xl">{stats.active_subscribers || 0}</p>
              <p className="text-xs text-muted-foreground uppercase">Active Subs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CreditCard className="w-6 h-6 text-accent mx-auto mb-2" />
              <p className="font-display font-bold text-2xl">{stats.pending_critiques || 0}</p>
              <p className="text-xs text-muted-foreground uppercase">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="font-display font-bold text-2xl text-secondary">{stats.completed_critiques || 0}</p>
              <p className="text-xs text-muted-foreground uppercase">Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="font-display font-bold text-2xl">{stats.total_posts || 0}</p>
              <p className="text-xs text-muted-foreground uppercase">Posts</p>
            </CardContent>
          </Card>
        </div>
        )}

        <Tabs defaultValue={isStreamOnly ? "live-stream" : "users"}>
          <div className="overflow-x-auto pb-2 mb-4 -mx-4 px-4 md:mx-0 md:px-0 touch-pan-x" style={{ WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain' }}>
            <TabsList className="inline-flex min-w-max md:flex-wrap">
              {isFullAdmin && (<>
              <TabsTrigger value="stats" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <BarChart className="w-4 h-4" /> Stats
              </TabsTrigger>
              <TabsTrigger value="users" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <Users className="w-4 h-4" />
                Users
              </TabsTrigger>
              <TabsTrigger value="coaches" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <UserCheck className="w-4 h-4" />
                Coaches
              </TabsTrigger>
              <TabsTrigger value="critiques" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <MessageSquare className="w-4 h-4" />
                Critiques
              </TabsTrigger>
              <TabsTrigger value="youth-athletes" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <Star className="w-4 h-4" />
                Youth
              </TabsTrigger>
              <TabsTrigger value="youth-applications" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <FileText className="w-4 h-4" />
                Applications
              </TabsTrigger>
              </>)}
              <TabsTrigger value="library" data-testid="tab-library" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <Video className="w-4 h-4" />
                Library
              </TabsTrigger>
              {isFullAdmin && (<>
              <TabsTrigger value="knowledge" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <BookOpen className="w-4 h-4" />
                KB
              </TabsTrigger>
              <TabsTrigger value="events" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <Calendar className="w-4 h-4" />
                Events
              </TabsTrigger>
              <TabsTrigger value="event-photos" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <ImageIcon className="w-4 h-4" />
                Photos
              </TabsTrigger>
              <TabsTrigger value="event-videos" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <Video className="w-4 h-4" />
                Videos
              </TabsTrigger>
              <TabsTrigger value="registrations" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <CreditCard className="w-4 h-4" />
                Registrations
              </TabsTrigger>
              </>)}
              <TabsTrigger value="live-stream" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <Video className="w-4 h-4" />
                Live
              </TabsTrigger>
              <TabsTrigger value="raw-uploads" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-raw-uploads">
                <Upload className="w-4 h-4" />
                Camera Drop
              </TabsTrigger>
              {isFullAdmin && (<>
              <TabsTrigger value="social-media" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <TrendingUp className="w-4 h-4" />
                Social
              </TabsTrigger>
              <TabsTrigger value="dartfish" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <Video className="w-4 h-4" />
                Dartfish
              </TabsTrigger>
              <TabsTrigger value="store" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <ShoppingBag className="w-4 h-4" />
                Store
              </TabsTrigger>
              <TabsTrigger value="agreements" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <FileText className="w-4 h-4" />
                Agreements
              </TabsTrigger>
              <TabsTrigger value="credit-settings" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <CreditCard className="w-4 h-4" />
                Credit Costs
              </TabsTrigger>
              <TabsTrigger value="media-pricing" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-media-pricing">
                <CreditCard className="w-4 h-4" />
                Media Pricing
              </TabsTrigger>
              <TabsTrigger value="video-orders" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-video-orders">
                <Video className="w-4 h-4" />
                Video Orders
              </TabsTrigger>
              <TabsTrigger value="guest-tickets" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-guest-tickets">
                <Ticket className="w-4 h-4" />
                Guest Tickets
              </TabsTrigger>
              <TabsTrigger value="sponsors" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-sponsors">
                <Star className="w-4 h-4" />
                Sponsors
              </TabsTrigger>
              <TabsTrigger value="teaser-videos" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <Video className="w-4 h-4" />
                Teasers
              </TabsTrigger>
              <TabsTrigger value="home-rails" data-testid="tab-home-rails" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                <Move className="w-4 h-4" />
                Home Page
              </TabsTrigger>
              </>)}
            </TabsList>
          </div>

          <TabsContent value="stats">
            <Card>
              <CardContent className="p-4 sm:p-6">
                <UserStatsTab />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card>
              <CardHeader className="px-4 sm:px-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <CardTitle className="font-display text-lg sm:text-xl uppercase">User Management</CardTitle>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
                    <div className="relative w-full sm:w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="user-search-input"
                      />
                    </div>
                    <Select value={subscriptionFilter} onValueChange={setSubscriptionFilter}>
                      <SelectTrigger className="w-full sm:w-44" data-testid="user-status-filter">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        <SelectItem value="active">Active Subscribers</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="trial">Trial</SelectItem>
                        <SelectItem value="none">No Subscription</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select onValueChange={(val) => {
                      const token = localStorage.getItem('token');
                      const url = `${API_URL}/api/admin/users/export?status=${val}`;
                      const a = document.createElement('a');
                      // Use fetch to add auth header, then download
                      fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
                        .then(r => r.blob())
                        .then(blob => {
                          const blobUrl = window.URL.createObjectURL(blob);
                          a.href = blobUrl;
                          a.download = `users_${val}.csv`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          window.URL.revokeObjectURL(blobUrl);
                          toast.success(`Exported ${val} users CSV`);
                        })
                        .catch(() => toast.error('Export failed'));
                    }}>
                      <SelectTrigger className="w-full sm:w-[180px]" data-testid="export-users-btn">
                        <Download className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Export CSV" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        <SelectItem value="active">Active Subscribers</SelectItem>
                        <SelectItem value="trial">Trial Users</SelectItem>
                        <SelectItem value="expired_trial">Expired Trials (Lapsed)</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="none">No Subscription</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleResyncAllStripe}
                      className="rounded-full whitespace-nowrap"
                      title="Force-check every subscriber's real Stripe status and fix drift"
                      data-testid="resync-all-stripe-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resync from Stripe
                    </Button>
                    <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
                      <DialogTrigger asChild>
                        <Button className="rounded-full whitespace-nowrap" data-testid="create-user-btn">
                          <UserPlus className="w-4 h-4 mr-2" />
                          Create User
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle className="font-display uppercase">Create New User</DialogTitle>
                          <DialogDescription>
                            Create a new user account directly. They will receive an active subscription.
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                          <div className="space-y-2">
                            <Label htmlFor="new-user-name">Name</Label>
                            <Input
                              id="new-user-name"
                              value={newUser.name}
                              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                              required
                              data-testid="new-user-name-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="new-user-email">Email</Label>
                            <Input
                              id="new-user-email"
                              type="email"
                              value={newUser.email}
                              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                              required
                              data-testid="new-user-email-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="new-user-password">Password</Label>
                            <div className="relative">
                              <Input
                                id="new-user-password"
                                type={showPasswords.newUser ? "text" : "password"}
                                value={newUser.password}
                                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                required
                                placeholder="Minimum 6 characters"
                                data-testid="new-user-password-input"
                              />
                              <button type="button" onClick={() => setShowPasswords(p => ({...p, newUser: !p.newUser}))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                {showPasswords.newUser ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="new-user-role">Role</Label>
                            <Select
                              value={newUser.role}
                              onValueChange={(val) => setNewUser({ ...newUser, role: val })}
                            >
                              <SelectTrigger data-testid="new-user-role-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="coach">Coach</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex justify-end gap-2 pt-4">
                            <Button type="button" variant="outline" onClick={() => setShowCreateUser(false)}>
                              Cancel
                            </Button>
                            <Button type="submit" data-testid="submit-create-user-btn">
                              Create User
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-2 sm:px-6">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground">Loading users...</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-2 sm:mx-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">User</TableHead>
                          <TableHead className="whitespace-nowrap">Role</TableHead>
                          <TableHead className="whitespace-nowrap">Status</TableHead>
                          <TableHead className="whitespace-nowrap">Credits</TableHead>
                          <TableHead className="whitespace-nowrap hidden sm:table-cell">Earned</TableHead>
                          <TableHead className="whitespace-nowrap hidden md:table-cell">Joined</TableHead>
                          <TableHead className="whitespace-nowrap hidden lg:table-cell">Last Login</TableHead>
                          <TableHead className="whitespace-nowrap hidden lg:table-cell">Cancelled</TableHead>
                          <TableHead className="whitespace-nowrap">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsers.map(u => (
                          <TableRow key={u.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{u.name}</p>
                                <p className="text-sm text-muted-foreground">{u.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={u.role}
                                onValueChange={(val) => handleRoleChange(u.id, val)}
                                disabled={u.id === user?.id}
                              >
                                <SelectTrigger className="w-28">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="user">User</SelectItem>
                                  <SelectItem value="coach">Coach</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <Badge
                                    variant={
                                      u.subscription_status === 'active' ? 'default' :
                                      u.subscription_status === 'trial' && u.subscription_end && new Date(u.subscription_end) < new Date() ? 'destructive' :
                                      u.subscription_status === 'trial' ? 'secondary' :
                                      'outline'
                                    }
                                    className={
                                      u.subscription_status === 'active' ? 'bg-secondary' :
                                      u.subscription_status === 'suspended' ? 'bg-destructive' : ''
                                    }
                                  >
                                    {u.subscription_plan === 'lifetime' ? 'lifetime' : 
                                     u.subscription_status === 'trial' && u.subscription_end && new Date(u.subscription_end) < new Date() ? 'trial ended' :
                                     (u.subscription_status || 'none')}
                                  </Badge>
                                  <Select
                                    value={u.subscription_plan === 'lifetime' ? 'lifetime' : 
                                           u.subscription_plan === 'free_month' ? 'free_month' :
                                           u.subscription_status}
                                    onValueChange={async (val) => {
                                      try {
                                        console.log('Updating subscription for', u.id, 'to', val);
                                        const response = await axios.put(
                                          `${API_URL}/api/admin/users/${u.id}/subscription?status=${val}`,
                                          {},
                                          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                        );
                                        console.log('API response:', response.data);
                                        // Update local state
                                        setUsers(prev => prev.map(user => 
                                          user.id === u.id ? response.data : user
                                        ));
                                        toast.success(`Subscription updated to ${val}`);
                                      } catch (err) {
                                        console.error('Failed to update subscription:', err);
                                        toast.error('Failed to update subscription: ' + (err.response?.data?.detail || err.message));
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="w-28 h-7 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">None</SelectItem>
                                      <SelectItem value="trial">Trial (7 days)</SelectItem>
                                      <SelectItem value="free_month">Free Month</SelectItem>
                                      <SelectItem value="free_annual">Free Annual</SelectItem>
                                      <SelectItem value="active">Active (Monthly)</SelectItem>
                                      <SelectItem value="lifetime">Lifetime</SelectItem>
                                      <SelectItem value="suspended">Suspended</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                {u.subscription_status === 'active' && u.subscription_plan && u.subscription_plan !== 'lifetime' && (
                                  <p className="text-xs font-medium text-primary">
                                    {u.subscription_plan === 'monthly' ? 'Monthly' : 
                                     u.subscription_plan === 'annual' ? 'Annual' : 
                                     u.subscription_plan === 'free_month' ? 'Free Month' :
                                     u.subscription_plan === 'free_annual' ? 'Free Annual' :
                                     u.subscription_plan === 'trial' ? 'Trial' :
                                     u.subscription_plan}
                                  </p>
                                )}
                                {u.subscription_plan === 'lifetime' ? (
                                  <p className="text-xs font-medium text-green-600">Never expires</p>
                                ) : u.subscription_end ? (
                                  <p className="text-xs text-muted-foreground">
                                    {u.subscription_status === 'active' ? 'Renews: ' : 'Expires: '}
                                    {new Date(u.subscription_end).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </p>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-lg w-10 text-center">{u.credits}</span>
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    placeholder="±"
                                    value={creditAdjust[u.id] || ''}
                                    onChange={(e) => setCreditAdjust({ ...creditAdjust, [u.id]: e.target.value })}
                                    className="w-20 h-8 text-sm"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleCreditAdjust(u.id, parseInt(creditAdjust[u.id] || 0));
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => handleCreditAdjust(u.id, parseInt(creditAdjust[u.id] || 0))}
                                    disabled={!creditAdjust[u.id] || savingCredits[u.id]}
                                    className="h-8 px-3"
                                  >
                                    {savingCredits[u.id] ? 'Saving...' : 'Save'}
                                  </Button>
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Enter + or - to add/remove
                              </p>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <div className="text-center">
                                <span className="font-bold text-lg text-green-600">
                                  {u.total_credits_earned || 0}
                                </span>
                                {u.role === 'coach' && (
                                  <p className="text-xs text-muted-foreground">from coaching</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground hidden md:table-cell">
                              {formatDate(u.created_at)}
                            </TableCell>
                            <TableCell className="text-muted-foreground hidden lg:table-cell">
                              {u.last_login_at ? formatDate(u.last_login_at) : <span className="text-xs italic text-muted-foreground/60">never</span>}
                            </TableCell>
                            <TableCell className="text-muted-foreground hidden lg:table-cell">
                              {u.cancelled_at ? <span className="text-destructive font-medium">{formatDate(u.cancelled_at)}</span> : <span className="text-xs italic text-muted-foreground/60">—</span>}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {u.id !== user?.id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setEditingUser(u);
                                      setEditUserForm({ name: u.name || '', email: u.email || '', region: u.region || '', phone: u.phone || '' });
                                    }}
                                    className="h-8 w-8 p-0"
                                    title="Edit User"
                                    data-testid={`edit-user-${u.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                )}
                                {u.id !== user?.id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setResetPasswordUser(u)}
                                    className="h-8 w-8 p-0"
                                    title="Reset Password"
                                  >
                                    <Key className="w-4 h-4" />
                                  </Button>
                                )}
                                {u.id !== user?.id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteUser(u.id, u.name || u.email)}
                                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                    title="Delete User"
                                    data-testid={`delete-user-${u.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                                {u.id !== user?.id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleResyncStripe(u.id, u.name || u.email)}
                                    className="h-8 w-8 p-0"
                                    title="Resync from Stripe"
                                    data-testid={`resync-stripe-${u.id}`}
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                  </Button>
                                )}
                                {u.id !== user?.id && u.subscription_status !== 'suspended' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleSuspend(u.id)}
                                    className="text-destructive hover:text-destructive"
                                  >
                                    Suspend
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reset Password Dialog */}
            <Dialog open={!!resetPasswordUser} onOpenChange={(open) => !open && setResetPasswordUser(null)}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display uppercase">Reset Password</DialogTitle>
                  <DialogDescription>
                    Set a new password for {resetPasswordUser?.email}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showPasswords.reset ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password (min 6 characters)"
                        data-testid="reset-password-input"
                      />
                      <button type="button" onClick={() => setShowPasswords(p => ({...p, reset: !p.reset}))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPasswords.reset ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setResetPasswordUser(null)}>
                      Cancel
                    </Button>
                    <Button onClick={handleResetPassword} data-testid="confirm-reset-password">
                      Reset Password
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Edit User Dialog */}
            <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display uppercase">Edit User</DialogTitle>
                  <DialogDescription>
                    Update profile for {editingUser?.email}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-user-name">Name</Label>
                    <Input id="edit-user-name" value={editUserForm.name} onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })} data-testid="edit-user-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-user-email">Email</Label>
                    <Input id="edit-user-email" type="email" value={editUserForm.email} onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })} data-testid="edit-user-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-user-region">Region</Label>
                    <Input id="edit-user-region" value={editUserForm.region} onChange={(e) => setEditUserForm({ ...editUserForm, region: e.target.value })} data-testid="edit-user-region" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-user-phone">Phone</Label>
                    <Input id="edit-user-phone" value={editUserForm.phone} onChange={(e) => setEditUserForm({ ...editUserForm, phone: e.target.value })} data-testid="edit-user-phone" />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
                    <Button onClick={handleEditUser} data-testid="save-edit-user">Save Changes</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

          </TabsContent>

          <TabsContent value="coaches">
            {/* Create Coach Dialog */}
            <div className="flex justify-end mb-6">

            {/* Admin Video Player Modal */}
            {adminVideoPlayer && (
              <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setAdminVideoPlayer(null)}>
                <div className="bg-card rounded-xl overflow-hidden w-full max-w-4xl shadow-2xl" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between px-4 py-3 border-b">
                    <h3 className="font-semibold text-sm truncate">{adminVideoPlayer.title}</h3>
                    <div className="flex items-center gap-2">
                      <a href={adminVideoPlayer.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">Open in new tab</a>
                      <button onClick={() => setAdminVideoPlayer(null)} className="p-1 hover:bg-muted rounded"><X className="w-5 h-5" /></button>
                    </div>
                  </div>
                  <div className="aspect-video bg-black">
                    {adminVideoPlayer.url.includes('youtube') || adminVideoPlayer.url.includes('youtu.be') ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${adminVideoPlayer.url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1] || ''}`}
                        className="w-full h-full" allowFullScreen frameBorder="0"
                      />
                    ) : adminVideoPlayer.url.includes('vimeo') ? (
                      <iframe
                        src={`https://player.vimeo.com/video/${adminVideoPlayer.url.match(/vimeo\.com\/(\d+)/)?.[1] || ''}`}
                        className="w-full h-full" allowFullScreen frameBorder="0"
                      />
                    ) : (
                      <video src={adminVideoPlayer.url} controls autoPlay className="w-full h-full" />
                    )}
                  </div>
                </div>
              </div>
            )}

              <Dialog open={showCreateCoach} onOpenChange={setShowCreateCoach}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="create-coach-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Coach
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-display uppercase">Create New Coach</DialogTitle>
                    <DialogDescription>
                      Add a new coach to the platform. They will receive coach-level access.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateCoach} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="coach-name">Name</Label>
                      <Input
                        id="coach-name"
                        value={newCoach.name}
                        onChange={(e) => setNewCoach({ ...newCoach, name: e.target.value })}
                        required
                        data-testid="coach-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="coach-email">Email</Label>
                      <Input
                        id="coach-email"
                        type="email"
                        value={newCoach.email}
                        onChange={(e) => setNewCoach({ ...newCoach, email: e.target.value })}
                        required
                        data-testid="coach-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="coach-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="coach-password"
                          type={showPasswords.coach ? "text" : "password"}
                          value={newCoach.password}
                          onChange={(e) => setNewCoach({ ...newCoach, password: e.target.value })}
                          required
                          data-testid="coach-password-input"
                        />
                        <button type="button" onClick={() => setShowPasswords(p => ({...p, coach: !p.coach}))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          {showPasswords.coach ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="coach-region">Region</Label>
                      <Input
                        id="coach-region"
                        value={newCoach.region}
                        onChange={(e) => setNewCoach({ ...newCoach, region: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="coach-phone">Cell Number</Label>
                      <Input
                        id="coach-phone"
                        type="tel"
                        value={newCoach.phone || ''}
                        onChange={(e) => setNewCoach({ ...newCoach, phone: e.target.value })}
                        placeholder="+1 (555) 123-4567"
                        data-testid="coach-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="coach-bio">Bio</Label>
                      <Textarea
                        id="coach-bio"
                        value={newCoach.bio}
                        onChange={(e) => setNewCoach({ ...newCoach, bio: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="coach-photo">Photo URL</Label>
                      <Input
                        id="coach-photo"
                        type="url"
                        value={newCoach.photo_url}
                        onChange={(e) => setNewCoach({ ...newCoach, photo_url: e.target.value })}
                        placeholder="https://example.com/photo.jpg"
                        data-testid="coach-photo-input"
                      />
                      <p className="text-xs text-muted-foreground">Paste a direct link to the coach's photo</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="coach-achievements">Championships & Achievements</Label>
                      <Textarea
                        id="coach-achievements"
                        value={newCoach.achievements}
                        onChange={(e) => setNewCoach({ ...newCoach, achievements: e.target.value })}
                        placeholder="2x CFR Qualifier&#10;2023 Calgary Stampede Champion&#10;CPRA Header of the Year"
                        rows={3}
                        data-testid="coach-achievements-input"
                      />
                      <p className="text-xs text-muted-foreground">One achievement per line - these will display as badges</p>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setShowCreateCoach(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" data-testid="submit-coach-btn">
                        Create Coach
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Coach Stats with Date Filter */}
            <Card className="mb-6">
              <CardHeader className="px-4 sm:px-6">
                <div className="flex flex-col gap-4">
                  <CardTitle className="font-display text-lg sm:text-xl uppercase">Coach Performance</CardTitle>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <Input
                        type="date"
                        value={dateFilter.start}
                        onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
                        className="flex-1 sm:w-32"
                      />
                    </div>
                    <span className="text-muted-foreground text-center hidden sm:block">to</span>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground sm:hidden">to</span>
                      <Input
                        type="date"
                        value={dateFilter.end}
                        onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
                        className="flex-1 sm:w-32"
                      />
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchCoachStatsFiltered} className="w-full sm:w-auto">
                      Filter
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-2 sm:px-6">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary mb-2" />
                    <p className="text-sm text-muted-foreground">Loading coach stats...</p>
                  </div>
                ) : coachStats.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No coaches yet</p>
                ) : (
                  <div className="overflow-x-auto -mx-2 sm:mx-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">Coach</TableHead>
                          <TableHead className="text-center whitespace-nowrap">Total</TableHead>
                          <TableHead className="text-center whitespace-nowrap hidden sm:table-cell">Done</TableHead>
                          <TableHead className="text-center whitespace-nowrap hidden sm:table-cell">Pending</TableHead>
                          <TableHead className="text-center whitespace-nowrap">Earned</TableHead>
                          <TableHead className="text-center w-24">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {coachStats.map(cs => (
                          <React.Fragment key={cs.coach_id}>
                            <TableRow>
                              <TableCell className="font-medium">{cs.coach_name}</TableCell>
                              <TableCell className="text-center">{cs.total_critiques}</TableCell>
                              <TableCell className="text-center text-secondary hidden sm:table-cell">{cs.completed_critiques}</TableCell>
                              <TableCell className="text-center hidden sm:table-cell">
                                {cs.pending_critiques > 0 ? (
                                  <button
                                    onClick={() => toggleCoachCritiques(cs.coach_id)}
                                    className="text-amber-500 font-semibold hover:underline cursor-pointer"
                                    data-testid={`view-pending-${cs.coach_id}`}
                                  >
                                    {cs.pending_critiques} {expandedCoach === cs.coach_id ? '▲' : '▼'}
                                  </button>
                                ) : (
                                  <span className="text-muted-foreground">0</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center font-bold">{cs.total_credits_earned}</TableCell>
                              <TableCell className="text-center">
                                {mergingCoach === cs.coach_id ? (
                                  <div className="flex flex-col gap-1">
                                    <select
                                      className="text-xs border rounded px-1 py-1 bg-background"
                                      onChange={(e) => { if (e.target.value) handleMergeCoach(cs.coach_id, e.target.value); }}
                                      defaultValue=""
                                    >
                                      <option value="" disabled>Merge into...</option>
                                      {coachStats.filter(c => c.coach_id !== cs.coach_id).map(c => (
                                        <option key={c.coach_id} value={c.coach_id}>{c.coach_name}</option>
                                      ))}
                                    </select>
                                    <button onClick={() => setMergingCoach(null)} className="text-xs text-muted-foreground hover:underline">Cancel</button>
                                  </div>
                                ) : (
                                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setMergingCoach(cs.coach_id)}
                                    title="Merge this coach's critiques into another coach">
                                    Merge
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                            {expandedCoach === cs.coach_id && (
                              <TableRow>
                                <TableCell colSpan={6} className="p-0">
                                  <div className="bg-muted/50 p-4 border-t">
                                    {loadingCritiques ? (
                                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Loader2 className="w-4 h-4 animate-spin" /> Loading critiques...
                                      </div>
                                    ) : pendingCritiques.length === 0 ? (
                                      <p className="text-sm text-muted-foreground">No pending critiques</p>
                                    ) : (
                                      <div className="space-y-3">
                                        <p className="font-semibold text-sm">{pendingCritiques.length} Pending Critique{pendingCritiques.length !== 1 ? 's' : ''}</p>
                                        {pendingCritiques.map(c => (
                                          <div key={c.id} className="bg-background rounded-lg p-3 border text-sm space-y-1">
                                            <div className="flex items-center justify-between">
                                              <span className="font-medium">{c.user_name || 'Unknown User'}</span>
                                              <Badge variant={c.status === 'in_progress' ? 'default' : 'outline'} className="text-xs">
                                                {c.status}
                                              </Badge>
                                            </div>
                                            <div className="text-muted-foreground text-xs space-y-0.5">
                                              <p>Discipline: {c.discipline || 'N/A'} · Package: {c.critique_package || 'N/A'}</p>
                                              <p>Submitted: {c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</p>
                                              {c.note && <p className="mt-1 italic text-muted-foreground">"{c.note}"</p>}
                                            </div>
                                            {c.video_link && (
                                              <button onClick={() => setAdminVideoPlayer({ url: c.video_link, title: `${c.user_name} - ${c.discipline || 'Critique'}` })} className="text-primary text-xs hover:underline inline-flex items-center gap-1 mt-1">
                                                <Video className="w-3 h-3" /> Watch Video
                                              </button>
                                            )}
                                            {c.additional_videos?.length > 0 && c.additional_videos.map((v, i) => (
                                              <button key={i} onClick={() => setAdminVideoPlayer({ url: v, title: `${c.user_name} - Video ${i + 2}` })} className="text-primary text-xs hover:underline inline-flex items-center gap-1 ml-3">
                                                <Video className="w-3 h-3" /> Watch Video {i + 2}
                                              </button>
                                            ))}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Active Coaches */}
            <Card className="mb-6">
              <CardHeader className="px-4 sm:px-6">
                <CardTitle className="font-display text-lg sm:text-xl uppercase">Active Coaches</CardTitle>
              </CardHeader>
              <CardContent className="px-2 sm:px-6">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary mb-2" />
                    <p className="text-sm text-muted-foreground">Loading coaches...</p>
                  </div>
                ) : coaches.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No coaches yet. Create one above.</p>
                ) : (
                  <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                    {coaches.map(coach => (
                      <Card key={coach.id} className="bg-muted/30">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="relative group">
                              {coach.photo_url ? (
                                <img 
                                  src={coach.photo_url} 
                                  alt={coach.name}
                                  className="w-16 h-16 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="text-xl font-bold text-primary">
                                    {coach.name?.charAt(0)?.toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                                <Upload className="w-5 h-5 text-white" />
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => handlePhotoUpload(coach.id, e.target.files[0])}
                                  disabled={uploadingPhoto === coach.id}
                                />
                              </label>
                              {uploadingPhoto === coach.id && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <h4 className="font-semibold">{coach.name}</h4>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditCoach(coach)}
                                    className="h-8 w-8 p-0"
                                    data-testid={`edit-coach-${coach.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteCoach(coach)}
                                    className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    data-testid={`delete-coach-${coach.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              <p className="text-sm text-muted-foreground">{coach.region}</p>
                              {coach.disciplines?.length > 0 && (
                                <div className="flex gap-1 mt-1 flex-wrap">
                                  {coach.disciplines.map(d => (
                                    <Badge key={d} variant="outline" className="text-xs">{d}</Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          {coach.achievements?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {coach.achievements.map((a, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-700">
                                  {a}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {coach.bio && (
                            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{coach.bio}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Edit Coach Dialog */}
            <Dialog open={!!editingCoach} onOpenChange={(open) => { 
              if (!open) {
                setEditingCoach(null);
                setShowCoachPhotoCropper(false);
              }
            }}>
              <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-display uppercase">Edit Coach</DialogTitle>
                  <DialogDescription>Update coach information</DialogDescription>
                </DialogHeader>
                
                {/* Photo Position Cropper */}
                {showCoachPhotoCropper && editingCoach?.photo_url ? (
                  <div className="py-4">
                    <p className="text-sm text-muted-foreground mb-3">Adjust how the photo appears on the landing page (3:4 portrait)</p>
                    <ThumbnailCropper
                      image={editingCoach.photo_url}
                      aspectRatio={3/4}
                      onCropComplete={(position) => {
                        handleSaveCoachPhotoPosition(position);
                      }}
                      onCancel={() => {
                        setShowCoachPhotoCropper(false);
                      }}
                    />
                    {savingCoachPhotoPosition && (
                      <div className="flex items-center justify-center mt-2">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        <span className="text-sm">Saving position...</span>
                      </div>
                    )}
                  </div>
                ) : (
                <form onSubmit={handleUpdateCoach} className="space-y-4 mt-4">
                  {/* Photo Upload */}
                  <div className="space-y-3">
                    <Label className="block">Coach Photo</Label>
                    <div className="flex items-start gap-4">
                      <div className="relative group flex-shrink-0">
                        {editingCoach?.photo_url ? (
                          <div className="relative">
                            <img 
                              src={editingCoach.photo_url} 
                              alt={editingCoach.name}
                              className="w-24 h-24 rounded-lg object-cover"
                              style={{
                                objectPosition: editingCoach?.photo_position?.objectPosition || 'center'
                              }}
                            />
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="absolute -bottom-2 -right-2 h-8 w-8 p-0 rounded-full shadow-md"
                              onClick={() => setShowCoachPhotoCropper(true)}
                              title="Adjust photo position"
                            >
                              <Move className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="w-24 h-24 rounded-lg bg-primary/10 flex items-center justify-center">
                            <span className="text-2xl font-bold text-primary">
                              {editingCoach?.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file && editingCoach) {
                              await handlePhotoUpload(editingCoach.id, file);
                              // Refresh the editing coach data and reset position
                              const response = await axios.get(`${API_URL}/api/coaches/${editingCoach.id}`);
                              setEditingCoach(response.data);
                              // Auto-open cropper for new photos
                              setShowCoachPhotoCropper(true);
                            }
                          }}
                          className="text-sm"
                          data-testid="edit-coach-photo"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Max 5MB. JPEG, PNG, or WebP.</p>
                        {editingCoach?.photo_url && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            onClick={() => setShowCoachPhotoCropper(true)}
                          >
                            <Move className="w-3 h-3 mr-1" />
                            Adjust Position
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Logo Upload */}
                  <div className="space-y-2">
                    <Label className="block">Branding Logo</Label>
                    <div className="flex items-start gap-4">
                      {editingCoach?.logo_url ? (
                        <div className="relative w-24 h-24 flex-shrink-0">
                          <img 
                            src={editingCoach.logo_url} 
                            alt="Logo" 
                            className="w-full h-full object-contain border rounded-lg bg-white p-2"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                            onClick={async () => {
                              await axios.put(`${API_URL}/api/admin/coaches/${editingCoach.id}`, {
                                logo_url: ''
                              });
                              const response = await axios.get(`${API_URL}/api/coaches/${editingCoach.id}`);
                              setEditingCoach(response.data);
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : null}
                      <div className="flex-1">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file && editingCoach) {
                              try {
                                const formData = new FormData();
                                formData.append('file', file);
                                const uploadResponse = await axios.post(
                                  `${API_URL}/api/admin/upload/image?folder=logos`,
                                  formData,
                                  { headers: { 'Content-Type': 'multipart/form-data' } }
                                );
                                await axios.put(`${API_URL}/api/admin/coaches/${editingCoach.id}`, {
                                  logo_url: uploadResponse.data.url
                                });
                                const response = await axios.get(`${API_URL}/api/coaches/${editingCoach.id}`);
                                setEditingCoach(response.data);
                              } catch (err) {
                                console.error('Failed to upload logo:', err);
                                alert('Failed to upload logo');
                              }
                            }
                          }}
                          className="text-sm"
                          data-testid="edit-coach-logo"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Square logo for branding. PNG with transparency works best.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">Name</Label>
                    <Input
                      id="edit-name"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-region">Region</Label>
                    <Input
                      id="edit-region"
                      value={editForm.region}
                      onChange={(e) => setEditForm({ ...editForm, region: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-phone">Cell Number</Label>
                    <Input
                      id="edit-phone"
                      type="tel"
                      value={editForm.phone || ''}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                      data-testid="edit-coach-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-bio">Bio</Label>
                    <Textarea
                      id="edit-bio"
                      value={editForm.bio}
                      onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Championships & Achievements</Label>
                    {/* Existing achievements with delete buttons */}
                    {editForm.achievements.split('\n').filter(a => a.trim()).length > 0 && (
                      <div className="space-y-2 mb-2">
                        {editForm.achievements.split('\n').filter(a => a.trim()).map((achievement, index) => (
                          <div key={index} className="flex items-center gap-2 bg-muted/50 p-2 rounded">
                            <span className="flex-1 text-sm">{achievement}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => {
                                const achievements = editForm.achievements.split('\n').filter(a => a.trim());
                                achievements.splice(index, 1);
                                setEditForm({ ...editForm, achievements: achievements.join('\n') });
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Input to add new achievement */}
                    <div className="flex gap-2">
                      <Input
                        id="new-achievement"
                        placeholder="Add new achievement (e.g., 2023 CFR Qualifier)"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const newAchievement = e.target.value.trim();
                            if (newAchievement) {
                              const current = editForm.achievements.trim();
                              setEditForm({ 
                                ...editForm, 
                                achievements: current ? `${current}\n${newAchievement}` : newAchievement 
                              });
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const input = document.getElementById('new-achievement');
                          const newAchievement = input.value.trim();
                          if (newAchievement) {
                            const current = editForm.achievements.trim();
                            setEditForm({ 
                              ...editForm, 
                              achievements: current ? `${current}\n${newAchievement}` : newAchievement 
                            });
                            input.value = '';
                          }
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Press Enter or click + to add</p>
                  </div>

                  {/* Extended Profile Fields */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-semibold mb-4">Extended Profile</h4>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="space-y-2">
                        <Label>Years Experience</Label>
                        <Input
                          type="number"
                          value={editForm.years_experience}
                          onChange={(e) => setEditForm({ ...editForm, years_experience: e.target.value })}
                          placeholder="e.g., 15"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Students Helped</Label>
                        <Input
                          type="number"
                          value={editForm.students_helped}
                          onChange={(e) => setEditForm({ ...editForm, students_helped: e.target.value })}
                          placeholder="e.g., 500"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <Label>Tagline</Label>
                      <Input
                        value={editForm.tagline}
                        onChange={(e) => setEditForm({ ...editForm, tagline: e.target.value })}
                        placeholder="A short memorable quote or motto"
                      />
                    </div>

                    <div className="space-y-2 mb-4">
                      <Label>Full Bio (detailed)</Label>
                      <Textarea
                        value={editForm.full_bio}
                        onChange={(e) => setEditForm({ ...editForm, full_bio: e.target.value })}
                        placeholder="Detailed biography for the coach profile page..."
                        rows={4}
                      />
                    </div>

                    <div className="space-y-2 mb-4">
                      <Label>Coaching Philosophy</Label>
                      <Textarea
                        value={editForm.coaching_philosophy}
                        onChange={(e) => setEditForm({ ...editForm, coaching_philosophy: e.target.value })}
                        placeholder="What's your approach to coaching?"
                        rows={2}
                      />
                    </div>

                    <div className="space-y-2 mb-4">
                      <Label>Specialties (one per line)</Label>
                      <Textarea
                        value={editForm.specialties}
                        onChange={(e) => setEditForm({ ...editForm, specialties: e.target.value })}
                        placeholder="Body position&#10;Timing at the box&#10;Horse handling"
                        rows={3}
                      />
                    </div>

                    <h4 className="font-semibold mb-4 mt-6">Social & Contact</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Website URL</Label>
                        <Input
                          value={editForm.website}
                          onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                          placeholder="https://..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Instagram Handle</Label>
                        <Input
                          value={editForm.instagram}
                          onChange={(e) => setEditForm({ ...editForm, instagram: e.target.value })}
                          placeholder="username (no @)"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Facebook URL</Label>
                        <Input
                          value={editForm.facebook}
                          onChange={(e) => setEditForm({ ...editForm, facebook: e.target.value })}
                          placeholder="https://facebook.com/..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Media Gallery Section */}
                  <div className="border-t pt-4 mt-4">
                    <Label className="text-base font-semibold flex items-center gap-2 mb-3">
                      <Video className="w-4 h-4" />
                      Training Highlights Gallery (up to 3)
                    </Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Add photos or videos that showcase your coaching. These will appear on your public profile.
                    </p>
                    
                    {/* Existing media */}
                    {/* Gallery Position Cropper */}
                    {editingGalleryItemIndex !== null && editingCoach?.media_gallery?.[editingGalleryItemIndex]?.type === 'image' && (
                      <div className="border rounded-lg p-4 bg-muted/30 mb-3">
                        <p className="text-sm font-medium mb-2">Adjust Image Position</p>
                        <ThumbnailCropper
                          image={editingCoach.media_gallery[editingGalleryItemIndex].url}
                          aspectRatio={16/9}
                          onCropComplete={async (position) => {
                            try {
                              const updatedGallery = [...editingCoach.media_gallery];
                              updatedGallery[editingGalleryItemIndex] = {
                                ...updatedGallery[editingGalleryItemIndex],
                                position: position
                              };
                              await axios.put(`${API_URL}/api/admin/coaches/${editingCoach.id}`, {
                                media_gallery: updatedGallery
                              });
                              setEditingCoach(prev => ({ ...prev, media_gallery: updatedGallery }));
                              setEditForm(prev => ({ ...prev, media_gallery: updatedGallery }));
                              setEditingGalleryItemIndex(null);
                            } catch (error) {
                              console.error('Failed to save position:', error);
                              alert('Failed to save position');
                            }
                          }}
                          onCancel={() => setEditingGalleryItemIndex(null)}
                        />
                      </div>
                    )}
                    
                    {(editingCoach?.media_gallery || []).length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {(editingCoach?.media_gallery || []).map((media, idx) => (
                          <div key={idx} className="relative aspect-video bg-muted rounded-lg overflow-hidden group">
                            {media.type === 'video' ? (
                              media.url.includes('youtube') || media.url.includes('youtu.be') ? (
                                <div className="w-full h-full flex items-center justify-center bg-primary/10">
                                  <Video className="w-8 h-8 text-primary" />
                                  <span className="absolute bottom-1 left-1 text-xs bg-black/50 text-white px-1 rounded">YouTube</span>
                                </div>
                              ) : (
                                <video src={media.url} className="w-full h-full object-cover" />
                              )
                            ) : (
                              <img 
                                src={media.url} 
                                alt="" 
                                className="w-full h-full object-cover"
                                style={{
                                  objectPosition: media.position?.objectPosition || 'center'
                                }}
                              />
                            )}
                            {/* Position adjust button for images */}
                            {media.type === 'image' && (
                              <button
                                type="button"
                                onClick={() => setEditingGalleryItemIndex(idx)}
                                className="absolute bottom-1 left-1 w-6 h-6 bg-background/80 text-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Adjust Position"
                              >
                                <Move className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveGalleryMedia(idx)}
                              className="absolute top-1 right-1 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add media buttons */}
                    {(editingCoach?.media_gallery || []).length < 3 && (
                      <div className="flex gap-2 flex-wrap">
                        <div className="relative">
                          <Input
                            type="file"
                            accept="image/*"
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleAddGalleryMedia(file, 'image');
                              e.target.value = '';
                            }}
                            disabled={uploadingGalleryMedia}
                          />
                          <Button type="button" variant="outline" size="sm" disabled={uploadingGalleryMedia}>
                            <ImageIcon className="w-4 h-4 mr-1" />
                            Add Photo
                          </Button>
                        </div>
                        <div className="relative">
                          <Input
                            type="file"
                            accept="video/*"
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleAddGalleryMedia(file, 'video');
                              e.target.value = '';
                            }}
                            disabled={uploadingGalleryMedia}
                          />
                          <Button type="button" variant="outline" size="sm" disabled={uploadingGalleryMedia}>
                            <Video className="w-4 h-4 mr-1" />
                            Upload Video
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const url = prompt('Enter YouTube or video URL:');
                            if (url) handleAddVideoUrl(url);
                          }}
                        >
                          <Link2 className="w-4 h-4 mr-1" />
                          Add Video URL
                        </Button>
                        {uploadingGalleryMedia && (
                          <span className="text-sm text-muted-foreground flex items-center">
                            <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            Uploading...
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setEditingCoach(null)}>
                      Cancel
                    </Button>
                    <Button type="submit">Save Changes</Button>
                  </div>
                </form>
                )}
              </DialogContent>
            </Dialog>

            {/* Coach Applications */}
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase">Coach Applications</CardTitle>
                <CardDescription>Review pending applications from aspiring coaches</CardDescription>
              </CardHeader>
              <CardContent>
                {coachApplications.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No pending applications</p>
                ) : (
                  <div className="space-y-4">
                    {coachApplications.map(app => (
                      <Card key={app.id} className="bg-muted/30">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold">{app.name}</h4>
                              <p className="text-sm text-muted-foreground">{app.email}</p>
                              <p className="text-sm text-muted-foreground">{app.region}</p>
                            </div>
                            <Badge variant={app.status === 'pending' ? 'outline' : app.status === 'approved' ? 'default' : 'destructive'}>
                              {app.status}
                            </Badge>
                          </div>
                          <div className="mt-3 space-y-2">
                            <div>
                              <p className="text-xs font-semibold uppercase text-muted-foreground">Experience</p>
                              <p className="text-sm">{app.experience}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold uppercase text-muted-foreground">Why coach?</p>
                              <p className="text-sm">{app.why_coach}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="critiques">
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase">All Critiques</CardTitle>
                <p className="text-sm text-muted-foreground">Review all critique requests and coach responses for quality control.</p>
              </CardHeader>
              <CardContent>
                <AdminCritiquesTab />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="youth-athletes">
            {/* Create Youth Athlete Dialog */}
            <div className="flex justify-end mb-6">
              <Dialog open={showCreateYouthAthlete} onOpenChange={setShowCreateYouthAthlete}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="create-youth-athlete-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Youth Athlete
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-display uppercase">Add Youth Athlete</DialogTitle>
                    <DialogDescription>
                      Add a new youth athlete to showcase on the platform.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    setCreatingYouthAthlete(true);
                    try {
                      const achievementsArray = newYouthAthlete.achievements
                        .split('\n')
                        .map(a => a.trim())
                        .filter(a => a.length > 0);
                      
                      // Create the athlete first
                      const createResponse = await axios.post(`${API_URL}/api/admin/youth-athletes`, {
                        ...newYouthAthlete,
                        age: newYouthAthlete.age ? parseInt(newYouthAthlete.age) : null,
                        years_competing: newYouthAthlete.years_competing ? parseInt(newYouthAthlete.years_competing) : null,
                        events_competed: newYouthAthlete.events_competed ? parseInt(newYouthAthlete.events_competed) : null,
                        achievements: achievementsArray
                      });
                      
                      const athleteId = createResponse.data.id;
                      
                      // Upload photo if one was selected
                      if (newYouthAthletePhotoFile) {
                        const formData = new FormData();
                        formData.append('file', newYouthAthletePhotoFile);
                        await axios.post(`${API_URL}/api/admin/youth-athletes/${athleteId}/photo`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        
                        // Save photo position if adjusted
                        if (newYouthAthletePhotoPosition) {
                          await axios.put(`${API_URL}/api/admin/youth-athletes/${athleteId}`, {
                            photo_position: newYouthAthletePhotoPosition
                          });
                        }
                      }
                      
                      setShowCreateYouthAthlete(false);
                      setNewYouthAthlete({ name: '', age: '', bio: '', region: 'Alberta', disciplines: [], photo_url: '', achievements: '', tagline: '', story: '', goals: '', years_competing: '', events_competed: '', instagram: '' });
                      setNewYouthAthletePhotoFile(null);
                      setNewYouthAthletePhotoPreview(null);
                      setNewYouthAthletePhotoPosition(null);
                      setShowNewYouthAthleteCropper(false);
                      
                      // Refresh the data
                      const youthAthletesRes = await axios.get(`${API_URL}/api/admin/youth-athletes`);
                      setYouthAthletes(youthAthletesRes.data.athletes || []);
                    } catch (error) {
                      console.error('Failed to create youth athlete:', error);
                      alert(error.response?.data?.detail || 'Failed to create youth athlete');
                    } finally {
                      setCreatingYouthAthlete(false);
                    }
                  }} className="space-y-4 mt-4">
                    {/* Photo Upload Section with Cropper */}
                    <div className="space-y-2">
                      <Label>Photo</Label>
                      {showNewYouthAthleteCropper && newYouthAthletePhotoPreview ? (
                        <div className="border rounded-lg p-4 bg-muted/30">
                          <ThumbnailCropper
                            image={newYouthAthletePhotoPreview}
                            aspectRatio={1}
                            onCropComplete={(position) => {
                              setNewYouthAthletePhotoPosition(position);
                              setShowNewYouthAthleteCropper(false);
                            }}
                            onCancel={() => {
                              setShowNewYouthAthleteCropper(false);
                            }}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-4">
                          {newYouthAthletePhotoPreview ? (
                            <div className="relative">
                              <img 
                                src={newYouthAthletePhotoPreview} 
                                alt="Preview" 
                                className="w-20 h-20 rounded-full object-cover"
                                style={{
                                  objectPosition: newYouthAthletePhotoPosition?.objectPosition || 'center'
                                }}
                              />
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                                onClick={() => {
                                  setNewYouthAthletePhotoFile(null);
                                  setNewYouthAthletePhotoPreview(null);
                                  setNewYouthAthletePhotoPosition(null);
                                }}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
                              <Upload className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 space-y-2">
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setNewYouthAthletePhotoFile(file);
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    setNewYouthAthletePhotoPreview(reader.result);
                                    setShowNewYouthAthleteCropper(true);
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                              className="text-sm"
                            />
                            <p className="text-xs text-muted-foreground">Max 5MB. JPEG, PNG, or WebP.</p>
                            {newYouthAthletePhotoPreview && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setShowNewYouthAthleteCropper(true)}
                              >
                                <Move className="w-3 h-3 mr-1" />
                                Adjust Position
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ya-name">Name *</Label>
                      <Input
                        id="ya-name"
                        value={newYouthAthlete.name}
                        onChange={(e) => setNewYouthAthlete({ ...newYouthAthlete, name: e.target.value })}
                        required
                        data-testid="youth-athlete-name-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="ya-age">Age</Label>
                        <Input
                          id="ya-age"
                          type="number"
                          value={newYouthAthlete.age}
                          onChange={(e) => setNewYouthAthlete({ ...newYouthAthlete, age: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ya-region">Region</Label>
                        <Input
                          id="ya-region"
                          value={newYouthAthlete.region}
                          onChange={(e) => setNewYouthAthlete({ ...newYouthAthlete, region: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ya-tagline">Tagline</Label>
                      <Input
                        id="ya-tagline"
                        value={newYouthAthlete.tagline}
                        onChange={(e) => setNewYouthAthlete({ ...newYouthAthlete, tagline: e.target.value })}
                        placeholder="e.g., Rising Star from Alberta"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ya-bio">Bio</Label>
                      <Textarea
                        id="ya-bio"
                        value={newYouthAthlete.bio}
                        onChange={(e) => setNewYouthAthlete({ ...newYouthAthlete, bio: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ya-achievements">Achievements</Label>
                      <Textarea
                        id="ya-achievements"
                        value={newYouthAthlete.achievements}
                        onChange={(e) => setNewYouthAthlete({ ...newYouthAthlete, achievements: e.target.value })}
                        placeholder="One achievement per line"
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ya-instagram">Instagram Handle</Label>
                      <Input
                        id="ya-instagram"
                        value={newYouthAthlete.instagram}
                        onChange={(e) => setNewYouthAthlete({ ...newYouthAthlete, instagram: e.target.value })}
                        placeholder="username (without @)"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => {
                        setShowCreateYouthAthlete(false);
                        setNewYouthAthletePhotoFile(null);
                        setNewYouthAthletePhotoPreview(null);
                        setNewYouthAthletePhotoPosition(null);
                        setShowNewYouthAthleteCropper(false);
                      }}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={creatingYouthAthlete} data-testid="submit-youth-athlete-btn">
                        {creatingYouthAthlete ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          'Create Youth Athlete'
                        )}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Youth Athletes Grid */}
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase">Youth Athletes</CardTitle>
                <CardDescription>
                  Showcase rising stars and young athletes in the roping community.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground">Loading youth athletes...</p>
                  </div>
                ) : youthAthletes.filter(a => a.is_active !== false).length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No youth athletes yet. Add one above.</p>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {youthAthletes.filter(a => a.is_active !== false).map(athlete => (
                      <Card key={athlete.id} className="bg-muted/30">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="relative group">
                              {athlete.photo_url ? (
                                <img 
                                  src={athlete.photo_url} 
                                  alt={athlete.name}
                                  className="w-16 h-16 rounded-full object-cover"
                                  style={{
                                    objectPosition: athlete.photo_position?.objectPosition || 'center'
                                  }}
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="text-xl font-bold text-primary">
                                    {athlete.name?.charAt(0)?.toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                                <Upload className="w-5 h-5 text-white" />
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    
                                    setUploadingYouthAthletePhoto(athlete.id);
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    
                                    try {
                                      await axios.post(`${API_URL}/api/admin/youth-athletes/${athlete.id}/photo`, formData, {
                                        headers: { 'Content-Type': 'multipart/form-data' }
                                      });
                                      fetchData();
                                    } catch (error) {
                                      console.error('Failed to upload photo:', error);
                                      alert(error.response?.data?.detail || 'Failed to upload photo');
                                    } finally {
                                      setUploadingYouthAthletePhoto(null);
                                    }
                                  }}
                                  disabled={uploadingYouthAthletePhoto === athlete.id}
                                />
                              </label>
                              {uploadingYouthAthletePhoto === athlete.id && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <h4 className="font-semibold">{athlete.name}</h4>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setYouthAthleteForm({
                                        name: athlete.name || '',
                                        age: athlete.age || '',
                                        bio: athlete.bio || '',
                                        full_bio: athlete.full_bio || '',
                                        region: athlete.region || '',
                                        disciplines: athlete.disciplines || [],
                                        achievements: (athlete.achievements || []).join('\n'),
                                        tagline: athlete.tagline || '',
                                        story: athlete.story || '',
                                        goals: athlete.goals || '',
                                        years_competing: athlete.years_competing || '',
                                        events_competed: athlete.events_competed || '',
                                        website: athlete.website || '',
                                        instagram: athlete.instagram || '',
                                        facebook: athlete.facebook || '',
                                        media_gallery: athlete.media_gallery || []
                                      });
                                      setYouthAthletePhotoPosition(athlete.photo_position || null);
                                      setEditingYouthAthlete(athlete);
                                    }}
                                    className="h-8 w-8 p-0"
                                    data-testid={`edit-youth-athlete-${athlete.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => {
                                      if (window.confirm(`Are you sure you want to delete ${athlete.name}?`)) {
                                        try {
                                          await axios.delete(`${API_URL}/api/admin/youth-athletes/${athlete.id}`);
                                          const youthAthletesRes = await axios.get(`${API_URL}/api/admin/youth-athletes`);
                                          setYouthAthletes(youthAthletesRes.data.athletes || []);
                                        } catch (error) {
                                          console.error('Failed to delete youth athlete:', error);
                                          alert(error.response?.data?.detail || 'Failed to delete youth athlete');
                                        }
                                      }
                                    }}
                                    className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    data-testid={`delete-youth-athlete-${athlete.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {athlete.region}
                                {athlete.age && ` | ${athlete.age} years old`}
                              </p>
                              {athlete.disciplines?.length > 0 && (
                                <div className="flex gap-1 mt-1 flex-wrap">
                                  {athlete.disciplines.map(d => (
                                    <Badge key={d} variant="outline" className="text-xs">{d}</Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          {athlete.achievements?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {athlete.achievements.slice(0, 3).map((a, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-700">
                                  {a}
                                </Badge>
                              ))}
                              {athlete.achievements.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{athlete.achievements.length - 3} more
                                </Badge>
                              )}
                            </div>
                          )}
                          {athlete.bio && (
                            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{athlete.bio}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Edit Youth Athlete Dialog */}
            <Dialog open={!!editingYouthAthlete} onOpenChange={(open) => { 
              if (!open) {
                setEditingYouthAthlete(null);
                setShowYouthAthletePhotoCropper(false);
              }
            }}>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-display uppercase">Edit Youth Athlete</DialogTitle>
                  <DialogDescription>Update youth athlete information</DialogDescription>
                </DialogHeader>
                
                {/* Photo Position Cropper */}
                {showYouthAthletePhotoCropper && editingYouthAthlete?.photo_url ? (
                  <div className="py-4">
                    <ThumbnailCropper
                      image={editingYouthAthlete.photo_url}
                      aspectRatio={1}
                      onCropComplete={async (position) => {
                        setSavingYouthAthletePhotoPosition(true);
                        try {
                          await axios.put(`${API_URL}/api/admin/youth-athletes/${editingYouthAthlete.id}`, {
                            photo_position: position
                          });
                          const response = await axios.get(`${API_URL}/api/youth-athletes/${editingYouthAthlete.id}`);
                          setEditingYouthAthlete(response.data);
                          setShowYouthAthletePhotoCropper(false);
                          setYouthAthletePhotoPosition(null);
                        } catch (error) {
                          console.error('Failed to save photo position:', error);
                          alert(error.response?.data?.detail || 'Failed to save photo position');
                        } finally {
                          setSavingYouthAthletePhotoPosition(false);
                        }
                      }}
                      onCancel={() => {
                        setShowYouthAthletePhotoCropper(false);
                      }}
                    />
                    {savingYouthAthletePhotoPosition && (
                      <div className="flex items-center justify-center mt-2">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        <span className="text-sm">Saving position...</span>
                      </div>
                    )}
                  </div>
                ) : (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    const achievementsArray = youthAthleteForm.achievements
                      .split('\n')
                      .map(a => a.trim())
                      .filter(a => a.length > 0);
                    
                    await axios.put(`${API_URL}/api/admin/youth-athletes/${editingYouthAthlete.id}`, {
                      name: youthAthleteForm.name,
                      age: youthAthleteForm.age ? parseInt(youthAthleteForm.age) : null,
                      bio: youthAthleteForm.bio,
                      full_bio: youthAthleteForm.full_bio,
                      region: youthAthleteForm.region,
                      disciplines: youthAthleteForm.disciplines,
                      achievements: achievementsArray,
                      tagline: youthAthleteForm.tagline,
                      story: youthAthleteForm.story,
                      goals: youthAthleteForm.goals,
                      years_competing: youthAthleteForm.years_competing ? parseInt(youthAthleteForm.years_competing) : null,
                      events_competed: youthAthleteForm.events_competed ? parseInt(youthAthleteForm.events_competed) : null,
                      website: youthAthleteForm.website,
                      instagram: youthAthleteForm.instagram,
                      facebook: youthAthleteForm.facebook,
                      media_gallery: youthAthleteForm.media_gallery
                    });
                    setEditingYouthAthlete(null);
                    fetchData();
                  } catch (error) {
                    console.error('Failed to update youth athlete:', error);
                    alert(error.response?.data?.detail || 'Failed to update youth athlete');
                  }
                }} className="space-y-4 mt-4">
                  {/* Photo Upload */}
                  <div className="space-y-3">
                    <Label className="block">Photo</Label>
                    <div className="flex items-start gap-4">
                      <div className="relative group flex-shrink-0">
                        {editingYouthAthlete?.photo_url ? (
                          <div className="relative">
                            <img 
                              src={editingYouthAthlete.photo_url} 
                              alt={editingYouthAthlete.name}
                              className="w-24 h-24 rounded-lg object-cover"
                              style={{
                                objectPosition: editingYouthAthlete?.photo_position?.objectPosition || 'center'
                              }}
                            />
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="absolute -bottom-2 -right-2 h-8 w-8 p-0 rounded-full shadow-md"
                              onClick={() => setShowYouthAthletePhotoCropper(true)}
                              title="Adjust photo position"
                            >
                              <Move className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="w-24 h-24 rounded-lg bg-primary/10 flex items-center justify-center">
                            <span className="text-2xl font-bold text-primary">
                              {editingYouthAthlete?.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file && editingYouthAthlete) {
                              setUploadingYouthAthletePhoto(editingYouthAthlete.id);
                              const formData = new FormData();
                              formData.append('file', file);
                              try {
                                await axios.post(`${API_URL}/api/admin/youth-athletes/${editingYouthAthlete.id}/photo`, formData, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                const response = await axios.get(`${API_URL}/api/youth-athletes/${editingYouthAthlete.id}`);
                                setEditingYouthAthlete(response.data);
                                setShowYouthAthletePhotoCropper(true);
                              } catch (err) {
                                alert('Failed to upload photo');
                              } finally {
                                setUploadingYouthAthletePhoto(null);
                              }
                            }
                          }}
                          className="text-sm"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Max 5MB. JPEG, PNG, or WebP.</p>
                        {editingYouthAthlete?.photo_url && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            onClick={() => setShowYouthAthletePhotoCropper(true)}
                          >
                            <Move className="w-3 h-3 mr-1" />
                            Adjust Position
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-ya-name">Name</Label>
                    <Input
                      id="edit-ya-name"
                      value={youthAthleteForm.name}
                      onChange={(e) => setYouthAthleteForm({ ...youthAthleteForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-ya-age">Age</Label>
                      <Input
                        id="edit-ya-age"
                        type="number"
                        value={youthAthleteForm.age}
                        onChange={(e) => setYouthAthleteForm({ ...youthAthleteForm, age: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-ya-region">Region</Label>
                      <Input
                        id="edit-ya-region"
                        value={youthAthleteForm.region}
                        onChange={(e) => setYouthAthleteForm({ ...youthAthleteForm, region: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-ya-tagline">Tagline</Label>
                    <Input
                      id="edit-ya-tagline"
                      value={youthAthleteForm.tagline}
                      onChange={(e) => setYouthAthleteForm({ ...youthAthleteForm, tagline: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-ya-bio">Short Bio</Label>
                    <Textarea
                      id="edit-ya-bio"
                      value={youthAthleteForm.bio}
                      onChange={(e) => setYouthAthleteForm({ ...youthAthleteForm, bio: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-ya-full-bio">Full Bio</Label>
                    <Textarea
                      id="edit-ya-full-bio"
                      value={youthAthleteForm.full_bio}
                      onChange={(e) => setYouthAthleteForm({ ...youthAthleteForm, full_bio: e.target.value })}
                      rows={4}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-ya-story">Their Journey/Story</Label>
                    <Textarea
                      id="edit-ya-story"
                      value={youthAthleteForm.story}
                      onChange={(e) => setYouthAthleteForm({ ...youthAthleteForm, story: e.target.value })}
                      rows={3}
                      placeholder="Tell their story..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-ya-goals">Goals & Dreams</Label>
                    <Textarea
                      id="edit-ya-goals"
                      value={youthAthleteForm.goals}
                      onChange={(e) => setYouthAthleteForm({ ...youthAthleteForm, goals: e.target.value })}
                      rows={2}
                      placeholder="What are their goals?"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-ya-achievements">Achievements</Label>
                    <Textarea
                      id="edit-ya-achievements"
                      value={youthAthleteForm.achievements}
                      onChange={(e) => setYouthAthleteForm({ ...youthAthleteForm, achievements: e.target.value })}
                      placeholder="One achievement per line"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-ya-years">Years Competing</Label>
                      <Input
                        id="edit-ya-years"
                        type="number"
                        value={youthAthleteForm.years_competing}
                        onChange={(e) => setYouthAthleteForm({ ...youthAthleteForm, years_competing: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-ya-events">Events Competed</Label>
                      <Input
                        id="edit-ya-events"
                        type="number"
                        value={youthAthleteForm.events_competed}
                        onChange={(e) => setYouthAthleteForm({ ...youthAthleteForm, events_competed: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-ya-instagram">Instagram Handle</Label>
                    <Input
                      id="edit-ya-instagram"
                      value={youthAthleteForm.instagram}
                      onChange={(e) => setYouthAthleteForm({ ...youthAthleteForm, instagram: e.target.value })}
                      placeholder="username (without @)"
                    />
                  </div>
                  
                  {/* Media Gallery */}
                  <div className="space-y-3">
                    <Label className="block">Media Gallery (up to 3)</Label>
                    
                    {/* Gallery Position Cropper for Youth Athletes */}
                    {editingYAGalleryItemIndex !== null && editingYouthAthlete?.media_gallery?.[editingYAGalleryItemIndex]?.type === 'image' && (
                      <div className="border rounded-lg p-4 bg-muted/30 mb-3">
                        <p className="text-sm font-medium mb-2">Adjust Image Position</p>
                        <ThumbnailCropper
                          image={editingYouthAthlete.media_gallery[editingYAGalleryItemIndex].url}
                          aspectRatio={16/9}
                          onCropComplete={async (position) => {
                            try {
                              const updatedGallery = [...editingYouthAthlete.media_gallery];
                              updatedGallery[editingYAGalleryItemIndex] = {
                                ...updatedGallery[editingYAGalleryItemIndex],
                                position: position
                              };
                              await axios.put(`${API_URL}/api/admin/youth-athletes/${editingYouthAthlete.id}`, {
                                media_gallery: updatedGallery
                              });
                              setEditingYouthAthlete(prev => ({ ...prev, media_gallery: updatedGallery }));
                              setYouthAthleteForm(prev => ({ ...prev, media_gallery: updatedGallery }));
                              setEditingYAGalleryItemIndex(null);
                            } catch (error) {
                              console.error('Failed to save position:', error);
                              alert('Failed to save position');
                            }
                          }}
                          onCancel={() => setEditingYAGalleryItemIndex(null)}
                        />
                      </div>
                    )}
                    
                    {editingYouthAthlete?.media_gallery && editingYouthAthlete.media_gallery.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {editingYouthAthlete.media_gallery.map((media, idx) => (
                          <div key={idx} className="relative aspect-video bg-muted rounded overflow-hidden group">
                            {media.type === 'video' ? (
                              <video src={media.url} className="w-full h-full object-cover" />
                            ) : (
                              <img 
                                src={media.url} 
                                alt="" 
                                className="w-full h-full object-cover"
                                style={{
                                  objectPosition: media.position?.objectPosition || 'center'
                                }}
                              />
                            )}
                            {/* Position adjust button for images */}
                            {media.type === 'image' && (
                              <button
                                type="button"
                                onClick={() => setEditingYAGalleryItemIndex(idx)}
                                className="absolute bottom-1 left-1 w-6 h-6 bg-background/80 text-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Adjust Position"
                              >
                                <Move className="w-3 h-3" />
                              </button>
                            )}
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={async () => {
                                const updatedGallery = editingYouthAthlete.media_gallery.filter((_, i) => i !== idx);
                                try {
                                  await axios.put(`${API_URL}/api/admin/youth-athletes/${editingYouthAthlete.id}`, {
                                    media_gallery: updatedGallery
                                  });
                                  setEditingYouthAthlete(prev => ({ ...prev, media_gallery: updatedGallery }));
                                  setYouthAthleteForm(prev => ({ ...prev, media_gallery: updatedGallery }));
                                } catch (err) {
                                  alert('Failed to remove media');
                                }
                              }}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {(!editingYouthAthlete?.media_gallery || editingYouthAthlete.media_gallery.length < 3) && (
                      <div className="flex gap-2">
                        <Input
                          type="file"
                          accept="image/*,video/*"
                          className="text-sm"
                          disabled={uploadingYouthAthleteGalleryMedia}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file || !editingYouthAthlete) return;
                            
                            setUploadingYouthAthleteGalleryMedia(true);
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              
                              const isVideo = file.type.startsWith('video/');
                              const endpoint = isVideo 
                                ? `${API_URL}/api/admin/upload/video?folder=youth_athlete_media`
                                : `${API_URL}/api/admin/upload/image?folder=youth_athlete_media`;
                              
                              const uploadResponse = await axios.post(endpoint, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              
                              const newMedia = {
                                type: isVideo ? 'video' : 'image',
                                url: uploadResponse.data.url,
                                caption: ''
                              };
                              
                              const currentGallery = editingYouthAthlete.media_gallery || [];
                              const updatedGallery = [...currentGallery, newMedia];
                              
                              await axios.put(`${API_URL}/api/admin/youth-athletes/${editingYouthAthlete.id}`, {
                                media_gallery: updatedGallery
                              });
                              
                              const response = await axios.get(`${API_URL}/api/youth-athletes/${editingYouthAthlete.id}`);
                              setEditingYouthAthlete(response.data);
                              setYouthAthleteForm(prev => ({ ...prev, media_gallery: response.data.media_gallery || [] }));
                            } catch (err) {
                              console.error('Failed to upload media:', err);
                              alert('Failed to upload media');
                            } finally {
                              setUploadingYouthAthleteGalleryMedia(false);
                              e.target.value = '';
                            }
                          }}
                        />
                        {uploadingYouthAthleteGalleryMedia && <Loader2 className="w-4 h-4 animate-spin" />}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setEditingYouthAthlete(null)}>
                      Cancel
                    </Button>
                    <Button type="submit">Save Changes</Button>
                  </div>
                </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="youth-applications">
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase">Youth Sponsorship Applications</CardTitle>
                <p className="text-sm text-muted-foreground">Review and approve/reject youth sponsorship draw applications.</p>
              </CardHeader>
              <CardContent>
                <YouthApplicationsTab />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="library">
            {/* Add Library Video Button */}
            <div className="flex justify-end mb-6">
              <Dialog open={showAddLibraryVideo} onOpenChange={setShowAddLibraryVideo}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="add-library-video-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Video
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-display uppercase">Add Library Video</DialogTitle>
                    <DialogDescription>
                      Upload a free training video for subscribers to access.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddLibraryVideo} className="space-y-4 mt-4">
                    {/* Video Upload or Link */}
                    <div className="space-y-2">
                      <Label>Video</Label>
                      {!libraryForm.video_url ? (
                        <Tabs defaultValue="upload" className="w-full">
                          <TabsList className="w-full">
                            <TabsTrigger value="upload" className="flex-1">Upload File</TabsTrigger>
                            <TabsTrigger value="link" className="flex-1">Paste Video Link</TabsTrigger>
                          </TabsList>
                          <TabsContent value="upload">
                            <div className="border-2 border-dashed rounded-lg p-6 text-center">
                              {uploadingLibraryVideo ? (
                                <div className="space-y-3">
                                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                                  <p className="text-sm text-muted-foreground">
                                    Uploading... {libraryUploadProgress}%
                                  </p>
                                  <div className="w-full bg-muted rounded-full h-2">
                                    <div 
                                      className="bg-primary h-2 rounded-full transition-all"
                                      style={{ width: `${libraryUploadProgress}%` }}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                                  <p className="text-sm text-muted-foreground mb-3">
                                    Upload video (MP4, MOV, AVI, WebM - max 1GB)
                                  </p>
                                  <label className="cursor-pointer">
                                    <input
                                      type="file"
                                      accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,.mov,.mp4,.avi,.webm"
                                      onChange={handleLibraryVideoUpload}
                                      className="hidden"
                                    />
                                    <Button type="button" variant="outline" className="rounded-full" asChild>
                                      <span>
                                        <Upload className="w-4 h-4 mr-2" />
                                        Select Video
                                      </span>
                                    </Button>
                                  </label>
                                </>
                              )}
                            </div>
                          </TabsContent>
                          <TabsContent value="link">
                            <div className="border rounded-lg p-4 space-y-3">
                              <Input
                                placeholder="Paste Vimeo or YouTube URL..."
                                onChange={(e) => {
                                  const val = e.target.value.trim();
                                  if (val) setLibraryForm({ ...libraryForm, video_url: val });
                                }}
                                data-testid="library-video-link-input"
                              />
                              <p className="text-xs text-muted-foreground">Paste a link and it will be saved directly — no download needed</p>
                            </div>
                          </TabsContent>
                        </Tabs>
                      ) : (
                        <div className="bg-muted/50 p-4 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Video className="w-5 h-5 text-primary" />
                              <span className="text-sm font-medium truncate max-w-[300px]">
                                {libraryForm.video_url.startsWith('http') ? libraryForm.video_url : 'Video uploaded'}
                              </span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setLibraryForm({ ...libraryForm, video_url: '' })}
                              className="text-muted-foreground hover:text-destructive"
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="library-title">Title</Label>
                      <Input
                        id="library-title"
                        value={libraryForm.title}
                        onChange={(e) => setLibraryForm({ ...libraryForm, title: e.target.value })}
                        placeholder="e.g., Basics of Heading"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="library-category">Category</Label>
                        <CreatableSelect
                          field="video_category"
                          value={libraryForm.category}
                          onValueChange={(val) => setLibraryForm({ ...libraryForm, category: val })}
                          placeholder="Select category"
                          testId="library-category-select"
                          labelize={(v) => v.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="library-difficulty">Difficulty</Label>
                        <CreatableSelect
                          field="video_difficulty"
                          value={libraryForm.difficulty}
                          onValueChange={(val) => setLibraryForm({ ...libraryForm, difficulty: val })}
                          placeholder="Select difficulty"
                          testId="library-difficulty-select"
                          labelize={(v) => v.charAt(0).toUpperCase() + v.slice(1)}
                        />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="library-coach">Coach (optional)</Label>
                        <select
                          id="library-coach"
                          className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                          value={libraryForm.coach_id || ''}
                          onChange={(e) => setLibraryForm({ ...libraryForm, coach_id: e.target.value })}
                          data-testid="library-coach-select"
                        >
                          <option value="">— No coach —</option>
                          {coaches.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                        <p className="text-xs text-muted-foreground">
                          Tagging the coach lets this video show up on their hover-peek on the home page.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="library-duration">Duration (optional)</Label>
                      <Input
                        id="library-duration"
                        value={libraryForm.duration}
                        onChange={(e) => setLibraryForm({ ...libraryForm, duration: e.target.value })}
                        placeholder="e.g., 5:30"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="library-description">Description</Label>
                      <div className="relative">
                        <Textarea
                          id="library-description"
                          value={libraryForm.description}
                          onChange={(e) => setLibraryForm({ ...libraryForm, description: e.target.value })}
                          placeholder="What will viewers learn from this video?"
                          rows={3}
                          required
                          className={isListening ? 'border-primary ring-2 ring-primary/20' : ''}
                        />
                        {isListening && (
                          <div className="absolute top-2 right-2 flex items-center gap-2 bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                            Listening...
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        {speechSupported && (
                          <Button
                            type="button"
                            variant={isListening ? "default" : "outline"}
                            size="sm"
                            onClick={toggleListening}
                            className={`gap-2 ${isListening ? 'bg-red-500 hover:bg-red-600' : ''}`}
                          >
                            {isListening ? (
                              <>
                                <MicOff className="w-4 h-4" />
                                Stop
                              </>
                            ) : (
                              <>
                                <Mic className="w-4 h-4" />
                                Speak
                              </>
                            )}
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={enhanceDescriptionWithAI}
                          disabled={enhancingDescription || !libraryForm.description.trim() || libraryForm.description.length < 10}
                          className="gap-2"
                        >
                          {enhancingDescription ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Enhancing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              AI Enhance
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="library-tags">Tags (comma separated)</Label>
                      <Input
                        id="library-tags"
                        value={libraryForm.tags}
                        onChange={(e) => setLibraryForm({ ...libraryForm, tags: e.target.value })}
                        placeholder="e.g., basics, position, beginner"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Thumbnail Image (optional)</Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Vimeo & YouTube videos auto-generate thumbnails. Uploaded MP4s capture a frame automatically. Upload one manually to override.
                      </p>
                      {showCropper && cropImage ? (
                        <ThumbnailCropper
                          image={cropImage}
                          aspectRatio={16/9}
                          onCropComplete={async (position) => {
                            // New upload for Add Library Video - this is always a new upload
                            setUploadingThumbnail(true);
                            try {
                              const response = await fetch(cropImage);
                              const blob = await response.blob();
                              const file = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' });
                              
                              const formData = new FormData();
                              formData.append('file', file);
                              const uploadResponse = await axios.post(`${API_URL}/api/admin/upload/image?folder=thumbnails`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setLibraryForm(prev => ({ 
                                ...prev, 
                                thumbnail_url: uploadResponse.data.url,
                                thumbnail_position: position.objectPosition
                              }));
                              setThumbnailPosition(position.objectPosition);
                            } catch (err) {
                              console.error('Failed to upload thumbnail:', err);
                              alert('Failed to upload thumbnail');
                            } finally {
                              setUploadingThumbnail(false);
                              setShowCropper(false);
                              setCropImage(null);
                            }
                          }}
                          onCancel={() => {
                            setShowCropper(false);
                            setCropImage(null);
                            setIsRepositioning(false);
                          }}
                        />
                      ) : libraryForm.thumbnail_url ? (
                        <div className="space-y-2">
                          <div 
                            className="relative w-40 h-24 rounded overflow-hidden"
                            style={{ 
                              backgroundImage: `url(${libraryForm.thumbnail_url})`,
                              backgroundSize: 'cover',
                              backgroundPosition: libraryForm.thumbnail_position || thumbnailPosition || 'center'
                            }}
                          >
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute top-1 right-1 h-6 w-6 p-0"
                              onClick={() => {
                                setLibraryForm({ ...libraryForm, thumbnail_url: '', thumbnail_position: '' });
                                setThumbnailPosition(null);
                              }}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setCropImage(libraryForm.thumbnail_url);
                              setIsRepositioning(true);
                              setShowCropper(true);
                            }}
                          >
                            <Move className="w-4 h-4 mr-1" />
                            Adjust Position
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            id="thumbnail-upload"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              // Read file and show cropper
                              const reader = new FileReader();
                              reader.onload = () => {
                                setCropImage(reader.result);
                                setIsRepositioning(false);
                                setShowCropper(true);
                              };
                              reader.readAsDataURL(file);
                              e.target.value = ''; // Reset input
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={uploadingThumbnail}
                            onClick={() => document.getElementById('thumbnail-upload')?.click()}
                          >
                            {uploadingThumbnail ? 'Uploading...' : 'Upload Thumbnail'}
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => {
                        setShowAddLibraryVideo(false);
                        setLibraryForm({ title: '', description: '', video_url: '', thumbnail_url: '', category: 'heading', difficulty: 'beginner', duration: '', tags: '' });
                      }}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={!libraryForm.video_url}>Add Video</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Library Videos */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <CardTitle className="font-display text-xl uppercase">Learning Library</CardTitle>
                    <CardDescription>
                      Free training videos available to all subscribers.
                    </CardDescription>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAutoScanThumbnails}
                      disabled={scanningThumbs}
                      className="rounded-full"
                      title="Try to auto-fill missing thumbnails from Vimeo/YouTube for library + past events"
                      data-testid="auto-scan-thumbnails-btn"
                    >
                      {scanningThumbs
                        ? (<><Loader2 className="w-3 h-3 mr-1 animate-spin" />Scanning…</>)
                        : (<><RefreshCw className="w-3 h-3 mr-1" />Auto-fill Missing Thumbnails</>)}
                    </Button>
                    {(legacyCount.legacy_general > 0 || legacyCount.legacy_advanced > 0) && (
                      <>
                        <div className="text-xs text-amber-700 text-right">
                          {legacyCount.legacy_general > 0 && (<>⚠ {legacyCount.legacy_general} row(s) with legacy category &quot;general&quot;<br /></>)}
                          {legacyCount.legacy_advanced > 0 && (<>⚠ {legacyCount.legacy_advanced} row(s) with legacy difficulty &quot;advanced&quot;</>)}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleClearLegacyValues}
                          disabled={clearingLegacy}
                          className="border-amber-500 text-amber-700 hover:bg-amber-50"
                          data-testid="clear-legacy-values-btn"
                        >
                          {clearingLegacy ? (<><Loader2 className="w-3 h-3 mr-1 animate-spin" />Clearing…</>) : 'Clear Legacy Values'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground">Loading library videos...</p>
                  </div>
                ) : libraryVideos.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No library videos yet. Add some to give subscribers free training content.
                  </p>
                ) : (
                  <LibraryVideosSortable
                    videos={libraryVideos}
                    onEdit={openEditLibraryVideo}
                    onDelete={handleDeleteLibraryVideo}
                    onMove={openMoveLibraryVideo}
                    onSendToTop={handleSendLibraryVideoToTop}
                  />
                )}
              </CardContent>
            </Card>

            {/* Move Library Video → Event Dialog */}
            <Dialog
              open={!!movingLibraryVideo}
              onOpenChange={(open) => {
                if (!open) {
                  setMovingLibraryVideo(null);
                  setMoveTargetEventId('');
                }
              }}
            >
              <DialogContent className="sm:max-w-md" data-testid="move-library-video-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display uppercase">Move to Event</DialogTitle>
                  <DialogDescription>
                    Move <span className="font-semibold">{movingLibraryVideo?.title}</span> out of the Library and attach it to a specific event.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label htmlFor="move-target-event">Destination event</Label>
                    <Select value={moveTargetEventId} onValueChange={setMoveTargetEventId}>
                      <SelectTrigger id="move-target-event" data-testid="move-library-video-event-select">
                        <SelectValue placeholder="Pick an event…" />
                      </SelectTrigger>
                      <SelectContent>
                        {events.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-muted-foreground">No events available</div>
                        ) : (
                          events.map(ev => (
                            <SelectItem key={ev.id} value={ev.id}>
                              {ev.title}{ev.event_date ? ` — ${ev.event_date}` : ' — TBA'}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    The video will be removed from the Learning Library and appear under the event you choose.
                  </p>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => { setMovingLibraryVideo(null); setMoveTargetEventId(''); }}
                      disabled={movingInProgress}
                      data-testid="move-library-video-cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleMoveLibraryVideoToEvent}
                      disabled={!moveTargetEventId || movingInProgress}
                      data-testid="move-library-video-confirm"
                    >
                      {movingInProgress ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Moving…</>
                      ) : 'Move Video'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Edit Library Video Dialog */}
            <Dialog 
              open={!!editingLibraryVideo} 
              onOpenChange={(open) => {
                // Don't close if cropper is active
                if (!open && !showCropper) {
                  setEditingLibraryVideo(null);
                  setShowCropper(false);
                  setCropImage(null);
                  setIsRepositioning(false);
                }
              }}
            >
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-display uppercase">Edit Library Video</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateLibraryVideo} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-library-title">Title</Label>
                    <Input
                      id="edit-library-title"
                      value={libraryForm.title}
                      onChange={(e) => setLibraryForm({ ...libraryForm, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category {!libraryForm.category && <span className="text-amber-600 text-xs">(needs value)</span>}</Label>
                      <Select 
                        value={libraryForm.category} 
                        onValueChange={(val) => setLibraryForm({ ...libraryForm, category: val })}
                      >
                        <SelectTrigger className={!libraryForm.category ? 'border-amber-500' : ''}>
                          <SelectValue placeholder="Pick a category…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="heading">Heading</SelectItem>
                          <SelectItem value="heeling">Heeling</SelectItem>
                          <SelectItem value="breakaway">Breakaway</SelectItem>
                          <SelectItem value="ranch_roping">Ranch Roping</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Difficulty {!libraryForm.difficulty && <span className="text-amber-600 text-xs">(needs value)</span>}</Label>
                      <Select 
                        value={libraryForm.difficulty} 
                        onValueChange={(val) => setLibraryForm({ ...libraryForm, difficulty: val })}
                      >
                        <SelectTrigger className={!libraryForm.difficulty ? 'border-amber-500' : ''}>
                          <SelectValue placeholder="Pick a difficulty…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="pro">Pro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Duration (optional)</Label>
                    <Input
                      value={libraryForm.duration}
                      onChange={(e) => setLibraryForm({ ...libraryForm, duration: e.target.value })}
                      placeholder="e.g., 5:30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <div className="relative">
                      <Textarea
                        value={libraryForm.description}
                        onChange={(e) => setLibraryForm({ ...libraryForm, description: e.target.value })}
                        rows={3}
                        required
                        className={isListening ? 'border-primary ring-2 ring-primary/20' : ''}
                      />
                      {isListening && (
                        <div className="absolute top-2 right-2 flex items-center gap-2 bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                          Listening...
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      {speechSupported && (
                        <Button
                          type="button"
                          variant={isListening ? "default" : "outline"}
                          size="sm"
                          onClick={toggleListening}
                          className={`gap-2 ${isListening ? 'bg-red-500 hover:bg-red-600' : ''}`}
                        >
                          {isListening ? (
                            <>
                              <MicOff className="w-4 h-4" />
                              Stop
                            </>
                          ) : (
                            <>
                              <Mic className="w-4 h-4" />
                              Speak
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={enhanceDescriptionWithAI}
                        disabled={enhancingDescription || !libraryForm.description.trim() || libraryForm.description.length < 10}
                        className="gap-2"
                      >
                        {enhancingDescription ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Enhancing...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            AI Enhance
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Tags (comma separated)</Label>
                    <Input
                      value={libraryForm.tags}
                      onChange={(e) => setLibraryForm({ ...libraryForm, tags: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Thumbnail Image (optional)</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Vimeo & YouTube videos auto-generate thumbnails. Uploaded MP4s capture a frame automatically. Upload one manually to override.
                    </p>
                    {showCropper && cropImage ? (
                      <ThumbnailCropper
                        image={cropImage}
                        aspectRatio={16/9}
                        onCropComplete={async (position) => {
                          if (isRepositioning && editingLibraryVideo) {
                            // Immediately save the position to the database
                            setUploadingThumbnail(true);
                            try {
                              await axios.put(`${API_URL}/api/admin/library/${editingLibraryVideo.id}`, {
                                thumbnail_position: position.objectPosition
                              });
                              // Update local state after successful save
                              setLibraryForm(prev => ({ 
                                ...prev, 
                                thumbnail_position: position.objectPosition
                              }));
                              setThumbnailPosition(position.objectPosition);
                              // Update the editing video object as well
                              setEditingLibraryVideo(prev => ({
                                ...prev,
                                thumbnail_position: position.objectPosition
                              }));
                            } catch (err) {
                              console.error('Failed to save thumbnail position:', err);
                              alert('Failed to save thumbnail position');
                            } finally {
                              setUploadingThumbnail(false);
                              setShowCropper(false);
                              setCropImage(null);
                              setIsRepositioning(false);
                            }
                          } else {
                            // New upload
                            setUploadingThumbnail(true);
                            try {
                              const response = await fetch(cropImage);
                              const blob = await response.blob();
                              const file = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' });
                              
                              const formData = new FormData();
                              formData.append('file', file);
                              const uploadResponse = await axios.post(`${API_URL}/api/admin/upload/image?folder=thumbnails`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setLibraryForm(prev => ({ 
                                ...prev, 
                                thumbnail_url: uploadResponse.data.url,
                                thumbnail_position: position.objectPosition
                              }));
                              setThumbnailPosition(position.objectPosition);
                            } catch (err) {
                              console.error('Failed to upload thumbnail:', err);
                              alert('Failed to upload thumbnail');
                            } finally {
                              setUploadingThumbnail(false);
                              setShowCropper(false);
                              setCropImage(null);
                            }
                          }
                        }}
                        onCancel={() => {
                          setShowCropper(false);
                          setCropImage(null);
                          setIsRepositioning(false);
                        }}
                      />
                    ) : libraryForm.thumbnail_url ? (
                      <div className="space-y-2">
                        <div 
                          className="relative w-40 h-24 rounded overflow-hidden"
                          style={{ 
                            backgroundImage: `url(${libraryForm.thumbnail_url})`,
                            backgroundSize: 'cover',
                            backgroundPosition: libraryForm.thumbnail_position || thumbnailPosition || 'center'
                          }}
                        >
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute top-1 right-1 h-6 w-6 p-0"
                            onClick={() => {
                              setLibraryForm({ ...libraryForm, thumbnail_url: '', thumbnail_position: '' });
                              setThumbnailPosition(null);
                            }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCropImage(libraryForm.thumbnail_url);
                            setIsRepositioning(true);
                            setShowCropper(true);
                          }}
                        >
                          <Move className="w-4 h-4 mr-1" />
                          Adjust Position
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          id="edit-thumbnail-upload"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = () => {
                              setCropImage(reader.result);
                              setIsRepositioning(false);
                              setShowCropper(true);
                            };
                            reader.readAsDataURL(file);
                            e.target.value = '';
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={uploadingThumbnail}
                          onClick={() => document.getElementById('edit-thumbnail-upload')?.click()}
                        >
                          {uploadingThumbnail ? 'Uploading...' : 'Upload Thumbnail'}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setEditingLibraryVideo(null)}>
                      Cancel
                    </Button>
                    <Button type="submit">Save Changes</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="knowledge">
            {/* Add Knowledge Button */}
            <div className="flex justify-end mb-6">
              <Dialog open={showAddKnowledge} onOpenChange={setShowAddKnowledge}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="add-knowledge-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Knowledge
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-display uppercase">Add Knowledge Entry</DialogTitle>
                    <DialogDescription>
                      Add information for the AI Training Assistant to reference when answering questions.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddKnowledge} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="knowledge-title">Title</Label>
                      <Input
                        id="knowledge-title"
                        value={knowledgeForm.title}
                        onChange={(e) => setKnowledgeForm({ ...knowledgeForm, title: e.target.value })}
                        placeholder="e.g., Proper Heading Position"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="knowledge-category">Category</Label>
                      <Select 
                        value={knowledgeForm.category} 
                        onValueChange={(val) => setKnowledgeForm({ ...knowledgeForm, category: val })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technique">Technique</SelectItem>
                          <SelectItem value="equipment">Equipment</SelectItem>
                          <SelectItem value="rules">Rules</SelectItem>
                          <SelectItem value="tips">Tips</SelectItem>
                          <SelectItem value="events">Events</SelectItem>
                          <SelectItem value="faq">FAQ</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="knowledge-content">Content</Label>
                      <Textarea
                        id="knowledge-content"
                        value={knowledgeForm.content}
                        onChange={(e) => setKnowledgeForm({ ...knowledgeForm, content: e.target.value })}
                        placeholder="Detailed information the AI can use to answer questions..."
                        rows={6}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="knowledge-tags">Tags (comma separated)</Label>
                      <Input
                        id="knowledge-tags"
                        value={knowledgeForm.tags}
                        onChange={(e) => setKnowledgeForm({ ...knowledgeForm, tags: e.target.value })}
                        placeholder="e.g., heading, position, basics"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setShowAddKnowledge(false)}>
                        Cancel
                      </Button>
                      <Button type="submit">Add Entry</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Knowledge Entries */}
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase">Knowledge Base Entries</CardTitle>
                <CardDescription>
                  The AI learns from coach responses automatically. Green entries are from real coaching interactions. You can also add manual entries.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground">Loading knowledge base...</p>
                  </div>
                ) : knowledgeEntries.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No knowledge entries yet. Add some manually or they'll be auto-generated from coach responses.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {knowledgeEntries.map(entry => (
                      <Card key={entry.id} className={`${entry.source === 'coach_response' ? 'bg-green-500/5 border-green-500/20' : 'bg-muted/30'}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <Badge variant="outline" className="text-xs capitalize">{entry.category}</Badge>
                                {entry.source === 'coach_response' && (
                                  <Badge className="text-xs bg-green-500/10 text-green-600 border-green-500/20">
                                    Auto from Coach
                                  </Badge>
                                )}
                                <h4 className="font-semibold">{entry.title}</h4>
                              </div>
                              {entry.student_question && (
                                <p className="text-xs text-muted-foreground italic mb-1">
                                  Student asked: "{entry.student_question}"
                                </p>
                              )}
                              <p className="text-sm text-muted-foreground line-clamp-2">{entry.content}</p>
                              {entry.coach_name && (
                                <p className="text-xs text-primary mt-1">— Coach {entry.coach_name}</p>
                              )}
                              {entry.tags?.length > 0 && (
                                <div className="flex gap-1 mt-2 flex-wrap">
                                  {entry.tags.map((tag, i) => (
                                    <span key={i} className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditKnowledge(entry)}
                                className="h-8 w-8 p-0"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteKnowledge(entry.id)}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Edit Knowledge Dialog */}
            <Dialog open={!!editingKnowledge} onOpenChange={(open) => !open && setEditingKnowledge(null)}>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-display uppercase">Edit Knowledge Entry</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateKnowledge} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-knowledge-title">Title</Label>
                    <Input
                      id="edit-knowledge-title"
                      value={knowledgeForm.title}
                      onChange={(e) => setKnowledgeForm({ ...knowledgeForm, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-knowledge-category">Category</Label>
                    <Select 
                      value={knowledgeForm.category} 
                      onValueChange={(val) => setKnowledgeForm({ ...knowledgeForm, category: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="technique">Technique</SelectItem>
                        <SelectItem value="equipment">Equipment</SelectItem>
                        <SelectItem value="rules">Rules</SelectItem>
                        <SelectItem value="tips">Tips</SelectItem>
                        <SelectItem value="events">Events</SelectItem>
                        <SelectItem value="faq">FAQ</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-knowledge-content">Content</Label>
                    <Textarea
                      id="edit-knowledge-content"
                      value={knowledgeForm.content}
                      onChange={(e) => setKnowledgeForm({ ...knowledgeForm, content: e.target.value })}
                      rows={6}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-knowledge-tags">Tags (comma separated)</Label>
                    <Input
                      id="edit-knowledge-tags"
                      value={knowledgeForm.tags}
                      onChange={(e) => setKnowledgeForm({ ...knowledgeForm, tags: e.target.value })}
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setEditingKnowledge(null)}>
                      Cancel
                    </Button>
                    <Button type="submit">Save Changes</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Events Management Tab */}
          <TabsContent value="events">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="font-display text-xl uppercase">Events Management</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Create and manage upcoming events, clinics, and competitions
                  </p>
                </div>
                <Button onClick={() => {
                  setEventForm({
                    title: '',
                    description: '',
                    event_date: '',
                    event_time: '',
                    location: '',
                    event_type: 'clinic',
                    registration_url: '',
                    registration_form_id: '',
                    image_url: '',
                    is_published: true
                  });
                  setEditingEvent(null);
                  setShowAddEvent(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Event
                </Button>
              </CardHeader>
              <CardContent>
                {events.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No events created yet. Click "Add Event" to create one.</p>
                ) : (
                  <div className="space-y-4">
                    {events.map(event => {
                      const eventDate = event.event_date ? new Date(event.event_date + 'T12:00:00') : null;
                      const isPast = eventDate && eventDate < new Date();
                      const isTBA = !event.event_date;
                      return (
                        <div key={event.id} className={`border rounded-lg p-4 ${isPast ? 'bg-muted/50' : ''}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-semibold">{event.title}</h4>
                                {isPast && (
                                  <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">Past</span>
                                )}
                                {event.is_published ? (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Published</span>
                                ) : (
                                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Draft</span>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {isTBA ? 'Date TBA' : new Date(event.event_date + 'T12:00:00').toLocaleDateString('en-US', { timeZone: 'America/Edmonton' })}
                                  {event.event_time && !isTBA && ` at ${event.event_time}`}
                                </span>
                                {event.location && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {event.location}
                                  </span>
                                )}
                                <span className="capitalize">{event.event_type?.replace('_', ' ')}</span>
                              </div>
                              {event.description && (
                                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{event.description}</p>
                              )}
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <Button variant="outline" size="sm" title="Edit" onClick={async () => {
                                setEventForm({
                                  title: event.title || '',
                                  description: event.description || '',
                                  event_date: event.event_date || '',
                                  event_time: event.event_time || '',
                                  location: event.location || '',
                                  map_url: event.map_url || '',
                                  event_type: event.event_type || 'clinic',
                                  registration_url: event.registration_url || '',
                                  registration_form_id: event.registration_form_id || '',
                                  image_url: event.image_url || '',
                                  sponsor_logo_url: event.sponsor_logo_url || '',
                                  is_published: event.is_published || false,
                                  opening_video_id: event.opening_video_id || '',
                                  video_display_order: event.video_display_order || [],
                                  homepage_title: event.homepage_title || '',
                                  homepage_description: event.homepage_description || '',
                                  homepage_thumbnail_url: event.homepage_thumbnail_url || '',
                                  association: event.association || '',
                                  instructor_coach_id: event.instructor_coach_id || '',
                                  youtube_channel_handle: event.youtube_channel_handle || '',
                                });
                                setEditingEvent(event);
                                setShowAddEvent(true);
                                // Lazy-load the videos attached to this event
                                // so the Playback & Homepage section can show
                                // the ordering + opening-video controls.
                                setLoadingEventVideos(true);
                                setEventVideosForModal([]);
                                try {
                                  const vidRes = await axios.get(
                                    `${API_URL}/api/admin/event-videos?event_id=${event.id}`,
                                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
                                  );
                                  setEventVideosForModal(vidRes.data?.videos || []);
                                } catch (e) {
                                  setEventVideosForModal([]);
                                } finally {
                                  setLoadingEventVideos(false);
                                }
                              }}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm" title="Duplicate" onClick={() => {
                                setEventForm({
                                  title: `${event.title || ''} (Copy)`,
                                  description: event.description || '',
                                  event_date: '',
                                  event_time: event.event_time || '',
                                  location: event.location || '',
                                  map_url: event.map_url || '',
                                  event_type: event.event_type || 'clinic',
                                  registration_url: '',
                                  registration_form_id: '',
                                  image_url: event.image_url || '',
                                  sponsor_logo_url: event.sponsor_logo_url || '',
                                  is_published: false
                                });
                                setEditingEvent(null);
                                setShowAddEvent(true);
                                toast.info('Event copied — update the details and save.');
                              }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                              </Button>
                              <Button variant="destructive" size="sm" onClick={async () => {
                                if (confirm('Delete this event? This cannot be undone.')) {
                                  try {
                                    await axios.delete(`${API_URL}/api/admin/events/${event.id}`, {
                                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                    });
                                    setEvents(prev => prev.filter(e => e.id !== event.id));
                                    toast.success('Event deleted');
                                  } catch (err) {
                                    toast.error('Failed to delete event');
                                  }
                                }
                              }}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add/Edit Event Dialog */}
            <Dialog open={showAddEvent} onOpenChange={setShowAddEvent}>
              <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingEvent ? 'Edit Event' : 'Add New Event'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setSavingEvent(true);
                  try {
                    const token = localStorage.getItem('token');
                    if (editingEvent) {
                      await axios.put(`${API_URL}/api/admin/events/${editingEvent.id}`, eventForm, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setEvents(prev => prev.map(ev => ev.id === editingEvent.id ? { ...ev, ...eventForm } : ev));
                      toast.success('Event updated');
                    } else {
                      const res = await axios.post(`${API_URL}/api/admin/events`, eventForm, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setEvents(prev => [res.data, ...prev]);
                      toast.success('Event created');
                    }
                    setShowAddEvent(false);
                  } catch (err) {
                    toast.error('Failed to save event: ' + (err.response?.data?.detail || err.message));
                  } finally {
                    setSavingEvent(false);
                  }
                }} className="space-y-4">
                  <div>
                    <Label htmlFor="event-title">Event Title *</Label>
                    <Input
                      id="event-title"
                      value={eventForm.title}
                      onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                      placeholder="e.g., Spring Roping Clinic"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="event-date">Event Date</Label>
                      <Input
                        id="event-date"
                        type="date"
                        value={eventForm.event_date}
                        onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Leave blank for TBA</p>
                    </div>
                    <div>
                      <Label htmlFor="event-time">Event Time</Label>
                      <Input
                        id="event-time"
                        type="time"
                        value={eventForm.event_time}
                        onChange={(e) => setEventForm({ ...eventForm, event_time: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="event-location">Location</Label>
                    <Input
                      id="event-location"
                      value={eventForm.location}
                      onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                      placeholder="e.g., Lazy J Arena, Calgary AB"
                    />
                  </div>

                  <div>
                    <Label htmlFor="event-map-url">Google Maps Link (optional)</Label>
                    <Input
                      id="event-map-url"
                      value={eventForm.map_url || ''}
                      onChange={(e) => setEventForm({ ...eventForm, map_url: e.target.value })}
                      placeholder="Paste Google Maps link for exact location"
                      data-testid="event-map-url-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Paste a Google Maps share link to override the auto-detected map pin</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="event-type">Event Type</Label>
                    <Select value={eventForm.event_type} onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clinic">Clinic</SelectItem>
                        <SelectItem value="competition">Competition</SelectItem>
                        <SelectItem value="jackpot">Jackpot</SelectItem>
                        <SelectItem value="practice">Practice</SelectItem>
                        <SelectItem value="live_recording">Live Recording</SelectItem>
                        <SelectItem value="tba">TBA</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="event-description">Description</Label>
                    <Textarea
                      id="event-description"
                      value={eventForm.description}
                      onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                      placeholder="Describe the event..."
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="event-registration">Registration URL</Label>
                    <Input
                      id="event-registration"
                      value={eventForm.registration_url}
                      onChange={(e) => setEventForm({ ...eventForm, registration_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>

                  <div>
                    <Label>Link Registration Form (for spots display)</Label>
                    <Select
                      value={eventForm.registration_form_id || 'none'}
                      onValueChange={(v) => setEventForm({ ...eventForm, registration_form_id: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger data-testid="event-reg-form-select">
                        <SelectValue placeholder="Select a registration form..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {registrationForms.map(rf => (
                          <SelectItem key={rf.id} value={rf.id}>
                            {rf.event_name} ({rf.registration_count || 0} registered)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">Links this event to a registration form to show spots remaining on the Events page.</p>
                  </div>
                  
                  <div>
                    <Label>Event Image</Label>
                    <div className="space-y-2">
                      {eventForm.image_url && (
                        <div className="relative w-32 h-20 rounded overflow-hidden border">
                          <img src={eventForm.image_url} alt="Event" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => setEventForm({ ...eventForm, image_url: '' })}
                            className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('folder', 'events');
                          
                          try {
                            const res = await axios.post(`${API_URL}/api/admin/upload/image`, formData, {
                              headers: { 
                                Authorization: `Bearer ${localStorage.getItem('token')}`,
                                'Content-Type': 'multipart/form-data'
                              }
                            });
                            setEventForm({ ...eventForm, image_url: res.data.url });
                            toast.success('Image uploaded');
                          } catch (err) {
                            toast.error('Failed to upload image');
                          }
                        }}
                      />
                      <p className="text-xs text-muted-foreground">Upload an image for the event (JPG, PNG, WebP)</p>
                    </div>
                  </div>

                  <div>
                    <Label>Sponsor Logo (optional)</Label>
                    <p className="text-xs text-muted-foreground mb-2">Upload a sponsor logo to watermark on the bottom-left of event photos and videos. EM logo stays on the bottom-right.</p>
                    <div className="space-y-2">
                      {eventForm.sponsor_logo_url && (
                        <div className="relative w-32 h-20 rounded overflow-hidden border bg-white flex items-center justify-center p-2">
                          <img src={eventForm.sponsor_logo_url} alt="Sponsor" className="max-w-full max-h-full object-contain" />
                          <button
                            type="button"
                            onClick={() => setEventForm({ ...eventForm, sponsor_logo_url: '' })}
                            className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <Input
                        type="file"
                        accept="image/*"
                        data-testid="sponsor-logo-upload"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('folder', 'sponsors');
                          
                          try {
                            const res = await axios.post(`${API_URL}/api/admin/upload/image`, formData, {
                              headers: { 
                                Authorization: `Bearer ${localStorage.getItem('token')}`,
                                'Content-Type': 'multipart/form-data'
                              }
                            });
                            setEventForm({ ...eventForm, sponsor_logo_url: res.data.url });
                            toast.success('Sponsor logo uploaded');
                          } catch (err) {
                            toast.error('Failed to upload sponsor logo');
                          }
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id="event-published"
                      checked={eventForm.is_published}
                      onCheckedChange={(checked) => setEventForm({ ...eventForm, is_published: checked })}
                    />
                    <Label htmlFor="event-published">Publish Event</Label>
                    <span className="text-xs text-muted-foreground">(Only published events appear on the Events page)</span>
                  </div>

                  {/* Playback & Homepage — only shown when editing an
                      existing event so attached videos can be enumerated. */}
                  {editingEvent && (
                    <div className="border-t pt-4 mt-2 space-y-4" data-testid="event-playback-homepage">
                      <div>
                        <h3 className="font-semibold text-sm uppercase tracking-wider">Playback &amp; Homepage</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          Controls how this event appears when pinned to a home page rail. All fields are optional — they fall back to the event's main title, description, image and oldest-first video order.
                        </p>
                      </div>

                      {/* YouTube channel sync — paste a handle like
                          @johnathoncoleshow and hit Sync. Pulls every
                          upload from that channel's uploads playlist into
                          this event. Sorted into the event tray next to
                          admin-uploaded clips, newest first. */}
                      <div className="space-y-2 rounded-md border border-dashed border-amber-400/50 bg-amber-50/40 dark:bg-amber-900/10 p-3">
                        <Label htmlFor="event-yt-handle" className="flex items-center gap-2">
                          YouTube Channel
                          <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-700 dark:text-amber-300">optional</span>
                        </Label>
                        <div className="flex gap-2 items-stretch">
                          <input
                            id="event-yt-handle"
                            type="text"
                            placeholder="@johnathoncoleshow"
                            value={eventForm.youtube_channel_handle || ''}
                            onChange={(e) => setEventForm({ ...eventForm, youtube_channel_handle: e.target.value })}
                            className="flex-1 border rounded-md px-3 py-2 bg-background text-sm"
                            data-testid="event-yt-handle"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={youtubeSyncing || !(eventForm.youtube_channel_handle || '').trim() || !editingEvent?.id}
                            onClick={async () => {
                              const handle = (eventForm.youtube_channel_handle || '').trim();
                              if (!handle || !editingEvent?.id) return;
                              setYoutubeSyncing(true);
                              setYoutubeSyncResult(null);
                              try {
                                const resp = await axios.post(
                                  `${API_URL}/api/admin/events/${editingEvent.id}/sync-youtube`,
                                  { handle },
                                  { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
                                );
                                setYoutubeSyncResult(resp.data);
                                toast.success(`Synced ${resp.data.total} videos from ${resp.data.channel_title}`);
                                // Refresh attached videos so the Opening Video
                                // dropdown and order-row immediately show them.
                                const vidRes = await axios.get(
                                  `${API_URL}/api/admin/event-videos?event_id=${editingEvent.id}`,
                                  { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
                                );
                                setEventVideosForModal(vidRes.data?.videos || []);
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'YouTube sync failed');
                              } finally {
                                setYoutubeSyncing(false);
                              }
                            }}
                            data-testid="event-yt-sync"
                          >
                            {youtubeSyncing ? 'Syncing…' : 'Sync Now'}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Paste a <code className="font-mono">@handle</code> or full YouTube channel URL. Every upload will be pulled into this event's tray (nightly auto-refresh coming — hit Sync Now any time).
                        </p>
                        {youtubeSyncResult && (
                          <p className="text-xs text-green-700 dark:text-green-300">
                            Last sync: <strong>{youtubeSyncResult.total}</strong> videos ({youtubeSyncResult.inserted} new, {youtubeSyncResult.updated} updated) from <strong>{youtubeSyncResult.channel_title}</strong>
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="event-opening-video">Opening Video</Label>
                        <select
                          id="event-opening-video"
                          className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                          value={eventForm.opening_video_id || ''}
                          onChange={(e) => setEventForm({ ...eventForm, opening_video_id: e.target.value })}
                          disabled={loadingEventVideos || eventVideosForModal.length === 0}
                          data-testid="event-opening-video-select"
                        >
                          <option value="">— First video by upload date —</option>
                          {eventVideosForModal.map((v) => (
                            <option key={v.id} value={v.id}>{v.filename || v.id}</option>
                          ))}
                        </select>
                        <p className="text-xs text-muted-foreground">
                          {loadingEventVideos
                            ? 'Loading videos…'
                            : eventVideosForModal.length === 0
                              ? 'No videos attached to this event yet. Add some from the Event Videos tab.'
                              : `${eventVideosForModal.length} video${eventVideosForModal.length === 1 ? '' : 's'} attached to this event.`}
                        </p>
                      </div>

                      {eventVideosForModal.length > 1 && (
                        <div className="space-y-2">
                          <Label>Video Display Order</Label>
                          <p className="text-xs text-muted-foreground">
                            Drag the arrows to reorder. The first in the list plays by default if no Opening Video is set.
                          </p>
                          <div className="space-y-1 border rounded-md p-2" data-testid="event-video-order">
                            {(() => {
                              // Resolve current order: honor display_order first,
                              // then append any videos not listed in it.
                              const desired = eventForm.video_display_order || [];
                              const byId = new Map(eventVideosForModal.map((v) => [v.id, v]));
                              const ordered = desired.map((id) => byId.get(id)).filter(Boolean);
                              const seen = new Set(ordered.map((v) => v.id));
                              for (const v of eventVideosForModal) if (!seen.has(v.id)) ordered.push(v);

                              const moveOrder = (i, dir) => {
                                const j = i + dir;
                                if (j < 0 || j >= ordered.length) return;
                                const copy = ordered.slice();
                                [copy[i], copy[j]] = [copy[j], copy[i]];
                                setEventForm({ ...eventForm, video_display_order: copy.map((v) => v.id) });
                              };

                              return ordered.map((v, i) => (
                                <div
                                  key={v.id}
                                  className="flex items-center gap-2 py-1"
                                  data-testid={`event-video-order-row-${v.id}`}
                                >
                                  <Button type="button" size="icon" variant="ghost" onClick={() => moveOrder(i, -1)} disabled={i === 0}>
                                    <ArrowUp className="w-4 h-4" />
                                  </Button>
                                  <Button type="button" size="icon" variant="ghost" onClick={() => moveOrder(i, 1)} disabled={i === ordered.length - 1}>
                                    <ArrowDown className="w-4 h-4" />
                                  </Button>
                                  <span className="text-sm flex-1 truncate">{i + 1}. {v.filename || v.id}</span>
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label htmlFor="event-homepage-title">Homepage Title (optional)</Label>
                        <Input
                          id="event-homepage-title"
                          placeholder={eventForm.title || 'e.g. Kyle Rodeo'}
                          value={eventForm.homepage_title || ''}
                          onChange={(e) => setEventForm({ ...eventForm, homepage_title: e.target.value })}
                          data-testid="event-homepage-title"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="event-homepage-desc">Homepage Description (optional)</Label>
                        <Textarea
                          id="event-homepage-desc"
                          rows={2}
                          placeholder="Short card description shown on the home page"
                          value={eventForm.homepage_description || ''}
                          onChange={(e) => setEventForm({ ...eventForm, homepage_description: e.target.value })}
                          data-testid="event-homepage-desc"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="event-homepage-thumb">Homepage Thumbnail URL (optional)</Label>
                        <Input
                          id="event-homepage-thumb"
                          placeholder="Falls back to the event image if blank"
                          value={eventForm.homepage_thumbnail_url || ''}
                          onChange={(e) => setEventForm({ ...eventForm, homepage_thumbnail_url: e.target.value })}
                          data-testid="event-homepage-thumb"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="event-instructor">Instructor Coach</Label>
                        <select
                          id="event-instructor"
                          className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                          value={eventForm.instructor_coach_id || ''}
                          onChange={(e) => setEventForm({ ...eventForm, instructor_coach_id: e.target.value })}
                          data-testid="event-instructor-select"
                        >
                          <option value="">— No coach —</option>
                          {coaches.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                        <p className="text-xs text-muted-foreground">
                          When set, every video attached to this event is credited to this coach and shows up on their hover-peek on the home page.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="event-association">Association</Label>
                        <CreatableSelect
                          field="event_association"
                          value={eventForm.association || ''}
                          onValueChange={(val) => setEventForm({ ...eventForm, association: val })}
                          placeholder="Pick or add association (CCA, CRA, CPRA, Independent…)"
                          testId="event-association-select"
                        />
                        <p className="text-xs text-muted-foreground">
                          Used so a rail like "CCA Rodeos" can later auto-populate events by association. Add new values with "+ Add new…".
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowAddEvent(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={savingEvent}>
                      {savingEvent ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      {editingEvent ? 'Update Event' : 'Create Event'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Event Photos Tab */}
          <TabsContent value="event-photos">
            <EventPhotosTab />
          </TabsContent>

          {/* Event Videos Tab */}
          <TabsContent value="event-videos">
            <EventVideosTab />
          </TabsContent>

          {/* Registrations Manager Tab */}
          <TabsContent value="registrations">
            {/* Top-level Manual Registration Form */}
            {showManualReg && !viewingRegistrations && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <div className="space-y-3" data-testid="manual-reg-form-toplevel">
                    <div className="flex items-center justify-between">
                      <h4 className="font-display font-bold uppercase text-sm">Add Registration Manually</h4>
                      <Button variant="ghost" size="sm" onClick={() => setShowManualReg(false)}><X className="w-4 h-4" /></Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium">Event / Registration Form *</label>
                        <Select value={manualRegForm.form_id || ''} onValueChange={v => {
                          const f = registrationForms.find(f => f.id === v);
                          setManualRegForm(p => ({...p, form_id: v, discipline: ''}));
                          setManualRegSelectedForm(f || null);
                        }}>
                          <SelectTrigger data-testid="manual-reg-event-select">
                            <SelectValue placeholder="Select event..." />
                          </SelectTrigger>
                          <SelectContent>
                            {registrationForms.map(f => (
                              <SelectItem key={f.id} value={f.id}>{f.event_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium">Category/Discipline *</label>
                        <Select value={manualRegForm.discipline} onValueChange={v => setManualRegForm(p => ({...p, discipline: v}))}>
                          <SelectTrigger data-testid="manual-reg-discipline-top">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {(manualRegSelectedForm?.categories || []).map(cat => (
                              <SelectItem key={cat.name} value={cat.name.toLowerCase()}>{cat.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium">Name *</label>
                        <Input value={manualRegForm.name} onChange={e => setManualRegForm(p => ({...p, name: e.target.value}))} placeholder="Full name" />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Email *</label>
                        <Input value={manualRegForm.email} onChange={e => setManualRegForm(p => ({...p, email: e.target.value}))} placeholder="Email" />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Phone</label>
                        <Input value={manualRegForm.phone} onChange={e => setManualRegForm(p => ({...p, phone: e.target.value}))} placeholder="Phone" />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Address</label>
                        <Input value={manualRegForm.address} onChange={e => setManualRegForm(p => ({...p, address: e.target.value}))} placeholder="City, Province" />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Experience Level</label>
                        <Select value={manualRegForm.experience_level} onValueChange={v => setManualRegForm(p => ({...p, experience_level: v}))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium">Payment Status</label>
                        <Select value={manualRegForm.payment_status} onValueChange={v => setManualRegForm(p => ({...p, payment_status: v}))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium">Special Requests</label>
                        <Input value={manualRegForm.special_requests} onChange={e => setManualRegForm(p => ({...p, special_requests: e.target.value}))} placeholder="Optional notes" />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" onClick={async () => {
                        if (!manualRegForm.form_id || !manualRegForm.name || !manualRegForm.email || !manualRegForm.discipline) {
                          toast.error('Event, name, email, and category are required');
                          return;
                        }
                        try {
                          const token = localStorage.getItem('token');
                          await axios.post(`${API_URL}/api/admin/registrations/${manualRegForm.form_id}/manual`, manualRegForm, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success(`${manualRegForm.name} registered!`);
                          setManualRegForm({ name: '', email: '', phone: '', address: '', discipline: '', experience_level: 'intermediate', special_requests: '', payment_status: 'paid', form_id: manualRegForm.form_id });
                          fetchData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to add registration');
                        }
                      }} data-testid="manual-reg-submit-top">
                        <CheckCircle className="w-4 h-4 mr-1" /> Save Registration
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setShowManualReg(false)}>Done</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {viewingRegistrations ? (
              // View registrations for a specific form
              <Card>
                <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
                  <div>
                    <Button variant="ghost" size="sm" onClick={() => { setViewingRegistrations(null); setRegistrationEntries([]); }} className="mb-2">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Forms
                    </Button>
                    <CardTitle className="font-display text-xl uppercase">{viewingRegistrations.event_name}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {registrationEntries.length} registration(s) • Registration URL: <code className="bg-muted px-2 py-0.5 rounded text-xs">/register/{viewingRegistrations.slug}</code>
                    </p>
                  </div>
                  <Button size="sm" onClick={() => { setShowManualReg(true); setManualRegForm({ name: '', email: '', phone: '', address: '', discipline: '', experience_level: 'intermediate', special_requests: '', payment_status: 'paid' }); }} data-testid="add-manual-reg-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Registration
                  </Button>
                </CardHeader>
                <CardContent>
                  {/* Manual Registration Form */}
                  {showManualReg && (
                    <div className="border rounded-lg p-4 mb-6 bg-muted/30 space-y-3" data-testid="manual-reg-form">
                      <h4 className="font-display font-bold uppercase text-sm">Add Registration Manually</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium">Name *</label>
                          <Input value={manualRegForm.name} onChange={e => setManualRegForm(p => ({...p, name: e.target.value}))} placeholder="Full name" data-testid="manual-reg-name" />
                        </div>
                        <div>
                          <label className="text-xs font-medium">Email *</label>
                          <Input value={manualRegForm.email} onChange={e => setManualRegForm(p => ({...p, email: e.target.value}))} placeholder="Email" data-testid="manual-reg-email" />
                        </div>
                        <div>
                          <label className="text-xs font-medium">Phone</label>
                          <Input value={manualRegForm.phone} onChange={e => setManualRegForm(p => ({...p, phone: e.target.value}))} placeholder="Phone" data-testid="manual-reg-phone" />
                        </div>
                        <div>
                          <label className="text-xs font-medium">Address</label>
                          <Input value={manualRegForm.address} onChange={e => setManualRegForm(p => ({...p, address: e.target.value}))} placeholder="City, Province" data-testid="manual-reg-address" />
                        </div>
                        <div>
                          <label className="text-xs font-medium">Category/Discipline *</label>
                          <Select value={manualRegForm.discipline} onValueChange={v => setManualRegForm(p => ({...p, discipline: v}))}>
                            <SelectTrigger data-testid="manual-reg-discipline">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              {(viewingRegistrations?.categories || []).map(cat => (
                                <SelectItem key={cat.name} value={cat.name.toLowerCase()}>{cat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs font-medium">Experience Level</label>
                          <Select value={manualRegForm.experience_level} onValueChange={v => setManualRegForm(p => ({...p, experience_level: v}))}>
                            <SelectTrigger data-testid="manual-reg-level">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="beginner">Beginner</SelectItem>
                              <SelectItem value="intermediate">Intermediate</SelectItem>
                              <SelectItem value="advanced">Advanced</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs font-medium">Payment Status</label>
                          <Select value={manualRegForm.payment_status} onValueChange={v => setManualRegForm(p => ({...p, payment_status: v}))}>
                            <SelectTrigger data-testid="manual-reg-payment">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="paid">Paid</SelectItem>
                              <SelectItem value="pending">Pending</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs font-medium">Special Requests</label>
                          <Input value={manualRegForm.special_requests} onChange={e => setManualRegForm(p => ({...p, special_requests: e.target.value}))} placeholder="Optional notes" data-testid="manual-reg-notes" />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button size="sm" onClick={async () => {
                          if (!manualRegForm.name || !manualRegForm.email || !manualRegForm.discipline) {
                            toast.error('Name, email, and category are required');
                            return;
                          }
                          try {
                            const token = localStorage.getItem('token');
                            const res = await axios.post(`${API_URL}/api/admin/registrations/${viewingRegistrations.id}/manual`, manualRegForm, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            setRegistrationEntries(prev => [...prev, res.data.registration]);
                            toast.success(`${manualRegForm.name} registered!`);
                            setManualRegForm({ name: '', email: '', phone: '', address: '', discipline: '', experience_level: 'intermediate', special_requests: '', payment_status: 'paid' });
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to add registration');
                          }
                        }} data-testid="manual-reg-submit">
                          <CheckCircle className="w-4 h-4 mr-1" /> Save Registration
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setShowManualReg(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                  {loadingEntries ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
                  ) : registrationEntries.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No registrations yet.</p>
                  ) : (
                    <div className="overflow-x-auto overflow-y-auto max-h-[60vh]">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-background z-10">
                          <tr className="border-b">
                            <th className="text-left py-3 px-2 font-semibold">Name</th>
                            <th className="text-left py-3 px-2 font-semibold">Email</th>
                            <th className="text-left py-3 px-2 font-semibold hidden sm:table-cell">Phone</th>
                            <th className="text-left py-3 px-2 font-semibold hidden md:table-cell">Discipline</th>
                            <th className="text-left py-3 px-2 font-semibold hidden md:table-cell">Level</th>
                            <th className="text-left py-3 px-2 font-semibold">Paid</th>
                            <th className="text-left py-3 px-2 font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {registrationEntries.map(entry => (
                            <tr key={entry.id} className="border-b hover:bg-muted/50">
                              <td className="py-3 px-2">
                                <div>
                                  <p className="font-medium">{entry.name}</p>
                                  <p className="text-xs text-muted-foreground">{entry.address}</p>
                                </div>
                              </td>
                              <td className="py-3 px-2">{entry.email}</td>
                              <td className="py-3 px-2 hidden sm:table-cell">{entry.phone}</td>
                              <td className="py-3 px-2 hidden md:table-cell capitalize">{entry.discipline}</td>
                              <td className="py-3 px-2 hidden md:table-cell capitalize">{entry.experience_level}</td>
                              <td className="py-3 px-2">
                                {entry.payment_status === 'paid' ? (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Paid</span>
                                ) : (
                                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Pending</span>
                                )}
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex gap-1">
                                  {entry.payment_status !== 'paid' && (
                                    <Button variant="outline" size="sm" onClick={async () => {
                                      try {
                                        await axios.put(`${API_URL}/api/admin/registrations/${viewingRegistrations.id}/entries/${entry.id}`, 
                                          { payment_status: 'paid' },
                                          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
                                        );
                                        setRegistrationEntries(prev => prev.map(e => e.id === entry.id ? {...e, payment_status: 'paid'} : e));
                                        toast.success('Marked as paid');
                                      } catch (err) {
                                        toast.error('Failed to update');
                                      }
                                    }}>
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button variant="destructive" size="sm" onClick={async () => {
                                    if (confirm('Delete this registration?')) {
                                      try {
                                        await axios.delete(`${API_URL}/api/admin/registrations/${viewingRegistrations.id}/entries/${entry.id}`, {
                                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                        });
                                        setRegistrationEntries(prev => prev.filter(e => e.id !== entry.id));
                                        toast.success('Registration deleted');
                                      } catch (err) {
                                        toast.error('Failed to delete');
                                      }
                                    }
                                  }}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {/* Show special requests in expandable section */}
                  {registrationEntries.filter(e => e.special_requests).length > 0 && (
                    <div className="mt-6 border-t pt-4">
                      <h4 className="font-semibold mb-3">Special Requests / Notes</h4>
                      <div className="space-y-2">
                        {registrationEntries.filter(e => e.special_requests).map(entry => (
                          <div key={entry.id} className="bg-muted/50 rounded p-3">
                            <p className="font-medium text-sm">{entry.name}:</p>
                            <p className="text-sm text-muted-foreground">{entry.special_requests}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              // List of registration forms
              <Card>
                <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="font-display text-xl uppercase">Registration Manager</CardTitle>
                    <p className="text-sm text-muted-foreground">Create registration pages for clinics and events</p>
                  </div>
                  <Button onClick={() => {
                    setRegistrationForm({
                      event_name: '',
                      slug: '',
                      description: '',
                      stripe_link: '',
                      price: 0,
                      is_open: true,
                      max_total: 0,
                      categories: []
                    });
                    setEditingRegistration(null);
                    setShowAddRegistration(true);
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Registration
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowManualReg(true); setManualRegForm({ name: '', email: '', phone: '', address: '', discipline: '', experience_level: 'intermediate', special_requests: '', payment_status: 'paid', form_id: '' }); setManualRegSelectedForm(null); }} data-testid="add-manual-reg-top-btn">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Registrant
                  </Button>
                </CardHeader>
                <CardContent>
                  {registrationForms.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No registration forms created yet.</p>
                  ) : (
                    <div className="space-y-4">
                      {registrationForms.map(form => (
                        <div key={form.id} className="border rounded-lg p-4">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-semibold">{form.event_name}</h4>
                                {form.linked_event ? (
                                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">Linked to: {form.linked_event}</span>
                                ) : (
                                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Not linked to event</span>
                                )}
                                {form.is_open ? (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Open</span>
                                ) : (
                                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Closed</span>
                                )}
                                <span className="text-xs bg-muted px-2 py-0.5 rounded">{form.registration_count || 0} registered</span>
                                {form.max_total > 0 && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                    {(form.categories || []).filter(c => c.max > 0).map(c => `${c.name}: ${(form.category_counts || {})[c.name.toLowerCase()] || 0}/${c.max}`).join(' / ')} — max {form.max_total}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                                <span>URL: <code className="bg-muted px-1 rounded">/register/{form.slug}</code></span>
                                {form.price > 0 && <span>${form.price} CAD</span>}
                              </div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <Button variant="outline" size="sm" onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}/register/${form.slug}`);
                                toast.success('Link copied!');
                              }}>
                                <Link2 className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={async () => {
                                setViewingRegistrations(form);
                                setLoadingEntries(true);
                                try {
                                  const res = await axios.get(`${API_URL}/api/admin/registrations/${form.id}/entries`, {
                                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                  });
                                  setRegistrationEntries(res.data.registrations || []);
                                } catch (err) {
                                  toast.error('Failed to load registrations');
                                } finally {
                                  setLoadingEntries(false);
                                }
                              }}>
                                <Users className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => {
                                setRegistrationForm({
                                  event_name: form.event_name,
                                  slug: form.slug,
                                  description: form.description || '',
                                  stripe_link: form.stripe_link,
                                  price: form.price || 0,
                                  is_open: form.is_open,
                                  max_total: form.max_total || 0,
                                  categories: form.categories || []
                                });
                                setEditingRegistration(form);
                                setShowAddRegistration(true);
                              }}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="destructive" size="sm" onClick={async () => {
                                if (confirm('Delete this registration form and all registrations?')) {
                                  try {
                                    await axios.delete(`${API_URL}/api/admin/registrations/${form.id}`, {
                                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                    });
                                    setRegistrationForms(prev => prev.filter(f => f.id !== form.id));
                                    toast.success('Registration form deleted');
                                  } catch (err) {
                                    toast.error('Failed to delete');
                                  }
                                }
                              }}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Add/Edit Registration Form Dialog */}
            <Dialog open={showAddRegistration} onOpenChange={setShowAddRegistration}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingRegistration ? 'Edit Registration Form' : 'Create Registration Form'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setSavingRegistration(true);
                  try {
                    const token = localStorage.getItem('token');
                    if (editingRegistration) {
                      await axios.put(`${API_URL}/api/admin/registrations/${editingRegistration.id}`, registrationForm, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success('Registration form updated');
                    } else {
                      await axios.post(`${API_URL}/api/admin/registrations`, registrationForm, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success('Registration form created');
                    }
                    setShowAddRegistration(false);
                    // Refresh forms from server to avoid stale state
                    try {
                      const regRes = await axios.get(`${API_URL}/api/admin/registrations`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setRegistrationForms(regRes.data.forms || []);
                    } catch {}
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to save');
                  } finally {
                    setSavingRegistration(false);
                  }
                }} className="space-y-4">
                  <div>
                    <Label htmlFor="reg-name">Event/Clinic Name *</Label>
                    <Input
                      id="reg-name"
                      value={registrationForm.event_name}
                      onChange={(e) => {
                        const name = e.target.value;
                        setRegistrationForm({ 
                          ...registrationForm, 
                          event_name: name,
                          slug: registrationForm.slug || name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').trim()
                        });
                      }}
                      placeholder="e.g., Spring Roping Clinic 2026"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="reg-slug">URL Slug *</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">/register/</span>
                      <Input
                        id="reg-slug"
                        value={registrationForm.slug}
                        onChange={(e) => setRegistrationForm({ ...registrationForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                        placeholder="spring-roping-clinic-2026"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="reg-desc">Description</Label>
                    <Textarea
                      id="reg-desc"
                      value={registrationForm.description}
                      onChange={(e) => setRegistrationForm({ ...registrationForm, description: e.target.value })}
                      placeholder="Brief description shown on registration page..."
                      rows={2}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="reg-stripe">Stripe Payment Link *</Label>
                    <Input
                      id="reg-stripe"
                      value={registrationForm.stripe_link}
                      onChange={(e) => setRegistrationForm({ ...registrationForm, stripe_link: e.target.value })}
                      placeholder="https://buy.stripe.com/..."
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="reg-price">Price (CAD)</Label>
                    <Input
                      id="reg-price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={registrationForm.price}
                      onChange={(e) => setRegistrationForm({ ...registrationForm, price: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Display price only - payment handled by Stripe</p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id="reg-open"
                      checked={registrationForm.is_open}
                      onCheckedChange={(checked) => setRegistrationForm({ ...registrationForm, is_open: checked })}
                    />
                    <Label htmlFor="reg-open">Registration Open</Label>
                  </div>
                  
                  {/* Capacity Limits */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold">Categories & Capacity</h4>
                      <Button type="button" variant="outline" size="sm" onClick={() => {
                        setRegistrationForm({
                          ...registrationForm,
                          categories: [...(registrationForm.categories || []), { name: '', max: 0 }]
                        });
                      }}>
                        <Plus className="w-3 h-3 mr-1" /> Add Category
                      </Button>
                    </div>
                    
                    {(registrationForm.categories || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground">No categories yet. Add categories like "Header", "Heeler", "Barrel Racer", etc.</p>
                    ) : (
                      <div className="space-y-2">
                        {(registrationForm.categories || []).map((cat, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input
                              placeholder="Category name (e.g. Header)"
                              value={cat.name}
                              onChange={(e) => {
                                const updated = [...registrationForm.categories];
                                updated[idx] = { ...updated[idx], name: e.target.value };
                                setRegistrationForm({ ...registrationForm, categories: updated });
                              }}
                              className="flex-1"
                              data-testid={`reg-cat-name-${idx}`}
                            />
                            <Input
                              type="number"
                              min="0"
                              placeholder="Max"
                              value={cat.max}
                              onChange={(e) => {
                                const updated = [...registrationForm.categories];
                                updated[idx] = { ...updated[idx], max: parseInt(e.target.value) || 0 };
                                setRegistrationForm({ ...registrationForm, categories: updated });
                              }}
                              className="w-20"
                              data-testid={`reg-cat-max-${idx}`}
                            />
                            <Button type="button" variant="ghost" size="sm" onClick={() => {
                              const updated = registrationForm.categories.filter((_, i) => i !== idx);
                              setRegistrationForm({ ...registrationForm, categories: updated });
                            }}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-3">
                      <Label htmlFor="reg-max-total">Max Total (0 = unlimited)</Label>
                      <Input
                        id="reg-max-total"
                        type="number"
                        min="0"
                        value={registrationForm.max_total}
                        onChange={(e) => setRegistrationForm({ ...registrationForm, max_total: parseInt(e.target.value) || 0 })}
                        data-testid="reg-max-total"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Overall cap across all categories. Set 0 for no limit.</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowAddRegistration(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={savingRegistration}>
                      {savingRegistration ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      {editingRegistration ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Live Stream Tab */}
          <TabsContent value="raw-uploads">
            <RawUploadsTab />
          </TabsContent>

          <TabsContent value="live-stream">
            <div className="space-y-6">
              {/* ================= FREE EVENT OVERRIDE ================= */}
              {/* Independent one-click switch for sponsor-covered events.
                  Never touches the main live stream config below. */}
              <Card className={`border-2 ${freeEvent.is_active ? 'border-green-600 bg-green-50/50 dark:bg-green-950/20' : 'border-green-600/30'}`}>
                <CardHeader>
                  <CardTitle className="font-display text-xl uppercase flex items-center gap-3">
                    Free Event — Sponsor Coverage
                    {freeEvent.is_active && (
                      <Badge className="bg-green-600 animate-pulse">LIVE — FREE FOR ALL</Badge>
                    )}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    One-click override that lets ANYONE watch — no login, no subscription, no TV pairing.
                    Turn ON when a sponsor covers the stream. Turn OFF after. Your regular Live Stream Settings below stay untouched.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {freeEvent.is_active && !freeEvent.embed_code && !liveStreamSettings.embed_code && (
                    <div className="rounded-lg border-2 border-red-500 bg-red-50 dark:bg-red-950/30 p-4">
                      <div className="font-bold text-red-800 dark:text-red-300 mb-1">
                        ⚠ No stream URL set — viewers can't see anything
                      </div>
                      <div className="text-sm text-red-800/90 dark:text-red-300/90">
                        You started the free event but neither this card nor the main Live Stream Settings has a Vimeo/YouTube URL. Paste one below (or in the main Live Stream card) and it'll go live instantly.
                      </div>
                    </div>
                  )}
                  {freeEvent.is_active && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="rounded-lg border border-green-600/40 bg-white dark:bg-green-950/40 p-4 text-center">
                        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Watching Now</div>
                        <div className="text-3xl font-display font-bold text-green-700 dark:text-green-400" data-testid="free-event-live-count">
                          {freeEventStats.watching_now || 0}
                        </div>
                      </div>
                      <div className="rounded-lg border border-green-600/40 bg-white dark:bg-green-950/40 p-4 text-center">
                        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total Unique</div>
                        <div className="text-3xl font-display font-bold" data-testid="free-event-total-count">
                          {freeEventStats.total_unique_viewers || 0}
                        </div>
                      </div>
                      <div className="rounded-lg border border-green-600/40 bg-white dark:bg-green-950/40 p-4 text-center">
                        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Peak Concurrent</div>
                        <div className="text-3xl font-display font-bold" data-testid="free-event-peak-count">
                          {freeEventStats.peak_concurrent || 0}
                        </div>
                      </div>
                      <div className="rounded-lg border border-green-600/40 bg-white dark:bg-green-950/40 p-4 text-center">
                        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">TV Screens</div>
                        <div className="text-3xl font-display font-bold" data-testid="free-event-tv-count">
                          {freeEventStats.tv_screens || 0}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="free-event-title">Event Name (shown to viewers)</Label>
                    <Input
                      id="free-event-title"
                      value={freeEvent.title}
                      onChange={(e) => setFreeEvent(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Innisfail Jackpot — Free Live Stream"
                      disabled={savingFreeEvent}
                      data-testid="free-event-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="free-event-embed">Vimeo / YouTube URL or Embed Code</Label>
                    <textarea
                      id="free-event-embed"
                      value={freeEvent.embed_code}
                      onChange={(e) => setFreeEvent(prev => ({ ...prev, embed_code: e.target.value }))}
                      placeholder="Paste the URL for this specific free event. Leave empty to reuse the URL from Live Stream Settings below."
                      disabled={savingFreeEvent}
                      className="w-full h-24 p-3 border rounded-md font-mono text-sm resize-none"
                      data-testid="free-event-embed"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    {!freeEvent.is_active ? (
                      <Button
                        type="button"
                        onClick={async () => {
                          setSavingFreeEvent(true);
                          try {
                            const res = await axios.put(`${API_URL}/api/admin/free-event`, {
                              ...freeEvent, is_active: true,
                            });
                            setFreeEvent(prev => ({ ...prev, is_active: !!res.data?.is_active }));
                            toast.success('Free event is LIVE — anyone can watch');
                            fetchSessionHistory();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Failed to start');
                          } finally {
                            setSavingFreeEvent(false);
                          }
                        }}
                        disabled={savingFreeEvent}
                        className="bg-green-600 hover:bg-green-700 text-white flex-1 h-14 text-lg font-bold"
                        data-testid="free-event-start"
                      >
                        {savingFreeEvent ? 'Starting…' : 'Start Free Event Now'}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm('End the free event and put the paywall back?')) return;
                          setSavingFreeEvent(true);
                          try {
                            const res = await axios.put(`${API_URL}/api/admin/free-event`, {
                              ...freeEvent, is_active: false,
                            });
                            setFreeEvent(prev => ({ ...prev, is_active: !!res.data?.is_active }));
                            toast.success('Free event ended — paywall is back');
                            fetchSessionHistory();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Failed to stop');
                          } finally {
                            setSavingFreeEvent(false);
                          }
                        }}
                        disabled={savingFreeEvent}
                        className="bg-red-600 hover:bg-red-700 text-white flex-1 h-14 text-lg font-bold"
                        data-testid="free-event-stop"
                      >
                        {savingFreeEvent ? 'Ending…' : 'End Free Event'}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        setSavingFreeEvent(true);
                        try {
                          await axios.put(`${API_URL}/api/admin/free-event`, freeEvent);
                          toast.success('Details saved');
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Failed to save');
                        } finally {
                          setSavingFreeEvent(false);
                        }
                      }}
                      disabled={savingFreeEvent}
                      className="sm:w-40 h-14"
                    >
                      Save Details
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Live Stream Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display text-xl uppercase">Live Stream Settings</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Configure the live stream embed. Live streams are exclusive to active subscribers — no pay-per-view.
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center gap-4">
                    <Label htmlFor="is-live" className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        id="is-live"
                        checked={liveStreamSettings.is_live}
                        onChange={(e) => setLiveStreamSettings(prev => ({ ...prev, is_live: e.target.checked }))}
                        className="w-5 h-5 rounded border-gray-300"
                      />
                      <span className="font-medium">Stream is Live</span>
                    </Label>
                    {liveStreamSettings.is_live && (
                      <Badge className="bg-red-600 animate-pulse">LIVE NOW</Badge>
                    )}
                  </div>

                  {/* Paid live-stream viewer stats (only while live) */}
                  {liveStreamSettings.is_live && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="rounded-lg border-2 border-red-600/40 bg-red-50 dark:bg-red-950/20 p-4 text-center">
                        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Watching Now</div>
                        <div className="text-3xl font-display font-bold text-red-700 dark:text-red-400" data-testid="paid-live-count">
                          {liveStreamStats.watching_now || 0}
                        </div>
                      </div>
                      <div className="rounded-lg border p-4 text-center">
                        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total Unique</div>
                        <div className="text-3xl font-display font-bold" data-testid="paid-total-count">
                          {liveStreamStats.total_unique_viewers || 0}
                        </div>
                      </div>
                      <div className="rounded-lg border p-4 text-center">
                        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Peak Concurrent</div>
                        <div className="text-3xl font-display font-bold" data-testid="paid-peak-count">
                          {liveStreamStats.peak_concurrent || 0}
                        </div>
                      </div>
                      <div className="rounded-lg border p-4 text-center">
                        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">TV Screens</div>
                        <div className="text-3xl font-display font-bold" data-testid="paid-tv-count">
                          {liveStreamStats.tv_screens || 0}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="stream-title">Event Title</Label>
                    <Input
                      id="stream-title"
                      value={liveStreamSettings.title}
                      onChange={(e) => setLiveStreamSettings(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., March Roping Clinic"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="embed-code">Vimeo / YouTube URL or Embed Code</Label>
                    <textarea
                      id="embed-code"
                      value={liveStreamSettings.embed_code}
                      onChange={(e) => setLiveStreamSettings(prev => ({ ...prev, embed_code: e.target.value }))}
                      placeholder='Paste a URL (https://vimeo.com/event/...) or full embed code...'
                      className="w-full h-32 p-3 border rounded-md font-mono text-sm resize-none"
                    />
                  </div>

                  {liveStreamSettings.embed_code && (
                    <div className="space-y-2">
                      <Label>Preview</Label>
                      <div className="border rounded-lg overflow-hidden bg-black aspect-video">
                        {(() => {
                          const val = liveStreamSettings.embed_code.trim();
                          const embedUrl = getEmbedUrlFromInput(val);
                          if (embedUrl) {
                            return <iframe src={embedUrl} className="w-full h-full" allow="autoplay; fullscreen; picture-in-picture; encrypted-media; web-share; clipboard-write" allowFullScreen />;
                          }
                          return <div dangerouslySetInnerHTML={{ __html: val }} />;
                        })()}
                      </div>
                    </div>
                  )}

                  <Button 
                    onClick={async () => {
                      setSavingLiveStream(true);
                      try {
                        await axios.put(
                          `${API_URL}/api/admin/live-stream`,
                          {
                            embed_code: liveStreamSettings.embed_code || '',
                            is_live: !!liveStreamSettings.is_live,
                            title: liveStreamSettings.title || 'Live Event',
                            subscriber_price: 0,
                            non_subscriber_price: 0,
                          },
                          {
                            headers: {
                              Authorization: `Bearer ${localStorage.getItem('token')}`,
                              'Content-Type': 'application/json',
                            },
                          }
                        );
                        toast.success('Live stream settings saved!');
                        // Re-fetch to confirm persistence
                        try {
                          const fresh = await axios.get(`${API_URL}/api/live-stream`);
                          setLiveStreamSettings(prev => ({ ...prev, ...fresh.data }));
                        } catch { /* ignore */ }
                        // Refresh session history in case we just ended a session
                        fetchSessionHistory();
                      } catch (err) {
                        toast.error('Failed to save: ' + (err.response?.data?.detail || err.message));
                      } finally {
                        setSavingLiveStream(false);
                      }
                    }}
                    disabled={savingLiveStream}
                    className="w-full"
                  >
                    {savingLiveStream ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Live Stream Settings
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Past Live Sessions — snapshots taken every time a stream ends */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display text-xl uppercase">Past Live Sessions</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Snapshot of viewer counts every time a live or free event ended. Newest first.
                  </p>
                </CardHeader>
                <CardContent>
                  {sessionHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">
                      No completed sessions yet. Data appears here after the first time you end a live stream or free event.
                    </p>
                  ) : (
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                      <table className="w-full text-sm min-w-[640px]" data-testid="past-sessions-table">
                        <thead>
                          <tr className="border-b text-left text-xs uppercase tracking-widest text-muted-foreground">
                            <th className="py-2 pr-4">Title</th>
                            <th className="py-2 pr-4">Type</th>
                            <th className="py-2 pr-4">Ended</th>
                            <th className="py-2 pr-4 text-right">Unique</th>
                            <th className="py-2 pr-4 text-right">Peak</th>
                            <th className="py-2 pr-4 text-right">TVs</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sessionHistory.map((s) => {
                            const ended = s.ended_at ? new Date(s.ended_at).toLocaleString() : '—';
                            return (
                              <tr key={s.id} className="border-b last:border-0">
                                <td className="py-3 pr-4 font-medium">{s.title || '(untitled)'}</td>
                                <td className="py-3 pr-4">
                                  <Badge className={s.kind === 'free' ? 'bg-green-600' : 'bg-red-600'}>
                                    {s.kind === 'free' ? 'Free' : 'Paid'}
                                  </Badge>
                                </td>
                                <td className="py-3 pr-4 text-muted-foreground whitespace-nowrap">{ended}</td>
                                <td className="py-3 pr-4 text-right font-bold">{s.total_unique_viewers || 0}</td>
                                <td className="py-3 pr-4 text-right">{s.peak_concurrent || 0}</td>
                                <td className="py-3 pr-4 text-right">{s.tv_screens || 0}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Past Events */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-display text-xl uppercase">Past Event Recordings</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Add recordings of past events. Recordings are exclusive to active subscribers — no pay-per-view.
                    </p>
                  </div>
                  <Button onClick={() => {
                    setPastEventForm({ title: '', description: '', embed_code: '', thumbnail_url: '', event_date: '' });
                    setEditingPastEvent(null);
                    setShowAddPastEvent(true);
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Past Event
                  </Button>
                </CardHeader>
                <CardContent>
                  {pastEvents.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No past events added yet</p>
                  ) : (
                    <div className="space-y-4">
                      {pastEvents.map(event => (
                        <div key={event.id} className="border rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold">{event.title}</h4>
                            <p className="text-sm text-muted-foreground">{event.event_date}</p>
                            <div className="flex gap-2 mt-1 text-xs">
                              <span className="inline-block bg-primary/10 text-primary px-2 py-0.5 rounded">Subscribers Only</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => {
                              setPastEventForm(event);
                              setEditingPastEvent(event);
                              setShowAddPastEvent(true);
                            }}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" title="Migrate to multi-video Event" data-testid={`migrate-past-${event.id}`} onClick={async () => {
                              if (!confirm(`Move "${event.title}" into the regular Events system so it can carry a playlist of multiple videos? The archive row will be removed and any pinned rails will be rewritten automatically.`)) return;
                              try {
                                const res = await axios.post(
                                  `${API_URL}/api/admin/past-events/${event.id}/migrate-to-event`,
                                  {},
                                  { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
                                );
                                setPastEvents(prev => prev.filter(e => e.id !== event.id));
                                toast.success('Migrated. Open Events tab to add more videos.');
                                // Refresh events so the new one shows up
                                try {
                                  const evRes = await axios.get(`${API_URL}/api/admin/events`, {
                                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                                  });
                                  setEvents(evRes.data?.events || []);
                                } catch { /* non-fatal */ }
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Migration failed');
                              }
                            }}>
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button variant="destructive" size="sm" onClick={async () => {
                              if (confirm('Delete this event?')) {
                                try {
                                  await axios.delete(`${API_URL}/api/admin/past-events/${event.id}`, {
                                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                  });
                                  setPastEvents(prev => prev.filter(e => e.id !== event.id));
                                  toast.success('Event deleted');
                                } catch (err) {
                                  toast.error('Failed to delete');
                                }
                              }
                            }}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Add/Edit Past Event Dialog */}
              <Dialog open={showAddPastEvent} onOpenChange={setShowAddPastEvent}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingPastEvent ? 'Edit' : 'Add'} Past Event</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Event Title</Label>
                      <Input
                        value={pastEventForm.title}
                        onChange={(e) => setPastEventForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="e.g., February Roping Clinic"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Event Date</Label>
                      <Input
                        type="date"
                        value={pastEventForm.event_date}
                        onChange={(e) => setPastEventForm(prev => ({ ...prev, event_date: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={pastEventForm.description}
                        onChange={(e) => setPastEventForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Brief description of the event"
                      />
                    </div>
                    <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-primary">
                      <strong>Access:</strong> Past event recordings are exclusive to active subscribers. No pay-per-view option.
                    </div>
                    <div className="space-y-2">
                      <Label>Vimeo / YouTube URL or Embed Code</Label>
                      <textarea
                        value={pastEventForm.embed_code}
                        onChange={(e) => setPastEventForm(prev => ({ ...prev, embed_code: e.target.value }))}
                        placeholder='Paste a URL (https://vimeo.com/...) or full embed code...'
                        className="w-full h-32 p-3 border rounded-md font-mono text-sm resize-none"
                      />
                    </div>
                    {pastEventForm.embed_code && (
                      <div className="space-y-2">
                        <Label>Preview</Label>
                        <div className="border rounded-lg overflow-hidden bg-black aspect-video">
                          {(() => {
                            const val = pastEventForm.embed_code.trim();
                            const embedUrl = getEmbedUrlFromInput(val);
                            if (embedUrl) {
                              return <iframe src={embedUrl} className="w-full h-full" allow="autoplay; fullscreen; picture-in-picture; encrypted-media; web-share; clipboard-write" allowFullScreen />;
                            }
                            return <div dangerouslySetInnerHTML={{ __html: val }} />;
                          })()}
                        </div>
                      </div>
                    )}
                    <Button 
                      onClick={async () => {
                        setSavingPastEvent(true);
                        try {
                          if (editingPastEvent) {
                            await axios.put(`${API_URL}/api/admin/past-events/${editingPastEvent.id}`, pastEventForm, {
                              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                            });
                            setPastEvents(prev => prev.map(e => e.id === editingPastEvent.id ? { ...e, ...pastEventForm } : e));
                          } else {
                            const res = await axios.post(`${API_URL}/api/admin/past-events`, pastEventForm, {
                              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                            });
                            setPastEvents(prev => [res.data, ...prev]);
                          }
                          toast.success(editingPastEvent ? 'Event updated!' : 'Event added!');
                          setShowAddPastEvent(false);
                        } catch (err) {
                          toast.error('Failed to save: ' + (err.response?.data?.detail || err.message));
                        } finally {
                          setSavingPastEvent(false);
                        }
                      }}
                      disabled={savingPastEvent || !pastEventForm.title}
                      className="w-full"
                    >
                      {savingPastEvent ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      {editingPastEvent ? 'Update Event' : 'Add Event'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>

          {/* Social Media Dashboard Tab */}
          <TabsContent value="social-media">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="font-display text-xl uppercase">Social Media Dashboard</CardTitle>
                    <CardDescription>
                      Track your social media growth and engagement
                      {nextScheduledRefresh && (
                        <span className="block text-xs text-muted-foreground mt-1">
                          Next auto-refresh: {new Date(nextScheduledRefresh).toLocaleString('en-US', { 
                            timeZone: 'America/Denver',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })} MST
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setEditingSocialProfiles(!editingSocialProfiles)}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      {editingSocialProfiles ? 'Cancel' : 'Edit Profiles'}
                    </Button>
                    <Button 
                      onClick={async () => {
                        setRefreshingSocialMedia(true);
                        toast.info('Scraping social media sites... This will take 2-3 minutes. Please wait.', { duration: 10000 });
                        try {
                          const token = localStorage.getItem('token');
                          const res = await axios.post(`${API_URL}/api/admin/social-media/refresh`, {}, {
                            headers: { Authorization: `Bearer ${token}` },
                            timeout: 300000 // 5 minute timeout
                          });
                          setSocialMediaStats(res.data);
                          toast.success('Social media stats refreshed!');
                          fetchData();
                        } catch (err) {
                          if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
                            toast.error('Request timed out. Stats may still be updating - try refreshing the page in a minute.');
                          } else {
                            toast.error('Failed to refresh: ' + (err.response?.data?.detail || err.message));
                          }
                        } finally {
                          setRefreshingSocialMedia(false);
                        }
                      }}
                      disabled={refreshingSocialMedia}
                    >
                      {refreshingSocialMedia ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      {refreshingSocialMedia ? 'Scraping sites (2-3 min)...' : 'Refresh Stats'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Edit Profiles Form */}
                {editingSocialProfiles && (
                  <Card className="mb-6 border-primary">
                    <CardContent className="pt-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="flex items-center gap-2">
                            <Instagram className="w-4 h-4" /> Instagram URL
                          </Label>
                          <Input 
                            value={socialProfilesForm.instagram || ''} 
                            onChange={(e) => setSocialProfilesForm({...socialProfilesForm, instagram: e.target.value})}
                            placeholder="https://instagram.com/username"
                          />
                        </div>
                        <div>
                          <Label className="flex items-center gap-2">
                            <Facebook className="w-4 h-4" /> Facebook URL
                          </Label>
                          <Input 
                            value={socialProfilesForm.facebook || ''} 
                            onChange={(e) => setSocialProfilesForm({...socialProfilesForm, facebook: e.target.value})}
                            placeholder="https://facebook.com/page"
                          />
                        </div>
                        <div>
                          <Label className="flex items-center gap-2">
                            <Youtube className="w-4 h-4" /> YouTube URL
                          </Label>
                          <Input 
                            value={socialProfilesForm.youtube || ''} 
                            onChange={(e) => setSocialProfilesForm({...socialProfilesForm, youtube: e.target.value})}
                            placeholder="https://youtube.com/@channel"
                          />
                        </div>
                        <div>
                          <Label>TikTok URL</Label>
                          <Input 
                            value={socialProfilesForm.tiktok || ''} 
                            onChange={(e) => setSocialProfilesForm({...socialProfilesForm, tiktok: e.target.value})}
                            placeholder="https://tiktok.com/@username"
                          />
                        </div>
                      </div>
                      <Button 
                        className="mt-4"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            await axios.post(`${API_URL}/api/admin/social-media/profiles`, socialProfilesForm, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success('Profiles updated!');
                            setEditingSocialProfiles(false);
                            fetchData();
                          } catch (err) {
                            toast.error('Failed to save profiles');
                          }
                        }}
                      >
                        <Save className="w-4 h-4 mr-2" /> Save Profiles
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Stats Cards */}
                {socialMediaStats ? (
                  <div className="space-y-6">
                    {/* Platform Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Instagram */}
                      {socialMediaStats.instagram && !socialMediaStats.instagram.error ? (
                        <Card className="border-pink-500/30 bg-gradient-to-br from-pink-500/5 to-purple-500/5">
                          <CardContent className="pt-6">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                                <Instagram className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="font-bold">Instagram</p>
                                <p className="text-xs text-muted-foreground">@{socialMediaStats.instagram.username}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center">
                              <div>
                                <p className="text-2xl font-bold">{socialMediaStats.instagram.followers?.toLocaleString() || 0}</p>
                                <p className="text-xs text-muted-foreground">Followers</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold">{socialMediaStats.instagram.posts?.toLocaleString() || 0}</p>
                                <p className="text-xs text-muted-foreground">Posts</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold">{socialMediaStats.instagram.following?.toLocaleString() || 0}</p>
                                <p className="text-xs text-muted-foreground">Following</p>
                              </div>
                            </div>
                            {socialMediaStats.instagram.recent_posts?.length > 0 && (
                              <div className="mt-4 pt-4 border-t">
                                <p className="text-xs font-medium mb-2">Recent Posts Engagement</p>
                                <div className="space-y-1">
                                  {socialMediaStats.instagram.recent_posts.slice(0, 3).map((post, i) => (
                                    <div key={i} className="flex justify-between text-xs">
                                      <span className="truncate flex-1">{post.caption || 'Post'}</span>
                                      <span className="text-muted-foreground ml-2">{post.likes} likes</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ) : socialMediaStats.instagram?.error ? (
                        <Card className="border-pink-500/30">
                          <CardContent className="pt-6 text-center">
                            <Instagram className="w-8 h-8 mx-auto mb-2 text-pink-500 opacity-50" />
                            <p className="text-sm text-muted-foreground">Instagram data unavailable</p>
                            <p className="text-xs text-red-500 mt-1">{socialMediaStats.instagram.error}</p>
                          </CardContent>
                        </Card>
                      ) : null}

                      {/* YouTube */}
                      {socialMediaStats.youtube && !socialMediaStats.youtube.error ? (
                        <Card className="border-red-500/30 bg-gradient-to-br from-red-500/5 to-orange-500/5">
                          <CardContent className="pt-6">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center">
                                <Youtube className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="font-bold">YouTube</p>
                                <p className="text-xs text-muted-foreground">{socialMediaStats.youtube.name}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center">
                              <div>
                                <p className="text-2xl font-bold">{socialMediaStats.youtube.subscribers?.toLocaleString() || 0}</p>
                                <p className="text-xs text-muted-foreground">Subscribers</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold">{socialMediaStats.youtube.videos?.toLocaleString() || 0}</p>
                                <p className="text-xs text-muted-foreground">Videos</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold">{socialMediaStats.youtube.views?.toLocaleString() || 0}</p>
                                <p className="text-xs text-muted-foreground">Views</p>
                              </div>
                            </div>
                            {socialMediaStats.youtube.recent_videos?.length > 0 && (
                              <div className="mt-4 pt-4 border-t">
                                <p className="text-xs font-medium mb-2">Recent Videos</p>
                                <div className="space-y-1">
                                  {socialMediaStats.youtube.recent_videos.slice(0, 3).map((video, i) => (
                                    <div key={i} className="flex justify-between text-xs">
                                      <span className="truncate flex-1">{video.title}</span>
                                      <span className="text-muted-foreground ml-2">{video.views?.toLocaleString()} views</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ) : socialMediaStats.youtube?.error ? (
                        <Card className="border-red-500/30">
                          <CardContent className="pt-6 text-center">
                            <Youtube className="w-8 h-8 mx-auto mb-2 text-red-500 opacity-50" />
                            <p className="text-sm text-muted-foreground">YouTube data unavailable</p>
                            <p className="text-xs text-red-500 mt-1">{socialMediaStats.youtube.error}</p>
                          </CardContent>
                        </Card>
                      ) : null}

                      {/* Facebook */}
                      {socialMediaStats.facebook && !socialMediaStats.facebook.error ? (
                        <Card className="border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-indigo-500/5">
                          <CardContent className="pt-6">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                                <Facebook className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="font-bold">Facebook</p>
                                <p className="text-xs text-muted-foreground">{socialMediaStats.facebook.name}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-center">
                              <div>
                                <p className="text-2xl font-bold">{socialMediaStats.facebook.followers?.toLocaleString() || 0}</p>
                                <p className="text-xs text-muted-foreground">Followers</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold">{socialMediaStats.facebook.likes?.toLocaleString() || 0}</p>
                                <p className="text-xs text-muted-foreground">Page Likes</p>
                              </div>
                            </div>
                            {socialMediaStats.facebook.recent_posts?.length > 0 && (
                              <div className="mt-4 pt-4 border-t">
                                <p className="text-xs font-medium mb-2">Recent Posts</p>
                                <div className="space-y-1">
                                  {socialMediaStats.facebook.recent_posts.slice(0, 3).map((post, i) => (
                                    <div key={i} className="flex justify-between text-xs">
                                      <span className="truncate flex-1">{post.text || 'Post'}</span>
                                      <span className="text-muted-foreground ml-2">{post.likes} likes</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ) : socialMediaStats.facebook?.error ? (
                        <Card className="border-blue-500/30">
                          <CardContent className="pt-6 text-center">
                            <Facebook className="w-8 h-8 mx-auto mb-2 text-blue-500 opacity-50" />
                            <p className="text-sm text-muted-foreground">Facebook data unavailable</p>
                            <p className="text-xs text-red-500 mt-1">{socialMediaStats.facebook.error}</p>
                          </CardContent>
                        </Card>
                      ) : null}

                      {/* TikTok */}
                      {socialMediaStats.tiktok && !socialMediaStats.tiktok.error ? (
                        <Card className="border-black/30 bg-gradient-to-br from-black/5 to-pink-500/5">
                          <CardContent className="pt-6">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                                </svg>
                              </div>
                              <div>
                                <p className="font-bold">TikTok</p>
                                <p className="text-xs text-muted-foreground">@{socialMediaStats.tiktok.username}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center">
                              <div>
                                <p className="text-2xl font-bold">{socialMediaStats.tiktok.followers?.toLocaleString() || 0}</p>
                                <p className="text-xs text-muted-foreground">Followers</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold">{socialMediaStats.tiktok.likes?.toLocaleString() || 0}</p>
                                <p className="text-xs text-muted-foreground">Likes</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold">{socialMediaStats.tiktok.videos?.toLocaleString() || 0}</p>
                                <p className="text-xs text-muted-foreground">Videos</p>
                              </div>
                            </div>
                            {socialMediaStats.tiktok.recent_videos?.length > 0 && (
                              <div className="mt-4 pt-4 border-t">
                                <p className="text-xs font-medium mb-2">Recent Videos</p>
                                <div className="space-y-1">
                                  {socialMediaStats.tiktok.recent_videos.slice(0, 3).map((video, i) => (
                                    <div key={i} className="flex justify-between text-xs">
                                      <span className="truncate flex-1">{video.description || 'Video'}</span>
                                      <span className="text-muted-foreground ml-2">{video.plays?.toLocaleString()} plays</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ) : socialMediaStats.tiktok?.error ? (
                        <Card className="border-black/30">
                          <CardContent className="pt-6 text-center">
                            <svg className="w-8 h-8 mx-auto mb-2 opacity-50" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                            </svg>
                            <p className="text-sm text-muted-foreground">TikTok data unavailable</p>
                            <p className="text-xs text-red-500 mt-1">{socialMediaStats.tiktok.error}</p>
                          </CardContent>
                        </Card>
                      ) : null}
                    </div>

                    {/* TikTok Placeholder - only show if no tiktok profile AND no tiktok stats */}
                    {!socialMediaProfiles.tiktok && !socialMediaStats.tiktok && (
                      <Card className="border-dashed">
                        <CardContent className="py-8 text-center">
                          <p className="text-muted-foreground">TikTok tracking not configured</p>
                          <Button variant="outline" size="sm" className="mt-2" onClick={() => setEditingSocialProfiles(true)}>
                            Add TikTok Profile
                          </Button>
                        </CardContent>
                      </Card>
                    )}

                    {/* Growth Chart */}
                    {socialMediaHistory.length > 0 && (
                      <Card className="mt-6">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <CardTitle className="text-base sm:text-lg">Follower Growth</CardTitle>
                            <Select value={chartTimePeriod} onValueChange={setChartTimePeriod}>
                              <SelectTrigger className="w-28 sm:w-32 text-xs sm:text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="weekly">7 Days</SelectItem>
                                <SelectItem value="monthly">30 Days</SelectItem>
                                <SelectItem value="all">All Time</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </CardHeader>
                        <CardContent className="px-2 sm:px-6">
                          <div className="h-48 sm:h-64 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                data={(() => {
                                  // Aggregate by day - keep only the LATEST record per day
                                  const byDay = {};
                                  socialMediaHistory
                                    .filter(h => h.instagram_followers || h.facebook_followers || h.youtube_subscribers || h.tiktok_followers)
                                    .forEach(h => {
                                      const date = new Date(h.timestamp);
                                      const dayKey = date.toLocaleDateString('en-US', { timeZone: 'America/Denver' });
                                      // Keep the most recent record for each day
                                      if (!byDay[dayKey] || new Date(h.timestamp) > new Date(byDay[dayKey].timestamp)) {
                                        byDay[dayKey] = h;
                                      }
                                    });
                                  
                                  // Convert to array and sort chronologically
                                  let chartData = Object.entries(byDay)
                                    .map(([dayKey, h]) => ({
                                      timestamp: h.timestamp,
                                      dayKey,
                                      Instagram: h.instagram_followers || 0,
                                      YouTube: h.youtube_subscribers || 0,
                                      Facebook: h.facebook_followers || 0,
                                      TikTok: h.tiktok_followers || 0
                                    }))
                                    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                                  
                                  // Filter by time period
                                  const now = new Date();
                                  chartData = chartData.filter(d => {
                                    const date = new Date(d.timestamp);
                                    const diffDays = (now - date) / (24 * 60 * 60 * 1000);
                                    switch(chartTimePeriod) {
                                      case 'weekly': return diffDays <= 7;
                                      case 'monthly': return diffDays <= 30;
                                      case 'all': return true;
                                      default: return true;
                                    }
                                  });
                                  
                                  // Format date labels
                                  return chartData.map(d => ({
                                    ...d,
                                    date: new Date(d.timestamp).toLocaleDateString('en-US', { 
                                      timeZone: 'America/Denver',
                                      month: 'short', 
                                      day: 'numeric'
                                    })
                                  }));
                                })()}
                                margin={{ top: 5, right: 5, left: -10, bottom: 5 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} width={35} />
                                <Tooltip 
                                  contentStyle={{ 
                                    backgroundColor: 'hsl(var(--background))', 
                                    border: '1px solid hsl(var(--border))',
                                    borderRadius: '8px',
                                    fontSize: '12px'
                                  }}
                                />
                                <Legend wrapperStyle={{ fontSize: '11px' }} />
                                <Line type="monotone" dataKey="Facebook" stroke="#1877F2" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                                <Line type="monotone" dataKey="Instagram" stroke="#E1306C" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                                <Line type="monotone" dataKey="TikTok" stroke="#000000" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                                <Line type="monotone" dataKey="YouTube" stroke="#FF0000" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                          <p className="text-xs text-muted-foreground text-center mt-2">
                            {(() => {
                              const validDays = Object.keys(
                                socialMediaHistory
                                  .filter(h => h.instagram_followers || h.facebook_followers || h.youtube_subscribers || h.tiktok_followers)
                                  .reduce((acc, h) => {
                                    const dayKey = new Date(h.timestamp).toLocaleDateString('en-US', { timeZone: 'America/Denver' });
                                    acc[dayKey] = true;
                                    return acc;
                                  }, {})
                              ).length;
                              return `${validDays} day${validDays !== 1 ? 's' : ''} of data`;
                            })()}
                            {' • Refresh daily to track growth'}
                          </p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Last Updated */}
                    {socialMediaStats.fetched_at && (
                      <p className="text-xs text-muted-foreground text-center">
                        Last updated: {new Date(socialMediaStats.fetched_at).toLocaleString('en-US', { timeZone: 'America/Denver', dateStyle: 'medium', timeStyle: 'short' })} MST
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <TrendingUp className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground mb-4">No social media data yet</p>
                    <Button onClick={async () => {
                      setRefreshingSocialMedia(true);
                      try {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${API_URL}/api/admin/social-media/refresh`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        setSocialMediaStats(res.data);
                        toast.success('Social media stats fetched!');
                      } catch (err) {
                        toast.error('Failed to fetch: ' + (err.response?.data?.detail || err.message));
                      } finally {
                        setRefreshingSocialMedia(false);
                      }
                    }} disabled={refreshingSocialMedia}>
                      {refreshingSocialMedia ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Fetch Social Media Stats
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="dartfish">
            <Card>
              <CardContent className="p-4 sm:p-6">
                <DartfishTab />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="store">
            <Card>
              <CardContent className="p-4 sm:p-6">
                <StoreManagementTab />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="agreements">
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase">Agreements & Waivers</CardTitle>
                <p className="text-sm text-muted-foreground">Manage agreements, waivers, and view signatures.</p>
              </CardHeader>
              <CardContent>
                <AdminAgreementsTab />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="credit-settings">
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase">Download Credit Costs</CardTitle>
                <p className="text-sm text-muted-foreground">Set how many credits photos and videos cost to download. Coaches and admins are always free.</p>
              </CardHeader>
              <CardContent>
                <CreditSettingsTab />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="media-pricing">
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase">Media Purchase Pricing</CardTitle>
                <p className="text-sm text-muted-foreground">Per-photo prices and custom video order pricing (subscriber vs non-subscriber).</p>
              </CardHeader>
              <CardContent>
                <MediaPricingTab />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="video-orders">
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase">Custom Video Orders</CardTitle>
                <p className="text-sm text-muted-foreground">Manage incoming video edit requests, deliver completed videos, and update status.</p>
              </CardHeader>
              <CardContent>
                <VideoOrdersTab />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="guest-tickets">
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase">Guest Ticket Buyers</CardTitle>
                <p className="text-sm text-muted-foreground">People who paid for event tickets without creating an account. Use this to recover access for customers who lost their link.</p>
              </CardHeader>
              <CardContent>
                <GuestTicketsTab />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="sponsors">
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase">Sponsors</CardTitle>
                <p className="text-sm text-muted-foreground">Manage sponsor logos grouped by tier. Showcased on the public Sponsors page.</p>
              </CardHeader>
              <CardContent>
                <SponsorsTab />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="teaser-videos">
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase">Landing Page Teaser Videos</CardTitle>
                <p className="text-sm text-muted-foreground">Add short teaser clips that display on the homepage to attract new subscribers.</p>
              </CardHeader>
              <CardContent>
                <TeaserVideosTab />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="home-rails">
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase">Home Page Rails</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Control which sections appear on the home page (<code>/</code>), their order, and which item is featured first.
                </p>
              </CardHeader>
              <CardContent>
                <HomePageRailsTab />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Build marker — compare between preview and production to confirm deploy */}
        <BuildMarker />
      </div>
    </div>
  );
};

export default AdminPage;
